import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
import { EditElementsPanel, EDIT_ELEMENTS_DEFAULT } from './EditElementsCards';
import {
  CAPTION_DEMO_CSS,
  CAPTION_STYLE_CARDS,
  CaptionStyleCards,
  HEADLINE_STYLE_CARDS,
  HeadlineStyleCards,
} from './CaptionStyleCards';
import type {
  VideoEditorAsset,
  VideoEditorCaptionStyle,
  VideoEditorClip,
  VideoEditorHeadlineStyle,
  VideoEditorImageOverlay,
  VideoEditorImageInsert,
  VideoEditorOverlay,
  VideoEditorOverlayAnimation,
  VideoEditorProject,
  VideoEditorRender,
  VideoEditorMusic,
  VideoEditorMusicTrack,
  VideoEditorSoundEffect,
  VideoEditorTextOverlay,
  VideoEditorTextRun,
} from '../../types/videoEditor';

interface Props {
  jobId: string;
  target: string;
  title?: string;
  onClose: () => void;
}

interface HistoryState {
  past: VideoEditorProject[];
  present: VideoEditorProject;
  future: VideoEditorProject[];
}

type SelectionItem = { kind: 'clip' | 'overlay'; id: string };
type Selection = SelectionItem | null;
// Retângulo de seleção (arrastar no vazio da faixa para pegar vários itens).
type Marquee = { track: 'clip' | 'overlay'; fromSeconds: number; toSeconds: number };
type SaveState = 'saved' | 'dirty' | 'saving' | 'conflict' | 'error';
type DropPosition = { x: number; y: number };
type ResizeHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

// Famílias que o index.html já carrega do Google Fonts para todo o app.
const BUNDLED_FONT_OPTIONS = [
  'Montserrat',
  'Poppins',
  'Inter',
  'Anton',
  'Bebas Neue',
  'Oswald',
  'Roboto Condensed',
  'Archivo Black',
  'League Spartan',
  'Pacifico',
];
// Famílias dos combos tipográficos do render automático — são as fontes que o
// vídeo realmente usa. Sem elas na lista, a pessoa não tinha como voltar à
// tipografia do próprio vídeo depois de mexer numa palavra. Elas não vêm no
// index.html: os arquivos reais (peso e itálico certos) chegam pelo endpoint de
// fonte do projeto, que serve o MESMO arquivo que o render usou.
const COMBO_FONT_OPTIONS = [
  'Archivo',
  'Raleway',
  'Nunito',
  'Playfair Display',
  'Libre Baskerville',
  'EB Garamond',
  'Bitter',
  'Zilla Slab',
];
const FONT_OPTIONS = [...BUNDLED_FONT_OPTIONS, ...COMBO_FONT_OPTIONS];
const BUNDLED_WEB_FONTS = new Set(BUNDLED_FONT_OPTIONS);
const RENDER_STALE_MS = 2 * 60 * 60 * 1000;
const EDITOR_MEDIA_FILE_PATTERN = /\.(mp4|mov|webm|avi|mkv|png|jpe?g|webp)$/i;
const ANIMATION_OPTIONS: Array<{ value: VideoEditorOverlayAnimation; label: string }> = [
  { value: 'none', label: 'Sem animação' },
  { value: 'fade', label: 'Fade' },
  { value: 'slide_up', label: 'Subir' },
  { value: 'slide_left', label: 'Entrar pela esquerda' },
  { value: 'zoom_in', label: 'Zoom' },
  { value: 'pop_bounce', label: 'Pop suave' },
  { value: 'pop_aggressive', label: 'Pop forte' },
  { value: 'snap', label: 'Snap' },
  { value: 'rise_pop', label: 'Subir + pop' },
];
const SOUND_EFFECT_LABELS: Record<string, string> = {
  oneclick: 'Clique limpo',
  simplewhosh: 'Whoosh simples',
  pop_soft_01: 'Pop suave',
  keyboard_tick_01: 'Toque de teclado',
  whoosh_short_01: 'Whoosh curto',
};
const IMAGE_RESIZE_HANDLE_CLASSES: Record<ResizeHandle, string> = {
  n: 'left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 cursor-ns-resize',
  s: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 cursor-ns-resize',
  e: 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2 cursor-ew-resize',
  w: 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize',
  ne: 'right-0 top-0 translate-x-1/2 -translate-y-1/2 cursor-nesw-resize',
  nw: 'left-0 top-0 -translate-x-1/2 -translate-y-1/2 cursor-nwse-resize',
  se: 'bottom-0 right-0 translate-x-1/2 translate-y-1/2 cursor-nwse-resize',
  sw: 'bottom-0 left-0 -translate-x-1/2 translate-y-1/2 cursor-nesw-resize',
};
const ASPECT_SIZES: Record<VideoEditorProject['aspectRatio'], { width: number; height: number }> = {
  '9:16': { width: 1080, height: 1920 },
  '16:9': { width: 1920, height: 1080 },
  '1:1': { width: 1080, height: 1080 },
  '4:5': { width: 1080, height: 1350 },
};

// Piso de duração para uma palavra acrescentada a uma legenda: abaixo disso ela
// nem pisca na tela, então vale mais apertar a run vizinha do que dar uma fatia
// invisível.
const MIN_RUN_SLICE = 0.08;

// Agrupa os trechos de texto por linha do bloco empilhado, preservando a ordem.
// Trechos sem `lineIndex` (projetos antigos) caem todos na linha 0, que é o
// comportamento anterior — frase corrida.
// Overlay que carrega estilo. So nele a previa dobra o contorno e aplica o
// achatamento: fazer isso em todo overlay mudaria a aparencia de todo projeto ja
// salvo, que ninguem pediu.
function estiloDoOverlay(overlay: VideoEditorTextOverlay): boolean {
  return Boolean(overlay.captionStyle || overlay.headlineStyle);
}

// Contorno dobrado na prévia: o traço do CSS é CENTRADO no glifo e o do vídeo
// (drawtext `borderw` e `\bord` do ASS) cresce só para fora, então o mesmo número
// desenha metade da espessura aqui. Vale para o overlay estilizado e para o bloco
// empilhado ajustado pelo layout do render (`stackLayout: 'fitted'`); o resto
// continua como sempre para não mudar projeto já salvo.
function contornoDobrado(overlay: VideoEditorTextOverlay): boolean {
  return estiloDoOverlay(overlay) || overlay.stackLayout === 'fitted';
}

// ── Zoom da exportação, reproduzido na prévia ────────────────────────────────
//
// A exportação (e o vídeo entregue pela IA) aplica `zoompan` em todo clipe de
// vídeo: uma base por clipe que alterna num ciclo de sete valores (o "salto de
// plano" da emenda) e um push-in de 4% ao longo do trecho, centrado no sujeito
// medido quando o tracking está ligado. A prévia mostrava o quadro CRU — o
// enquadramento no editor não batia com o do MP4 (rosto menor, mais espaço em
// volta), e todo texto parecia estar em outro lugar em relação à pessoa. Os
// números são os de `segmentZoomFilter` no backend, e têm de continuar iguais.
const ZOOM_CYCLE = [1.14, 1.20, 1.12, 1.22, 1.16, 1.10, 1.18];
const ZOOM_PUSH_IN = 0.04;
const ZOOM_MIN_DURATION = 0.9;

function zoomCycleBase(index: number): number {
  const n = ZOOM_CYCLE.length;
  return ZOOM_CYCLE[((index % n) + n) % n];
}

function previewZoomFor(
  project: VideoEditorProject,
  clip: VideoEditorClip | null,
  playhead: number,
): { zoom: number; centerX: number } | null {
  if (!clip) return null;
  // Projeto achatado já tem o zoom queimado no arquivo; imagem não recebe zoompan.
  if (project.sourceMode === 'flattened') return null;
  const asset = project.assets.find(item => item.id === clip.assetId);
  if (asset?.kind === 'image') return null;
  const elementos = { ...EDIT_ELEMENTS_DEFAULT, ...(project.editElements || {}) };
  if (!elementos.zoomCuts && !elementos.zoomAuto) return null;
  const index = project.clips.findIndex(item => item.id === clip.id);
  if (index < 0) return null;
  const rawDuration = Math.max(0, clip.sourceEnd - clip.sourceStart);
  if (rawDuration <= 0.02) return null;
  const base = elementos.zoomCuts ? zoomCycleBase(index) : 1;
  const comPush = elementos.zoomAuto && rawDuration >= ZOOM_MIN_DURATION;
  const nextIndex = index + 1 < project.clips.length ? index + 1 : null;
  const baseVizinha = elementos.zoomCuts && nextIndex != null ? zoomCycleBase(nextIndex) : null;
  const paraFora = baseVizinha != null && baseVizinha > base + 1e-9;
  const progresso = clamp((playhead - clip.timelineStart) / Math.max(0.001, clip.duration), 0, 1);
  const zoom = comPush
    ? (paraFora ? base + ZOOM_PUSH_IN * (1 - progresso) : base + ZOOM_PUSH_IN * progresso)
    : base;
  const tracking = (elementos.zoomCuts || elementos.zoomAuto) && elementos.tracking;
  const centro = tracking && clip.zoomCenter ? clip.zoomCenter : null;
  const limita = (valor: unknown) => {
    const numero = Number(valor);
    return Number.isFinite(numero) ? Math.min(0.85, Math.max(0.15, numero)) : 0.5;
  };
  const cx = centro ? limita(centro.centerX) : 0.5;
  const cxFim = centro && centro.centerXEnd != null ? limita(centro.centerXEnd) : cx;
  const centerX = (!comPush || Math.abs(cxFim - cx) < 0.005) ? cx : cx + (cxFim - cx) * progresso;
  return { zoom, centerX };
}

// ── Legenda: quem tem FANTASMA e quem não tem ───────────────────────────────
//
// CONTRATO com o backend (videoEditorService: EDITOR_CAPTION_STYLED_PRESET): os
// quatro estilos novos usam `stylePreset: 'caption_styled'` e o fantasma de 34%
// da palavra futura fica restrito a `'caption'`, o MODO ANTIGO. O nome é o mesmo
// nos dois lados de propósito — renomear de um lado só religa o defeito.
//
// O QUE ACONTECIA ANTES (medido): a prévia ligava o esmaecimento por
// `stylePreset === 'caption'`, e o desenho estilizado também vinha com 'caption'.
// Nos quatro estilos novos o motor NÃO desenha a palavra que ainda não foi falada
// — no karaokê o editor pintava 43.866 px de tinta onde o render pinta 10.422
// (4,2x). Aqui, na legenda `serifada` com 1 de 4 palavras faladas: 3.999 px de
// tinta com fantasma contra 2.928 px sem (o palco inteiro, medido no navegador).
//
// O `captionStyle` entra na conta porque projeto salvo ANTES da correção guarda
// 'caption' junto de um estilo carimbado. O saneamento do backend promove esse
// caso a 'caption_styled' na leitura, mas a prévia não pode depender de já ter
// passado por lá (rascunho em memória, undo de um projeto antigo).
function legendaComFantasma(overlay: VideoEditorTextOverlay): boolean {
  return overlay.stylePreset === 'caption' && !overlay.captionStyle;
}

// Legenda para o inspetor em LOTE: os dois presets contam. Só `'caption'` deixaria
// o botão "Selecionar todas as legendas" cego para os quatro estilos novos —
// exatamente o mesmo tipo de controle inerte que esta leva está consertando.
// Trecho com peso EXPLÍCITO (500/600/700/800 dos estilos). Onde ele existe, o
// booleano `bold` não decide mais nada: a prévia usa `run.fontWeight || (bold ? 900
// : 400)` e o export usa `editorAssFontName`, onde "weight explícito vence o
// booleano". MEDIDO na legenda `serifada`: clicar em B deixava o peso computado em
// 700 e a tinta do palco idêntica (4.818 px) — o botão acendia e não mudava nada.
//
// NÃO é para o B passar a escrever `fontWeight`: o peso de cada trecho sai de uma
// tabela MEDIDA, casada com o que o render desenha — é isso que faz o editor e o
// MP4 baterem. Sobrescrever em silêncio recria a classe de bug que esta leva
// consertou (editor desenha uma coisa, MP4 outra), e desligar o B num estilo de 600
// cairia em 400: o gesto de "tirar negrito" PERDERIA o peso da tabela, num estado
// que nenhum clique recupera. Peso por trecho, se um dia for pedido, é feature
// ("peso deste trecho"), redesenhada pelo `restyle` como os outros campos.
function pesoDoEstiloTravaNegrito(overlay: VideoEditorTextOverlay): boolean {
  return Boolean(overlay.fontWeight)
    || (overlay.textRuns || []).some(run => Boolean(run.fontWeight));
}

// Como a legenda ACENDE. `group` = o grupo inteiro de uma vez (os três estilos
// estáticos: `simples`, `serifada`, `classica`); `word` = palavra a palavra (só o
// `karaoke`). Quem manda é o CAMPO, nunca os `start` dos runs: projeto já salvo
// guarda o `start` por palavra de antes desta correção, e deduzir pelos tempos
// daria justamente nesses projetos o desenho que o MP4 não tem — 2/3 do grupo
// ausente onde o motor pinta a linha completa (medido no backend: 10.928 px em
// intensidade cheia no motor contra 7.967 no desenho antigo, −27%).
//
// Lido por asserção porque `VideoEditorTextOverlay` (src/types/videoEditor.ts) não
// é arquivo desta tarefa: FALTA declarar `captionReveal?: 'word' | 'group'` lá.
function revelacaoDaLegenda(overlay: VideoEditorTextOverlay): 'word' | 'group' | null {
  const valor = (overlay as { captionReveal?: unknown }).captionReveal;
  return valor === 'group' || valor === 'word' ? valor : null;
}

function ehLegenda(overlay: VideoEditorOverlay): boolean {
  return overlay.type === 'text'
    && (overlay.stylePreset === 'caption' || overlay.stylePreset === 'caption_styled');
}

function groupRunsByLine(runs: VideoEditorTextRun[]): VideoEditorTextRun[][] {
  const lines: VideoEditorTextRun[][] = [];
  for (const run of runs) {
    const index = Number.isFinite(run.lineIndex) ? Number(run.lineIndex) : 0;
    while (lines.length <= index) lines.push([]);
    lines[index].push(run);
  }
  return lines.filter(line => line.length);
}

// Reescreve o TEXTO do bloco preservando o desenho de cada trecho.
//
// Zerar os trechos ao editar o texto (o comportamento anterior) achatava o bloco
// inteiro na fonte e na cor do overlay: a pessoa clicava para trocar uma palavra
// e perdia o combo tipográfico, as cores por papel, os corpos por linha e o
// empilhamento — tudo de uma vez, sem como recuperar. Aqui cada palavra nova
// herda o estilo da palavra que estava na mesma posição; sobrando palavras, elas
// herdam o estilo da última. Uma quebra de linha digitada vira linha do bloco.
function remapRunsToText(
  runs: VideoEditorTextRun[],
  text: string,
  overlayEnd?: number,
): VideoEditorTextRun[] {
  if (!runs.length) return [];
  const lines = String(text ?? '').split(/\r?\n/);
  const hasTypedBreaks = lines.length > 1;
  const words = lines.flatMap((line, lineOrder) => line
    .split(/\s+/)
    .filter(Boolean)
    .map(word => ({ word, lineOrder })));
  if (!words.length) return [];

  // Tempo PRÓPRIO para as palavras acrescentadas além da contagem de runs.
  //
  // Herdar a última run inteira (o comportamento anterior) copiava o start/end
  // dela: duas palavras acendiam no MESMO instante e o karaokê parava de andar
  // naquele bloco. Aqui o que sobra até o fim da camada é fatiado em partes
  // iguais, na ordem em que as palavras foram digitadas. Se não sobra nada (a
  // última run já termina no fim da camada), as fatias saem do intervalo da
  // própria última run, que encurta para abrir espaço — apertar é melhor do que
  // empilhar duas palavras no mesmo quadro.
  const ultima = runs[runs.length - 1];
  const extras = words.length - runs.length;
  const limite = Math.max(
    Number.isFinite(overlayEnd as number) ? Number(overlayEnd) : ultima.end,
    ultima.end,
  );
  const folga = limite - ultima.end;
  // Sem folga suficiente para dar a cada palavra nova o mínimo legível, a última
  // run entra no rateio junto com elas.
  const cortaDaUltima = extras > 0 && folga < extras * MIN_RUN_SLICE;
  const inicioFatias = cortaDaUltima ? ultima.start : ultima.end;
  const fimFatias = Math.max(limite, inicioFatias);
  const totalFatias = extras + (cortaDaUltima ? 1 : 0);
  const passo = totalFatias > 0
    ? Math.max(0.01, (fimFatias - inicioFatias) / totalFatias)
    : 0;

  return words.map((entry, index) => {
    const base = runs[Math.min(index, runs.length - 1)];
    // O estilo (fonte, corpo, cor, contorno) e a linha do bloco continuam vindo
    // da run que estava na mesma posição — é o que impede a edição de texto de
    // achatar o bloco inteiro numa fonte só.
    const run: VideoEditorTextRun = {
      ...base,
      text: entry.word,
      lineIndex: hasTypedBreaks ? entry.lineOrder : base.lineIndex,
    };
    if (index < runs.length) {
      // Palavra que já tinha run: tempo preservado. A exceção é a última quando
      // ela precisa ceder espaço às novas.
      if (cortaDaUltima && index === runs.length - 1) {
        run.start = inicioFatias;
        run.end = inicioFatias + passo;
      }
      return run;
    }
    const ordem = index - runs.length + (cortaDaUltima ? 1 : 0);
    run.start = inicioFatias + ordem * passo;
    run.end = run.start + passo;
    return run;
  });
}

function uid(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// O desenho (onda ou miniaturas) cobre o asset INTEIRO; o bloco mostra só a
// janela [sourceStart, sourceEnd]. Em vez de pedir um desenho novo a cada ajuste
// de corte, a imagem é esticada para o comprimento total do asset e deslocada
// para a esquerda — o recorte visível cai sozinho no lugar certo, e continua
// certo enquanto o usuário arrasta a borda.
function mediaBackdropStyle(
  imageUrl: string,
  clip: { sourceStart: number; playbackRate: number },
  assetDuration: number,
  zoom: number,
): React.CSSProperties {
  const rate = clip.playbackRate || 1;
  const fullWidth = Math.max(1, (assetDuration / rate) * zoom);
  return {
    backgroundImage: `url(${imageUrl})`,
    backgroundSize: `${fullWidth}px 100%`,
    backgroundPosition: `${-(clip.sourceStart / rate) * zoom}px center`,
    backgroundRepeat: 'no-repeat',
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
}

function formatTime(value: number): string {
  const total = Math.max(0, value || 0);
  const minutes = Math.floor(total / 60);
  const seconds = Math.floor(total % 60);
  const frames = Math.floor((total % 1) * 30);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}:${String(frames).padStart(2, '0')}`;
}

function colorWithOpacity(color: string, opacity: number): string {
  const match = String(color || '').match(/^#([0-9a-f]{6})$/i);
  if (!match) return `rgba(0, 0, 0, ${clamp(opacity, 0, 1)})`;
  const value = Number.parseInt(match[1], 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${clamp(opacity, 0, 1)})`;
}

function entranceMotion(animation: VideoEditorOverlayAnimation | undefined, elapsed: number, duration = 0.48): {
  opacity: number;
  transform: string;
} {
  const progress = clamp(elapsed / Math.max(0.025, duration), 0, 1);
  const eased = 1 - Math.pow(1 - progress, 3);
  if (!animation || animation === 'none') return { opacity: 1, transform: '' };
  if (animation === 'fade') return { opacity: eased, transform: '' };
  if (animation === 'slide_left') return { opacity: eased, transform: `translateX(${(1 - eased) * -28}%)` };
  if (animation === 'slide_up') return { opacity: eased, transform: `translateY(${(1 - eased) * 28}%)` };
  if (animation === 'rise_pop') {
    const scale = progress < 0.75 ? 0.78 + (progress / 0.75) * 0.3 : 1.08 - ((progress - 0.75) / 0.25) * 0.08;
    return { opacity: eased, transform: `translateY(${(1 - eased) * 24}%) scale(${scale})` };
  }
  if (animation === 'snap') return { opacity: eased, transform: `scale(${1.18 - eased * 0.18})` };
  if (animation === 'pop_aggressive') {
    const scale = progress < 0.65 ? 0.45 + (progress / 0.65) * 0.72 : 1.17 - ((progress - 0.65) / 0.35) * 0.17;
    return { opacity: eased, transform: `scale(${scale})` };
  }
  const peak = animation === 'pop_bounce' ? 1.08 : 1;
  const scale = progress < 0.72 ? 0.7 + (progress / 0.72) * (peak - 0.7) : peak - ((progress - 0.72) / 0.28) * (peak - 1);
  return { opacity: eased, transform: `scale(${scale})` };
}

function exitMotion(animation: VideoEditorOverlayAnimation | undefined, remaining: number, duration = 0.48): {
  opacity: number;
  transform: string;
} {
  const base = entranceMotion(animation, remaining, duration);
  const progress = clamp(remaining / Math.max(0.025, duration), 0, 1);
  const eased = 1 - Math.pow(1 - progress, 3);
  if (animation === 'slide_up') return { ...base, transform: `translateY(${(1 - eased) * -28}%)` };
  if (animation === 'rise_pop') {
    const scale = progress < 0.75 ? 0.78 + (progress / 0.75) * 0.3 : 1.08 - ((progress - 0.75) / 0.25) * 0.08;
    return { ...base, transform: `translateY(${(1 - eased) * -24}%) scale(${scale})` };
  }
  return base;
}

function overlayMotion(overlay: VideoEditorOverlay, playhead: number): {
  opacity: number;
  transform: string;
} {
  const motionDuration = Math.min(0.48, Math.max(0.025, (overlay.end - overlay.start) / 2));
  const entry = entranceMotion(overlay.animation, playhead - overlay.start, motionDuration);
  const exit = exitMotion(overlay.exitAnimation, overlay.end - playhead, motionDuration);
  return {
    opacity: entry.opacity * exit.opacity,
    // A ordem é estável e espelha a composição de exportação: entrada, depois saída.
    transform: `${entry.transform} ${exit.transform}`.trim(),
  };
}

function applyTextCase(value: string, mode: VideoEditorTextOverlay['textCase']): string {
  if (mode === 'upper') return value.toLocaleUpperCase('pt-BR');
  if (mode === 'lower') return value.toLocaleLowerCase('pt-BR');
  if (mode === 'title') {
    return value.toLocaleLowerCase('pt-BR').replace(/(^|\s)(\p{L})/gu, match => match.toLocaleUpperCase('pt-BR'));
  }
  return value;
}

// Palavra de texto (textRun) vive em tempo ABSOLUTO da timeline e tem que estar
// dentro da janela da própria camada — se ficar fora, ela nunca acende no palco
// nem no MP4 exportado, e a camada parece ter sumido.
//
// Existe para curar projetos salvos com runs fora da janela (bug do arraste de
// camadas, que somava o deslocamento a cada evento do mouse). Quando TODAS as
// palavras estão fora, devolvemos o bloco inteiro para o começo da camada
// preservando o espaçamento entre elas; nos demais casos só apara nas bordas.
function repairTextRuns(runs: VideoEditorTextRun[], start: number, end: number): VideoEditorTextRun[] {
  if (!runs.length) return runs;
  const window = Math.max(0.05, end - start);
  // Último recurso: os tempos por palavra se perderam. Distribui em fatias iguais
  // dentro da camada — a legenda volta a acender palavra por palavra, só que com
  // ritmo uniforme (é o melhor que dá para reconstruir sem os tempos originais).
  const spreadEvenly = () => runs.map((run, index) => ({
    ...run,
    start: +(start + (window / runs.length) * index).toFixed(3),
    end: +(start + (window / runs.length) * (index + 1)).toFixed(3),
  }));

  const allOutside = runs.every(run => run.start >= end || run.end <= start);
  if (allOutside) {
    const firstStart = Math.min(...runs.map(run => run.start));
    const blockSpan = Math.max(...runs.map(run => run.end)) - firstStart;
    // Ao SALVAR, o backend já tinha grampeado todas as palavras para a borda da
    // camada (sanitizeEditorProject), então elas chegam aqui empilhadas no mesmo
    // ponto: não há espaçamento para preservar.
    if (blockSpan < 0.02) return spreadEvenly();
    const offset = start - firstStart;
    runs = runs.map(run => ({ ...run, start: run.start + offset, end: run.end + offset }));
  }

  const repaired = runs
    .map(run => ({
      ...run,
      start: clamp(run.start, start, Math.max(start, end - 0.01)),
      end: clamp(run.end, Math.min(end, start + 0.01), end),
    }))
    .filter(run => run.end > run.start);
  // Nunca devolver vazio quando havia texto: sem runs a camada perde a animação
  // palavra a palavra.
  return repaired.length ? repaired : spreadEvenly();
}

// ── Legenda acompanha o clipe ────────────────────────────────────────────────
//
// Os overlays vivem em tempo ABSOLUTO da timeline, mas o que eles marcam é a
// fala de um clipe. Esticar o início de um clipe, encurtar o fim, mudar a
// velocidade ou reordenar empurra o conteúdo dos clipes seguintes na régua — e
// os overlays ficavam parados: "aumentei um vídeo e todas as legendas ficaram
// fora do lugar". A conversão passa pelo TEMPO DE FONTE (segundo do arquivo
// original): o instante antigo vira segundo da mídia no clipe antigo, e esse
// segundo é procurado nos clipes novos (o mesmo clipe primeiro; senão outro
// clipe da mesma mídia que contenha o segundo, que é o caso do clipe dividido).
// Sem destino — clipe apagado — o overlay é estacionado além do fim da timeline
// (esmaecido na régua, fora da exportação, recuperável com Ctrl+Z ou arrastando).
function clipsIguais(a: VideoEditorClip[], b: VideoEditorClip[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((clip, index) => {
    const outro = b[index];
    return clip.id === outro.id
      && clip.assetId === outro.assetId
      && Math.abs(clip.sourceStart - outro.sourceStart) < 1e-6
      && Math.abs(clip.sourceEnd - outro.sourceEnd) < 1e-6
      && Math.abs(clip.playbackRate - outro.playbackRate) < 1e-6;
  });
}

function mapeadorDeTempo(antes: VideoEditorClip[], depois: VideoEditorClip[]): (t: number) => number {
  const totalAntes = antes.reduce((soma, clip) => soma + clip.duration, 0);
  const totalDepois = depois.reduce((soma, clip) => soma + clip.duration, 0);
  return (t: number) => {
    if (!antes.length || !depois.length) return t;
    // Além do fim antigo: mantém a distância até o fim.
    if (t >= totalAntes - 1e-6) return Math.max(0, t + (totalDepois - totalAntes));
    const origem = antes.find(clip => t >= clip.timelineStart && t < clip.timelineStart + clip.duration)
      || antes[antes.length - 1];
    const segundoDaFonte = origem.sourceStart + Math.max(0, t - origem.timelineStart) * origem.playbackRate;
    const contem = (clip: VideoEditorClip) => segundoDaFonte >= clip.sourceStart - 1e-3
      && segundoDaFonte <= clip.sourceEnd + 1e-3;
    const mesmoId = depois.filter(clip => clip.id === origem.id);
    const destino = mesmoId.find(contem)
      || depois.find(clip => clip.assetId === origem.assetId && contem(clip))
      // O trecho foi aparado do clipe: o overlay encosta na borda que sobrou.
      || mesmoId[0]
      || null;
    if (!destino) return totalDepois + Math.max(0, t - origem.timelineStart);
    const local = clamp(segundoDaFonte, destino.sourceStart, destino.sourceEnd);
    return destino.timelineStart + (local - destino.sourceStart) / destino.playbackRate;
  };
}

function normalizeTimeline(project: VideoEditorProject, anterior?: VideoEditorProject | null): VideoEditorProject {
  let cursor = 0;
  const clips = project.clips.map(clip => {
    const playbackRate = clamp(Number(clip.playbackRate), 0.25, 4);
    const sourceStart = Math.max(0, Number(clip.sourceStart) || 0);
    const sourceEnd = Math.max(sourceStart + 0.08, Number(clip.sourceEnd) || sourceStart + 0.08);
    const duration = (sourceEnd - sourceStart) / playbackRate;
    const next = {
      ...clip,
      sourceStart,
      sourceEnd,
      playbackRate,
      timelineStart: cursor,
      duration,
    };
    cursor += duration;
    return next;
  });
  const identidade = (t: number) => t;
  const mapear = anterior && !clipsIguais(anterior.clips, clips)
    ? mapeadorDeTempo(anterior.clips, clips)
    : identidade;
  return {
    ...project,
    clips,
    duration: cursor,
    // ── Os overlays NÃO são comprimidos para dentro da duração ─────────────
    //
    // Antes, cada normalização fazia `start = clamp(start, 0, cursor - 0.05)` e
    // `end = clamp(end, ..., cursor)`. Isso é DESTRUTIVO: qualquer encurtamento
    // momentâneo da timeline — digitar "25" em "Fim fonte" (o "2" entra antes do
    // "5"), apagar o campo para escrever outro número (vira 0 → o clipe cai para
    // 0,08s), subir a velocidade e voltar — empilhava TODAS as legendas depois do
    // novo fim num intervalo de 50ms, e elas não voltavam quando a duração
    // voltava. Era isso que aparecia como "aumentei o vídeo e todos os overlays
    // ficaram um em cima do outro".
    //
    // Agora o tempo do overlay é preservado. O que passa do fim da timeline
    // simplesmente não aparece na prévia (o playhead nunca chega lá) e fica
    // esmaecido na régua; se a timeline crescer de novo, ele volta ao lugar. A
    // exportação ignora o que ficou além do fim (ver `overlaysDentroDaTimeline`
    // no backend). Só o que é inválido por si (início negativo, fim antes do
    // início) é consertado aqui.
    //
    // E quando os CLIPES mudaram (ver `mapeadorDeTempo`), todo tempo — overlay,
    // trecho de palavra, inserção de tela dividida e marcação — acompanha a fala.
    overlays: project.overlays.map(overlay => {
      const start = Math.max(0, mapear(Number(overlay.start) || 0));
      const end = Math.max(start + 0.05, mapear(Number(overlay.end) || 0));
      if (overlay.type !== 'text' || !overlay.textRuns?.length) return { ...overlay, start, end };
      const runs = mapear === identidade
        ? overlay.textRuns
        : overlay.textRuns.map(run => ({ ...run, start: mapear(run.start), end: mapear(run.end) }));
      return { ...overlay, start, end, textRuns: repairTextRuns(runs, start, end) };
    }),
    ...(mapear !== identidade && project.imageInserts?.length ? {
      imageInserts: project.imageInserts.map(insert => {
        const start = Math.max(0, mapear(insert.start));
        return { ...insert, start, end: Math.max(start + 0.2, mapear(insert.end)) };
      }),
    } : {}),
    ...(mapear !== identidade && project.notes?.length ? {
      notes: project.notes.map(note => {
        const start = Math.max(0, mapear(note.start));
        return { ...note, start, end: Math.max(start + 0.05, mapear(note.end)) };
      }),
    } : {}),
  };
}

function cloneProject(project: VideoEditorProject): VideoEditorProject {
  return structuredClone(project);
}

// Os cartões dos elementos de edição vivem em `EditElementsCards`: o passo a passo
// de produção mostra os MESMOS cartões antes de gerar, e duas cópias do painel era
// o jeito garantido de os dois divergirem (já aconteceu com o catálogo de overlay).

// Estilo de legenda e de gancho DEPOIS de criado o vídeo. Antes os dois só
// existiam na tela de criação e eram consumidos uma vez pelo backend: quem
// escolheu karaokê e viu que a fala era rápida demais para acompanhar não tinha
// como trocar para a clássica sem gastar outro crédito gerando o vídeo de novo.
//
// TROCAR O ESTILO REFAZ O DESENHO, e é por isso que o aviso existe: família,
// peso, corpo, posição na tela, caixa e passo de linha vêm todos da tabela do
// estilo. Não existe "trocar o estilo mantendo o ajuste fino" — o ajuste fino era
// em cima de outro desenho (um corpo 67 serifado não tem como herdar a posição de
// um corpo 42 de duas linhas). Quem só quer mexer num overlay continua tendo o
// inspetor de texto, que não é tocado por aqui.
//
// ── A COR DE DESTAQUE (`accent`) ─────────────────────────────────────────────
//
// Antes ela só existia na tela de criação: depois de gerado, a pessoa não podia
// mais mudar a cor que o `realce` e o `misto` pintam. Agora dá, e o caminho é o
// MESMO da troca de estilo — `headlineAccent` + `restyle` num `commit` (undo
// cobre) e o SERVIDOR repinta.
//
// Por que não pintar aqui: quem desenha a cor no .ass são `run.color` (a 2ª linha
// do `misto`) e `backgroundColor` (o bloco do `realce`), e a regra de qual dos dois
// recebe a cor está na tabela de estilos do backend ("`outline` e `card` NUNCA
// pintam destaque"). Derivar isso no cliente seria a segunda cópia da tabela
// dentro do editor — o defeito que `trocarEstilo` foi escrito para evitar. Só
// mandar `headlineAccent` sem `restyle` também não serve: o campo é persistido mas
// não repinta nada, e aí seria mais um controle que mexe o número e não muda a
// tela.
//
// O seletor aparece SÓ em `realce` e `misto` pelo mesmo motivo: nos outros dois a
// cor não é desenhada em lugar nenhum do vídeo.
function StylePanel({
  captionStyle,
  headlineStyle,
  accent,
  legendas,
  ganchos,
  onCaptionStyle,
  onHeadlineStyle,
  onAccent,
}: {
  captionStyle: VideoEditorCaptionStyle | null;
  headlineStyle: VideoEditorHeadlineStyle | null;
  accent: string;
  legendas: number;
  ganchos: number;
  onCaptionStyle: (estilo: VideoEditorCaptionStyle) => void;
  onHeadlineStyle: (estilo: VideoEditorHeadlineStyle) => void;
  onAccent: (cor: string) => void;
}) {
  // Sem overlay estilizado não há o que trocar: o vídeo foi gerado com o desenho
  // antigo (bloco tipográfico da marca ou legenda antiga) e sortear um estilo aqui
  // mudaria a cara de um vídeo já aprovado sem ninguém pedir.
  if (!legendas && !ganchos) return null;
  const rotuloLegenda = CAPTION_STYLE_CARDS.find(c => c.value === captionStyle)?.label;
  const rotuloGancho = HEADLINE_STYLE_CARDS.find(c => c.value === headlineStyle)?.label;
  return (
    <div className="space-y-3">
      <style>{CAPTION_DEMO_CSS}</style>
      {captionStyle && (
        <div>
          <FieldLabel>Estilo de legenda · {rotuloLegenda}</FieldLabel>
          <CaptionStyleCards value={captionStyle} onChange={onCaptionStyle} tema="escuro" colunas="grid-cols-2" />
        </div>
      )}
      {headlineStyle && (
        <div>
          <FieldLabel>Estilo de gancho · {rotuloGancho}</FieldLabel>
          <HeadlineStyleCards value={headlineStyle} onChange={onHeadlineStyle} accent={accent} tema="escuro" colunas="grid-cols-2" />
          {(headlineStyle === 'realce' || headlineStyle === 'misto') && (
            <div className="mt-2">
              <FieldLabel>Cor de destaque</FieldLabel>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={accent.slice(0, 7)}
                  onChange={event => onAccent(event.target.value)}
                  className="h-9 w-14 shrink-0 rounded-lg border border-slate-700 bg-slate-900 p-1"
                  aria-label="Cor de destaque do gancho"
                />
                <p className="text-[9px] leading-snug text-slate-500">
                  {headlineStyle === 'realce'
                    ? 'É a cor do bloco atrás de cada linha do gancho.'
                    : 'É a cor da segunda linha do gancho.'}
                  {' '}Vem da paleta da marca; trocar aqui redesenha o gancho no servidor.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
      <p className="rounded-lg border border-amber-400/20 bg-amber-500/10 p-1.5 text-[9px] leading-snug text-amber-200">
        <strong>Trocar o estilo redesenha {legendas ? `${legendas} legenda(s)` : ''}{legendas && ganchos ? ' e ' : ''}{ganchos ? 'o gancho' : ''}.</strong>{' '}
        Tipografia, corpo, posição e caixa vêm da tabela do estilo novo — ajuste manual feito
        nesses textos é perdido. O texto, o tempo e a trava continuam. Dá desfazer com Ctrl+Z.
      </p>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="mb-1 block text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">{children}</label>;
}

function NumberInput({
  label,
  value,
  min,
  max,
  step = 0.1,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
}) {
  // Rascunho local do texto digitado. O campo era controlado direto pelo número
  // do projeto e aplicava `clamp(Number(texto))` a CADA tecla: apagar o campo
  // para digitar outro valor virava `Number('') = 0`, e o clipe era encurtado
  // para o mínimo no meio da digitação. Agora o texto vazio ou inválido fica só
  // no rascunho; o projeto recebe apenas números válidos e, ao sair do campo, o
  // rascunho volta a mostrar o valor real.
  const [draft, setDraft] = useState(String(Number(value.toFixed(3))));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setDraft(String(Number(value.toFixed(3))));
  }, [value, focused]);
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <input
        type="number"
        value={draft}
        min={min}
        max={max}
        step={step}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          setDraft(String(Number(value.toFixed(3))));
        }}
        onChange={event => {
          const text = event.target.value;
          setDraft(text);
          if (text.trim() === '') return;
          const parsed = Number(text);
          if (!Number.isFinite(parsed)) return;
          onChange(clamp(parsed, min, max));
        }}
        className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-2 text-xs text-slate-100 outline-none focus:border-indigo-500"
      />
    </div>
  );
}

function SoundEffectControls({
  soundEffects,
  value,
  volume,
  previewingId,
  onChange,
  onVolumeChange,
  onPreview,
}: {
  soundEffects: VideoEditorSoundEffect[];
  value?: string | null;
  volume: number;
  previewingId: string | null;
  onChange: (value: string | null, defaultVolume?: number) => void;
  onVolumeChange: (value: number) => void;
  onPreview: (soundEffectId: string, volume: number) => void;
}) {
  const selected = soundEffects.find(effect => effect.id === value);
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
      <FieldLabel>Som de entrada</FieldLabel>
      <div className="flex gap-2">
        <select
          value={value || 'none'}
          onChange={event => {
            const next = event.target.value === 'none' ? null : event.target.value;
            const effect = soundEffects.find(item => item.id === next);
            onChange(next, effect?.defaultVolume);
          }}
          className="min-w-0 flex-1 rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-2 text-xs"
        >
          <option value="none">Nenhum</option>
          {soundEffects.map(effect => (
            <option key={effect.id} value={effect.id}>{SOUND_EFFECT_LABELS[effect.id] || effect.name || effect.id}</option>
          ))}
        </select>
        <button
          type="button"
          disabled={!selected}
          onClick={() => selected && onPreview(selected.id, volume)}
          className="rounded-lg border border-white/10 px-2.5 text-xs hover:bg-white/10 disabled:opacity-30"
          aria-label="Ouvir efeito sonoro"
        >
          {previewingId === selected?.id ? 'Ⅱ' : '▶'}
        </button>
      </div>
      {selected && (
        <div className="mt-3">
          <div className="mb-1 flex justify-between text-[9px] text-slate-500"><span>Volume</span><span>{Math.round(volume * 100)}%</span></div>
          <input type="range" min={0} max={1} step={0.05} value={volume} onChange={event => onVolumeChange(Number(event.target.value))} className="w-full accent-emerald-500" />
        </div>
      )}
    </div>
  );
}

function EditorPortal({ children }: { children: React.ReactNode }) {
  return createPortal(children, document.body);
}

export function VideoProjectEditor({ jobId, target, title, onClose }: Props) {
  const [history, setHistory] = useState<HistoryState | null>(null);
  // Seleção múltipla. `selection` (o item primário, = o último selecionado) continua
  // existindo e é o que os painéis de propriedade editam; as ações de conjunto
  // (mover, excluir, duplicar) usam a lista inteira.
  const [selectedItems, setSelectedItems] = useState<SelectionItem[]>([]);
  const [marquee, setMarquee] = useState<Marquee | null>(null);
  const [playhead, setPlayhead] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [zoom, setZoom] = useState(34);
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [render, setRender] = useState<VideoEditorRender | null>(null);
  const [conflictProject, setConflictProject] = useState<VideoEditorProject | null>(null);
  const [assetUrls, setAssetUrls] = useState<Record<string, string>>({});
  // Desenhos de fundo dos blocos da régua: onda sonora e tira de miniaturas.
  // Chave: `${assetId}:${kind}`.
  const [assetGraphics, setAssetGraphics] = useState<Record<string, string>>({});
  const [draggedClipId, setDraggedClipId] = useState<string | null>(null);
  const [draggedAssetId, setDraggedAssetId] = useState<string | null>(null);
  const [uploadingAssets, setUploadingAssets] = useState(false);
  const [externalDragActive, setExternalDragActive] = useState(false);
  const [deletingAssetId, setDeletingAssetId] = useState<string | null>(null);
  const [downloadingAssetId, setDownloadingAssetId] = useState<string | null>(null);
  const [soundEffects, setSoundEffects] = useState<VideoEditorSoundEffect[]>([]);
  // Trilha do Instagram: catalogo consultado sob demanda (so quando o usuario
  // abre o seletor), porque e chamada remota e a maioria das sessoes nao troca
  // a faixa que a IA escolheu.
  const [musicPickerOpen, setMusicPickerOpen] = useState(false);
  const [musicCatalog, setMusicCatalog] = useState<VideoEditorMusicTrack[]>([]);
  const [musicQuery, setMusicQuery] = useState('');
  const [musicLoading, setMusicLoading] = useState(false);
  const [musicCatalogError, setMusicCatalogError] = useState<string | null>(null);
  const musicAudioRef = useRef<HTMLAudioElement | null>(null);
  const [musicPlaybackError, setMusicPlaybackError] = useState<string | null>(null);
  const [previewingSoundEffectId, setPreviewingSoundEffectId] = useState<string | null>(null);
  const [stageHeight, setStageHeight] = useState(0);
  // Ligado por padrão: o erro que ele evita (texto atrás da interface do app) só
  // aparece depois de publicado, quando já não dá para corrigir.
  const [showSafeArea, setShowSafeArea] = useState(true);
  const assetInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const timelineContentRef = useRef<HTMLDivElement>(null);
  const objectUrlsRef = useRef<string[]>([]);
  const assetExpiryRef = useRef<Record<string, number>>({});
  const assetRetryRef = useRef<Record<string, number>>({});
  const assetLoadingRef = useRef<Set<string>>(new Set());
  const graphicsLoadingRef = useRef<Set<string>>(new Set());
  const soundPreviewAudioRef = useRef<HTMLAudioElement | null>(null);
  const soundPreviewUrlRef = useRef<string | null>(null);
  const soundEffectUrlsRef = useRef<Record<string, string>>({});
  const triggeredSoundEffectsRef = useRef<Set<string>>(new Set());
  const previousPlaybackTimeRef = useRef(0);
  const wasPlayingSoundRef = useRef(false);
  const imagePlaybackRafRef = useRef<number | null>(null);
  const imagePlaybackStartedRef = useRef<{ at: number; playhead: number } | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const renderTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const playingRef = useRef(false);
  const activeClipIdRef = useRef<string | null>(null);
  const editGenerationRef = useRef(0);
  const projectRef = useRef<VideoEditorProject | null>(null);
  const renderGenerationRef = useRef(0);
  // A seleção é lida por ref no atalho de teclado pelo mesmo motivo da agulha:
  // o handler é inscrito uma vez e não deve se reinscrever a cada clique.
  const selectedItemsRef = useRef<SelectionItem[]>([]);
  selectedItemsRef.current = selectedItems;
  const project = history?.present || null;
  projectRef.current = project;
  // A agulha muda a cada quadro durante a reprodução: lê-la por ref evita
  // reinscrever o atalho de teclado sessenta vezes por segundo.
  const playheadRef = useRef(0);
  playheadRef.current = playhead;
  const stageScale = project && stageHeight > 0 ? stageHeight / project.height : 1;
  const projectFontFamilies = useMemo(
    () => [...new Set((project?.overlays || []).flatMap(overlay => overlay.type === 'text' ? [
      overlay.fontFamily,
      ...(overlay.textRuns || []).map(run => run.fontFamily),
    ] : []).filter(Boolean))].sort().join('|'),
    [project?.overlays],
  );
  // Pesos que o projeto REALMENTE usa. Até aqui a prévia registrava só 400 e 900
  // (derivados do booleano `bold`), e os estilos pedem 500 (clássica), 600
  // (simples), 700 (serifada) e 800 (gancho contorno): o navegador falsificava
  // negrito em cima de um arquivo regular e a legenda serifada aparecia num
  // desenho que o MP4 não tem.
  const projectFontWeights = useMemo(
    () => [...new Set((project?.overlays || []).flatMap(overlay => overlay.type === 'text' ? [
      overlay.fontWeight,
      ...(overlay.textRuns || []).map(run => run.fontWeight),
    ] : []).filter((peso): peso is number => Number.isFinite(peso as number)))].sort().join('|'),
    [project?.overlays],
  );

  useEffect(() => {
    // TODAS as famílias do projeto, inclusive as do index.html. A folha global
    // carrega os pesos 400–900 mas NENHUM itálico, e o bloco empilhado usa
    // itálico em duas das quatro variantes (a primeira linha e a de acento). Com
    // as famílias do index.html fora desta lista, o navegador falsificava a
    // inclinação em cima do romano enquanto o MP4 saía com o itálico desenhado —
    // medido em Roboto Condensed 900: 720px de largura falsificado contra 699px
    // com o arquivo real, no mesmo texto e corpo.
    const customFonts = projectFontFamilies.split('|').filter(Boolean);
    const linkId = 'astra-video-editor-project-fonts';
    const styleId = 'astra-video-editor-project-font-files';
    document.getElementById(linkId)?.remove();
    document.getElementById(styleId)?.remove();
    if (!customFonts.length) return;
    let cancelled = false;
    const objectUrls: string[] = [];
    const style = document.createElement('style');
    style.id = styleId;
    document.head.appendChild(style);
    let fallbackLink: HTMLLinkElement | null = null;
    const loadProjectFonts = async () => {
      const fallbackFonts: string[] = [];
      // Uma variante por papel do bloco: o corpo leve é 400, os demais são 900 e
      // a linha de acento é itálica. Registrar UM arquivo para os quatro casos
      // obrigava o navegador a falsificar negrito e itálico em cima de um arquivo
      // já preto — era o que deixava a tipografia do combo "bugada" no editor.
      // Os pesos EXPLÍCITOS do projeto entram junto dos quatro de sempre: sem
      // eles a face 600/700 nunca chegava ao navegador.
      const pesosDoProjeto = projectFontWeights.split('|').filter(Boolean).map(Number);
      //
      // Os pesos do projeto entram nas DUAS inclinações. Antes só o romano era
      // registrado, e um trecho itálico em 500/600/700/800 caía na inclinação
      // SINTÉTICA do navegador (ele cisalha o romano) enquanto o .ass do render
      // pede o arquivo itálico de verdade. MEDIDO em Poppins 600, mesmo texto e
      // mesmo corpo: 65,92px de largura no romano, 69,27px na inclinação
      // falsificada e 66,68px com a face itálica servida pelo endpoint — três
      // larguras diferentes, e a do meio é a que não existe no MP4.
      //
      // O CUSTO é uma requisição por variante e por família a cada job: por isso
      // entram só os pesos que aparecem DE FATO nos overlays (`projectFontWeights`
      // varre o overlay e os trechos), e não a matriz cheia de pesos x itálico.
      // Num projeto com legenda `serifada` e gancho `misto` isso são 2 famílias x
      // (4 fixas + 700 e 600 nas duas inclinações) = 16 pedidos, contra 36 da
      // matriz 100-900 x 2.
      const variants = [
        { weight: 400, italic: false },
        { weight: 900, italic: false },
        { weight: 400, italic: true },
        { weight: 900, italic: true },
        ...pesosDoProjeto
          .filter(peso => peso !== 400 && peso !== 900)
          .flatMap(peso => [
            { weight: peso, italic: false },
            { weight: peso, italic: true },
          ]),
      ];
      await Promise.all(customFonts.map(async font => {
        const safeFamily = font.replace(/['"\\{};]/g, '');
        const faces = await Promise.all(variants.map(async variant => {
          try {
            const blob = await api.downloadVideoEditorFont(jobId, target, font, variant.weight, variant.italic);
            if (cancelled) return null;
            const objectUrl = URL.createObjectURL(blob);
            objectUrls.push(objectUrl);
            return `@font-face{font-family:'${safeFamily}';src:url('${objectUrl}');`
              + `font-weight:${variant.weight};font-style:${variant.italic ? 'italic' : 'normal'};font-display:swap;}`;
          } catch {
            return null;
          }
        }));
        const loaded = faces.filter(Boolean);
        // Só cai no Google Fonts se NENHUMA variante tiver vindo do servidor — e
        // as famílias do index.html não precisam nem disso: a folha global já
        // está na página, então pedir de novo só duplicaria requisição.
        if (!loaded.length && !BUNDLED_WEB_FONTS.has(font)) fallbackFonts.push(font);
        else if (!cancelled) style.textContent += loaded.join('');
      }));
      if (cancelled || !fallbackFonts.length) return;
      fallbackLink = document.createElement('link');
      fallbackLink.id = linkId;
      fallbackLink.rel = 'stylesheet';
      // Sem eixos, de propósito: o css2 responde 400 para a família que não
      // publica o peso ou o itálico pedido (EB Garamond não tem 900, Libre
      // Baskerville só tem 400 e 700), e um único 400 derrubava a folha INTEIRA
      // — todas as fontes do combo sumiam de uma vez por causa de uma. Aqui o
      // negrito pode sair falsificado, mas este é o último recurso: o caminho
      // normal são os arquivos reais servidos acima.
      fallbackLink.href = `https://fonts.googleapis.com/css2?${fallbackFonts
        .map(font => `family=${encodeURIComponent(font).replace(/%20/g, '+')}`)
        .join('&')}&display=swap`;
      document.head.appendChild(fallbackLink);
    };
    void loadProjectFonts();
    return () => {
      cancelled = true;
      style.remove();
      fallbackLink?.remove();
      objectUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [jobId, projectFontFamilies, projectFontWeights, target]);

  // O item primário é o último clicado: é ele que os painéis de propriedade editam.
  const selection = useMemo<Selection>(
    () => selectedItems.length ? selectedItems[selectedItems.length - 1] : null,
    [selectedItems],
  );
  // Mesma assinatura de antes (setSelection(item | null)), então todos os pontos
  // que já selecionavam um item continuam valendo — agora zerando a multi-seleção.
  const setSelection = useCallback((next: Selection) => setSelectedItems(next ? [next] : []), []);
  const isSelected = useCallback(
    (kind: SelectionItem['kind'], id: string) => selectedItems.some(item => item.kind === kind && item.id === id),
    [selectedItems],
  );
  const selectedClipIds = useMemo(
    () => selectedItems.filter(item => item.kind === 'clip').map(item => item.id),
    [selectedItems],
  );
  const selectedOverlayIds = useMemo(
    () => selectedItems.filter(item => item.kind === 'overlay').map(item => item.id),
    [selectedItems],
  );

  // Clique com modificador: Ctrl/Cmd alterna um item, Shift pega a faixa inteira
  // entre o primário e o clicado (na ordem em que aparecem na trilha).
  const handleTimelineItemClick = useCallback((
    event: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean },
    kind: SelectionItem['kind'],
    id: string,
    orderedIds: string[],
  ) => {
    const additive = event.ctrlKey || event.metaKey;
    if (additive) {
      setSelectedItems(current => current.some(item => item.kind === kind && item.id === id)
        ? current.filter(item => !(item.kind === kind && item.id === id))
        : [...current, { kind, id }]);
      return false;
    }
    if (event.shiftKey) {
      setSelectedItems(current => {
        const anchor = [...current].reverse().find(item => item.kind === kind);
        const fromIndex = anchor ? orderedIds.indexOf(anchor.id) : -1;
        const toIndex = orderedIds.indexOf(id);
        if (fromIndex < 0 || toIndex < 0) return [{ kind, id }];
        const [a, b] = fromIndex <= toIndex ? [fromIndex, toIndex] : [toIndex, fromIndex];
        const range = orderedIds.slice(a, b + 1).map(rangeId => ({ kind, id: rangeId }));
        const others = current.filter(item => item.kind !== kind);
        return [...others, ...range];
      });
      return false;
    }
    setSelectedItems([{ kind, id }]);
    return true;
  }, []);

  const selectedClip = useMemo(
    () => selection?.kind === 'clip' ? project?.clips.find(clip => clip.id === selection.id) || null : null,
    [project, selection],
  );
  // Elementos de edição do projeto, com o mesmo default do painel. Serve também ao
  // inspetor de clipe: o controle de centro do zoom só faz sentido quando existe
  // recorte para deslocar.
  const editElementsAtivos = useMemo(
    () => ({ ...EDIT_ELEMENTS_DEFAULT, ...(project?.editElements || {}) }),
    [project?.editElements],
  );
  // Estilos em uso no projeto. Vêm dos OVERLAYS e não de um campo do projeto de
  // propósito: é neles que o backend carimba a escolha (e é neles que ela
  // sobrevive num manifesto reaberto), e um vídeo pode ter legenda estilizada sem
  // gancho estilizado. `null` = nenhum overlay estilizado: o vídeo saiu com o
  // desenho antigo e o painel de estilos nem aparece.
  const estilosEmUso = useMemo(() => {
    const textos = (project?.overlays || []).filter(
      (overlay): overlay is VideoEditorTextOverlay => overlay.type === 'text',
    );
    const legendas = textos.filter(overlay => overlay.captionStyle);
    const ganchos = textos.filter(overlay => overlay.headlineStyle);
    return {
      captionStyle: (legendas[0]?.captionStyle || null) as VideoEditorCaptionStyle | null,
      headlineStyle: (ganchos[0]?.headlineStyle || null) as VideoEditorHeadlineStyle | null,
      accent: ganchos[0]?.headlineAccent || '#FF5200',
      legendas: legendas.length,
      ganchos: ganchos.length,
    };
  }, [project?.overlays]);
  const selectedOverlay = useMemo(
    () => selection?.kind === 'overlay' ? project?.overlays.find(overlay => overlay.id === selection.id) || null : null,
    [project, selection],
  );
  const selectedTextOverlay = selectedOverlay?.type === 'text' ? selectedOverlay : null;
  const selectedImageOverlay = selectedOverlay?.type === 'image' ? selectedOverlay : null;
  // O modo "apenas legenda" do pipeline solta ~40 blocos de 2-3 palavras, um por
  // grupo de fala, todos na mesma altura. Editando um por vez, subir a legenda de
  // um Reel de 60s é inviável — então o inspetor passa a trabalhar sobre a LISTA
  // de camadas de texto selecionadas, e não só sobre a primária.
  const selectedTextOverlays = useMemo(
    () => (project?.overlays.filter(
      (overlay): overlay is VideoEditorTextOverlay => overlay.type === 'text' && selectedOverlayIds.includes(overlay.id),
    ) || []),
    [project?.overlays, selectedOverlayIds],
  );
  // Lote é só quando a primária também é camada: com um clipe primário quem manda
  // no painel continua sendo o painel do clipe.
  const overlayLoteAtivo = selection?.kind === 'overlay' && selectedTextOverlays.length > 1;
  const selectedTextOverlayIds = useMemo(
    () => selectedTextOverlays.map(overlay => overlay.id),
    [selectedTextOverlays],
  );
  // O caso de uso real é "todas as legendas de uma vez", não "todos os textos":
  // título, CTA e selo também são overlays de texto e não devem ir junto.
  const captionOverlayIds = useMemo(
    () => project?.overlays
      .filter(ehLegenda)
      .map(overlay => overlay.id) || [],
    [project?.overlays],
  );
  // Os campos em lote mostram os valores da camada PRIMÁRIA (a última clicada),
  // mesmo critério do painel de um item só — e não uma média, que não
  // corresponderia a nenhuma legenda de verdade.
  const loteReferencia = selectedTextOverlay || selectedTextOverlays[0] || null;
  // `some` e não a camada de referência: basta UMA das selecionadas ter peso do
  // estilo para o clique já não ser honesto — o gesto em lote escreveria `bold` em
  // todas e a que tem peso não mudaria nem na tela nem no MP4. E é o caso comum:
  // "Selecionar todas as legendas" pega justamente as legendas da IA, que TODAS
  // carregam `fontWeight`.
  const loteTravaNegrito = selectedTextOverlays.some(pesoDoEstiloTravaNegrito);

  const selecionarTodasLegendas = useCallback(() => {
    if (!captionOverlayIds.length) return;
    setSelectedItems(captionOverlayIds.map(id => ({ kind: 'overlay' as const, id })));
  }, [captionOverlayIds]);
  const selectedClipAsset = useMemo(
    () => project?.assets.find(asset => asset.id === selectedClip?.assetId) || null,
    [project?.assets, selectedClip?.assetId],
  );
  const activeClip = useMemo(() => {
    if (!project) return null;
    return project.clips.find(clip => playhead >= clip.timelineStart && playhead < clip.timelineStart + clip.duration)
      || project.clips[project.clips.length - 1]
      || null;
  }, [playhead, project]);
  const activeAsset = useMemo(
    () => project?.assets.find(asset => asset.id === activeClip?.assetId) || null,
    [activeClip?.assetId, project?.assets],
  );

  const loadProject = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await api.getVideoEditorProject(jobId, target);
      const loaded = normalizeTimeline(response.project);
      setHistory({ past: [], present: loaded, future: [] });
      projectRef.current = loaded;
      renderGenerationRef.current = editGenerationRef.current;
      setRender(response.render?.projectRevision === loaded.revision
        ? response.render
        : response.render ? { ...response.render, status: 'idle', progress: 0, url: undefined } : null);
      setPlayhead(0);
      setSelection(loaded.clips[0] ? { kind: 'clip', id: loaded.clips[0].id } : null);
      setSaveState('saved');
      setConflictProject(null);
    } catch (error: any) {
      setLoadError(error?.message || 'Não foi possível abrir o projeto.');
    } finally {
      setLoading(false);
    }
  }, [jobId, target]);

  useEffect(() => {
    loadProject();
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (renderTimerRef.current) clearInterval(renderTimerRef.current);
      objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
      objectUrlsRef.current = [];
      // Os desenhos da régua apontam para blobs que acabaram de ser revogados, e
      // uma chave presente conta como resolvida — sem limpar aqui, trocar de
      // projeto sem desmontar deixaria a régua cega para sempre.
      setAssetGraphics({});
      setAssetUrls({});
    };
  }, [loadProject]);

  useEffect(() => {
    let cancelled = false;
    api.listVideoEditorSoundEffects()
      .then(response => { if (!cancelled) setSoundEffects(response.soundEffects || []); })
      .catch(() => { if (!cancelled) setSoundEffects([]); });
    return () => {
      cancelled = true;
      soundPreviewAudioRef.current?.pause();
      soundPreviewAudioRef.current = null;
      Object.values(soundEffectUrlsRef.current).forEach(url => URL.revokeObjectURL(url));
      soundEffectUrlsRef.current = {};
      if (soundPreviewUrlRef.current) URL.revokeObjectURL(soundPreviewUrlRef.current);
      soundPreviewUrlRef.current = null;
    };
  }, []);

  const invalidateCompletedRender = useCallback(() => {
    setRender(current => current?.status === 'completed'
      ? { ...current, status: 'idle', progress: 0, url: undefined }
      : current);
  }, []);

  const commit = useCallback((updater: (draft: VideoEditorProject) => void) => {
    setHistory(current => {
      if (!current) return current;
      const before = cloneProject(current.present);
      const draft = cloneProject(current.present);
      updater(draft);
      const present = normalizeTimeline(draft, current.present);
      return {
        past: [...current.past.slice(-49), before],
        present,
        future: [],
      };
    });
    editGenerationRef.current += 1;
    invalidateCompletedRender();
    setSaveState('dirty');
  }, [invalidateCompletedRender]);

  const replaceDuringGesture = useCallback((updater: (draft: VideoEditorProject) => void) => {
    setHistory(current => {
      if (!current) return current;
      const draft = cloneProject(current.present);
      updater(draft);
      return { ...current, present: normalizeTimeline(draft, current.present) };
    });
    editGenerationRef.current += 1;
    invalidateCompletedRender();
    setSaveState('dirty');
  }, [invalidateCompletedRender]);

  const pushGestureHistory = useCallback((before: VideoEditorProject) => {
    setHistory(current => {
      if (!current) return current;
      const rebasedBefore = {
        ...before,
        revision: current.present.revision,
        updatedAt: current.present.updatedAt,
      };
      return {
        past: [...current.past.slice(-49), rebasedBefore],
        present: current.present,
        future: [],
      };
    });
  }, []);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const updateSize = () => setStageHeight(stage.getBoundingClientRect().height);
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [project?.aspectRatio]);

  const undo = useCallback(() => {
    setHistory(current => {
      if (!current?.past.length) return current;
      const previous = current.past[current.past.length - 1];
      return {
        past: current.past.slice(0, -1),
        present: cloneProject(previous),
        future: [cloneProject(current.present), ...current.future],
      };
    });
    editGenerationRef.current += 1;
    invalidateCompletedRender();
    setSaveState('dirty');
  }, [invalidateCompletedRender]);

  const redo = useCallback(() => {
    setHistory(current => {
      if (!current?.future.length) return current;
      const next = current.future[0];
      return {
        past: [...current.past, cloneProject(current.present)],
        present: cloneProject(next),
        future: current.future.slice(1),
      };
    });
    editGenerationRef.current += 1;
    invalidateCompletedRender();
    setSaveState('dirty');
  }, [invalidateCompletedRender]);

  const saveNow = useCallback(async (quiet = false) => {
    if (!history?.present || saveState === 'saving') return null;
    const snapshot = history.present;
    const savedGeneration = editGenerationRef.current;
    setSaveState('saving');
    try {
      const response = await api.saveVideoEditorProject(jobId, target, snapshot, snapshot.revision);
      const hasNewerLocalEdits = editGenerationRef.current !== savedGeneration;
      setHistory(current => {
        if (!current) return current;
        const rebaseRevision = (value: VideoEditorProject) => ({
          ...value,
          revision: response.project.revision,
          updatedAt: response.project.updatedAt,
        });
        if (!hasNewerLocalEdits) return {
          past: current.past.map(rebaseRevision),
          present: response.project,
          future: current.future.map(rebaseRevision),
        };
        return {
          past: current.past.map(rebaseRevision),
          present: rebaseRevision(current.present),
          future: current.future.map(rebaseRevision),
        };
      });
      setSaveState(hasNewerLocalEdits ? 'dirty' : 'saved');
      setConflictProject(null);
      if (!quiet && !hasNewerLocalEdits) toast.success('Projeto salvo.');
      return hasNewerLocalEdits ? null : response.project;
    } catch (error: any) {
      const conflict = /outra aba|recarregue|conflito/i.test(error?.message || '');
      setSaveState(conflict ? 'conflict' : 'error');
      if (conflict && error?.project) setConflictProject(normalizeTimeline(error.project));
      if (!quiet) toast.error(error?.message || 'Erro ao salvar projeto.');
      return null;
    }
  }, [history?.present, jobId, saveState, target]);

  // ── Salvar DEPOIS do commit, não com o snapshot velho ──────────────────────
  //
  // `saveNow` fecha sobre `history.present` do render em que foi criado. Chamá-lo
  // na mesma volta do `commit` mandava ao servidor o projeto de ANTES da mudança e,
  // pior, instalava a resposta em `present` — o commit era sobrescrito pelo eco do
  // estado velho, e o autosave nem chegava a rodar porque o save "deu certo" e o
  // estado virou 'saved'.
  //
  // MEDIDO com dublê do servidor: clicar no card "Clássica" mandava
  // `captionStyle: 'serifada', restyle: false` no corpo do PUT e o rótulo do painel
  // continuava "SERIFADA"; trocar a cor de destaque mandava o `headlineAccent`
  // antigo sem `restyle` e o gancho ficava verde. Ou seja: o painel de estilos
  // inteiro era um controle inerte, e não só a cor.
  //
  // A ref sempre aponta para o `saveNow` do último render; o macrotask garante que
  // ele já esteja recriado com o `present` novo (o React libera o commit antes de
  // ir para a fila de timers).
  const saveNowRef = useRef(saveNow);
  saveNowRef.current = saveNow;
  const salvarDepoisDoCommit = useCallback(() => {
    setTimeout(() => { void saveNowRef.current(true); }, 0);
  }, []);

  const trocarEstilo = useCallback((campo: 'captionStyle' | 'headlineStyle', estilo: string) => {
    // Escreve pelo MESMO caminho de todas as outras edições (`commit`), então o
    // desfazer cobre a troca inteira. O `restyle` é um COMANDO para o servidor:
    // quem refaz o desenho é ele, com a tabela do motor de overlays — derivar
    // família, peso, corpo, posição e passo de linha aqui seria a segunda cópia da
    // tabela no projeto, que é exatamente o defeito que estamos consertando.
    commit(draft => {
      for (const overlay of draft.overlays) {
        if (overlay.type !== 'text') continue;
        if (!overlay[campo]) continue;
        if (campo === 'captionStyle') overlay.captionStyle = estilo as VideoEditorCaptionStyle;
        else overlay.headlineStyle = estilo as VideoEditorHeadlineStyle;
        overlay.restyle = true;
      }
    });
    // Salvar na hora: o desenho novo só existe depois da volta do servidor, e o
    // `saveNow` já instala o projeto saneado que ele devolve. Sem isto a pessoa
    // veria o rótulo trocar e o texto na tela continuar no desenho antigo até o
    // autosave. Pelo agendador, nunca direto: ver `salvarDepoisDoCommit`.
    salvarDepoisDoCommit();
  }, [commit, salvarDepoisDoCommit]);

  // Cor de destaque do gancho DEPOIS de gerado. Mesmo caminho de `trocarEstilo`,
  // e pelos mesmos motivos: `commit` (então o Ctrl+Z desfaz a troca inteira em um
  // passo), `restyle` como COMANDO para o servidor repintar, e `saveNow` na hora
  // porque o desenho novo só existe na volta dele — sem isso a pessoa veria o
  // seletor mudar e o gancho na tela continuar na cor antiga até o autosave.
  //
  // Quem pinta com a cor é a tabela do estilo no backend (`realce` no bloco,
  // `misto` na 2ª linha; `outline` e `card` não pintam): derivar `run.color` e
  // `backgroundColor` aqui seria a segunda cópia dessa tabela no cliente.
  const trocarAccent = useCallback((cor: string) => {
    commit(draft => {
      for (const overlay of draft.overlays) {
        if (overlay.type !== 'text') continue;
        if (!overlay.headlineStyle) continue;
        overlay.headlineAccent = cor;
        overlay.restyle = true;
      }
    });
    salvarDepoisDoCommit();
  }, [commit, salvarDepoisDoCommit]);

  const acceptRemoteConflict = useCallback(() => {
    if (!conflictProject) return;
    const remote = normalizeTimeline(conflictProject);
    setHistory({ past: [], present: remote, future: [] });
    projectRef.current = remote;
    setSelection(remote.clips[0] ? { kind: 'clip', id: remote.clips[0].id } : null);
    setConflictProject(null);
    setSaveState('saved');
    toast.success('Versão mais recente carregada.');
  }, [conflictProject]);

  const keepLocalConflict = useCallback(() => {
    if (!conflictProject) return;
    const rebase = (value: VideoEditorProject) => ({
      ...value,
      revision: conflictProject.revision,
      updatedAt: conflictProject.updatedAt,
    });
    setHistory(current => current ? {
      past: current.past.map(rebase),
      present: rebase(current.present),
      future: current.future.map(rebase),
    } : current);
    setConflictProject(null);
    setSaveState('dirty');
    toast('Suas edições foram mantidas e serão salvas sobre a revisão atual.');
  }, [conflictProject]);

  useEffect(() => {
    if (saveState !== 'dirty') return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { void saveNow(true); }, 1400);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [history?.present, saveNow, saveState]);

  const ensureAssetUrl = useCallback(async (asset: VideoEditorAsset) => {
    const cachedExpiry = assetExpiryRef.current[asset.id] || 0;
    if (assetUrls[asset.id] && cachedExpiry > Date.now() + 60_000) return;
    if (assetLoadingRef.current.has(asset.id)) return;
    assetLoadingRef.current.add(asset.id);
    try {
      const response = await api.getVideoEditorAssetUrl(jobId, asset.id);
      assetExpiryRef.current[asset.id] = Date.parse(response.asset.expiresAt || '') || Date.now() + 10 * 60 * 1000;
      setAssetUrls(current => ({ ...current, [asset.id]: response.asset.url }));
    } catch {
      try {
        const blob = await api.downloadVideoEditorArtifact(jobId, asset.artifactId);
        const url = URL.createObjectURL(blob);
        objectUrlsRef.current.push(url);
        assetExpiryRef.current[asset.id] = Number.MAX_SAFE_INTEGER;
        setAssetUrls(current => ({ ...current, [asset.id]: url }));
      } catch (error: any) {
        toast.error(error?.message || `Erro ao carregar ${asset.name}.`);
      }
    } finally {
      assetLoadingRef.current.delete(asset.id);
    }
  }, [assetUrls, jobId]);

  // A onda sonora e a tira de miniaturas são o que transforma o bloco da régua de
  // um retângulo cego em algo onde dá para VER o silêncio e acertar o corte de
  // primeira. Falhar em carregá-las não pode atrapalhar a edição — a régua
  // continua funcionando sem elas.
  const ensureAssetGraphics = useCallback(async (assetId: string, kind: 'waveform' | 'filmstrip') => {
    const key = `${assetId}:${kind}`;
    // `in` e não truthy: string vazia é uma resposta legítima ("não há desenho
    // para esta mídia") e precisa contar como resolvida. Sem isso, cada edição
    // rearmava o pedido, e cada pedido baixa o vídeo inteiro no servidor — um
    // arrasto de bordinha viraria trabalho de servidor sem limite.
    if (key in assetGraphics || graphicsLoadingRef.current.has(key)) return;
    graphicsLoadingRef.current.add(key);
    try {
      const url = await api.getVideoEditorAssetGraphics(jobId, assetId, kind);
      if (url) objectUrlsRef.current.push(url);
      setAssetGraphics(current => ({ ...current, [key]: url }));
    } catch {
      // Falhou: registra o fracasso para não tentar de novo em toda re-renderização.
      // É enfeite funcional, não conteúdo — a régua funciona sem ele.
      setAssetGraphics(current => ({ ...current, [key]: '' }));
    } finally {
      graphicsLoadingRef.current.delete(key);
    }
  }, [assetGraphics, jobId]);

  useEffect(() => {
    if (!project) return;
    const videoAssetIds = new Set(
      project.clips
        .map(clip => project.assets.find(asset => asset.id === clip.assetId))
        .filter((asset): asset is VideoEditorAsset => Boolean(asset) && asset!.kind === 'video')
        .map(asset => asset.id),
    );
    videoAssetIds.forEach(assetId => {
      void ensureAssetGraphics(assetId, 'waveform');
      void ensureAssetGraphics(assetId, 'filmstrip');
    });
  }, [ensureAssetGraphics, project]);

  // ── Marcar e reclamar ──────────────────────────────────────────────────────
  // O usuário estaciona a agulha, aperta M no começo do problema, vai até o fim,
  // aperta M de novo e escreve o que está errado. É o caminho de quem sabe
  // assistir sem saber editar — e mesmo quem sabe editar registra mais rápido
  // assim do que consertando na hora.
  const [pendingNoteStart, setPendingNoteStart] = useState<number | null>(null);
  const [noteDraft, setNoteDraft] = useState<{ start: number; end: number; text: string } | null>(null);
  const notes = project?.notes || [];

  const toggleNoteMark = useCallback(() => {
    const current = projectRef.current;
    if (!current) return;
    const at = playheadRef.current ?? 0;
    if (pendingNoteStart === null) {
      setPendingNoteStart(at);
      return;
    }
    const start = Math.min(pendingNoteStart, at);
    const end = Math.max(pendingNoteStart, at);
    setPendingNoteStart(null);
    setNoteDraft({ start, end: Math.max(end, start + 0.05), text: '' });
  }, [pendingNoteStart]);

  const commitNoteDraft = useCallback(() => {
    if (!noteDraft) return;
    const text = noteDraft.text.trim();
    if (!text) { setNoteDraft(null); return; }
    commit(draft => {
      draft.notes = [
        ...(draft.notes || []),
        {
          id: uid('note'),
          start: +noteDraft.start.toFixed(3),
          end: +noteDraft.end.toFixed(3),
          text,
          status: 'open' as const,
          createdAt: new Date().toISOString(),
          origin: 'editor' as const,
        },
      ].sort((a, b) => a.start - b.start);
    });
    setNoteDraft(null);
  }, [commit, noteDraft]);

  // Ajusta UMA palavra dentro da frase, sem tocar nas outras. É o contrário do
  // seletor de fonte do overlay, que aplica a todas de propósito.
  const updateTextRun = useCallback((overlayId: string, runIndex: number, patch: Partial<VideoEditorTextRun>) => {
    commit(draft => {
      draft.overlays = draft.overlays.map(overlay => {
        if (overlay.id !== overlayId || overlay.type === 'image') return overlay;
        const runs = overlay.textRuns || [];
        if (!runs[runIndex]) return overlay;
        return {
          ...overlay,
          textRuns: runs.map((run, index) => (index === runIndex ? { ...run, ...patch } : run)),
        };
      });
    });
  }, [commit]);

  // ── Trilha de fundo (catalogo do Instagram) ────────────────────────────────
  // A faixa NAO e mixada no arquivo renderizado: musica licenciada do Instagram
  // e anexada na publicacao (audioConfiguration). Aqui ela existe para ser
  // ouvida, trocada e removida — e para viajar junto com o projeto ate o disparo.
  const music = project?.music || null;
  // Arquivo proprio primeiro; o link da Meta so como alternativa. `audioUrl` nao
  // expira, entao quando ele existe o `previewIsExternal` deixa de importar.
  const musicAudioSrc = music?.audioUrl
    || (music?.previewUrl && !/(^|\/\/)([\w-]+\.)*instagram\.com\//i.test(music.previewUrl) ? music.previewUrl : null)
    || null;

  const patchMusic = useCallback((patch: Partial<VideoEditorMusic>) => {
    commit(draft => {
      if (!draft.music) return;
      draft.music = { ...draft.music, ...patch };
    });
  }, [commit]);

  const chooseMusicTrack = useCallback((track: VideoEditorMusicTrack) => {
    commit(draft => {
      draft.music = {
        source: 'instagram',
        enabled: true,
        audioId: track.audioId,
        title: track.title ?? null,
        artist: track.artist ?? null,
        duration: track.duration ?? null,
        previewUrl: track.previewUrl ?? null,
        previewFetchedAt: new Date().toISOString(),
        // Mantem o volume que ja estava valendo: trocar de faixa nao e motivo
        // para reverter uma mixagem que o usuario ajustou.
        volume: draft.music?.volume ?? 0.1,
        reason: null,
        accountId: draft.music?.accountId ?? null,
        pickedBy: 'user',
        pickedAt: new Date().toISOString(),
      };
    });
    setMusicPickerOpen(false);
    setMusicPlaybackError(null);
  }, [commit]);

  const removeMusic = useCallback(() => {
    commit(draft => { draft.music = null; });
  }, [commit]);

  // Trilha acompanhando o play do editor — so possivel quando a API devolveu
  // ARQUIVO de audio. Com link de pagina (o caso comum hoje, download_url null
  // na Meta) nao ha o que tocar, e o efeito nao faz nada. Trilha de fundo comeca
  // em 0 e repete: o `loop` cobre faixa mais curta que o video.
  // Volume: efeito proprio, so quando o valor muda.
  useEffect(() => {
    const audio = musicAudioRef.current;
    if (audio) audio.volume = clamp(music?.volume ?? 0.1, 0, 1);
  }, [music?.volume, musicAudioSrc]);

  // Play/pause. Ao dar play, sincroniza UMA vez pelo tamanho REAL do arquivo.
  useEffect(() => {
    const audio = musicAudioRef.current;
    if (!audio) return;
    if (!music?.enabled || !musicAudioSrc || !playing) {
      audio.pause();
      return;
    }
    const dur = audio.duration;
    const at = playheadRef.current ?? 0;
    audio.currentTime = dur && Number.isFinite(dur) && dur > 0 ? at % dur : at;
    // Falha aqui NAO pode ser engolida: um catch vazio transformava o problema
    // em "a musica simplesmente nao toca" — sem erro, sem log, sem pista.
    void audio.play().catch(err => {
      console.warn('[Editor] Trilha nao tocou:', err?.message || err);
      setMusicPlaybackError('A prévia da música não carregou. Reabra a edição; se persistir, escolha a faixa de novo.');
    });
  }, [music?.enabled, musicAudioSrc, playing]);

  // Correcao de deriva, no MAXIMO uma vez a cada 2s.
  //
  // A versao anterior corrigia a cada quadro e travava o editor inteiro. Dois
  // motivos: `playhead` muda ~30x por segundo, e o alvo era calculado com
  // `music.duration` — a duracao da MUSICA INTEIRA vinda da API (200s), nao a do
  // arquivo de previa (bem mais curto). Os dois nunca batiam, entao todo quadro
  // disparava um seek, e cada seek forca o navegador a rebufferizar o audio.
  const musicDriftRef = useRef(0);
  useEffect(() => {
    const audio = musicAudioRef.current;
    if (!audio || !playing || !music?.enabled || !musicAudioSrc) return;
    const now = Date.now();
    if (now - musicDriftRef.current < 2000) return;
    const dur = audio.duration;
    if (!dur || !Number.isFinite(dur) || dur <= 0) return;
    const target = playhead % dur;
    if (Math.abs(audio.currentTime - target) > 0.7) {
      musicDriftRef.current = now;
      audio.currentTime = target;
    }
  }, [playhead, playing, music?.enabled, musicAudioSrc]);

  useEffect(() => {
    if (!musicPickerOpen) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      setMusicLoading(true);
      setMusicCatalogError(null);
      api.listVideoEditorMusic({ q: musicQuery.trim() || undefined })
        .then(response => {
          if (cancelled) return;
          setMusicCatalog(response.tracks || []);
          setMusicCatalogError(response.unavailable || null);
        })
        .catch(error => {
          if (cancelled) return;
          setMusicCatalog([]);
          setMusicCatalogError(error?.message || 'Nao foi possivel carregar o catalogo.');
        })
        .finally(() => { if (!cancelled) setMusicLoading(false); });
    }, musicQuery.trim() ? 450 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [musicPickerOpen, musicQuery]);

  const removeNote = useCallback((noteId: string) => {
    commit(draft => { draft.notes = (draft.notes || []).filter(note => note.id !== noteId); });
  }, [commit]);

  const toggleNoteStatus = useCallback((noteId: string) => {
    commit(draft => {
      draft.notes = (draft.notes || []).map(note => note.id === noteId
        ? { ...note, status: note.status === 'done' ? 'open' as const : 'done' as const }
        : note);
    });
  }, [commit]);

  const requiredPreviewAssets = useMemo(() => {
    if (!project) return [];
    const ids = new Set<string>();
    if (activeAsset) ids.add(activeAsset.id);
    project.assets.forEach(asset => { if (asset.kind === 'image') ids.add(asset.id); });
    project.overlays.forEach(overlay => {
      if (overlay.type === 'image' && playhead >= overlay.start && playhead <= overlay.end) ids.add(overlay.assetId);
    });
    return project.assets.filter(asset => ids.has(asset.id));
  }, [activeAsset, playhead, project]);

  useEffect(() => {
    requiredPreviewAssets.forEach(asset => { void ensureAssetUrl(asset); });
  }, [ensureAssetUrl, requiredPreviewAssets]);

  useEffect(() => {
    playingRef.current = playing;
    const video = videoRef.current;
    if (activeAsset?.kind === 'image') return;
    if (!video || !activeClip) return;
    video.playbackRate = activeClip.playbackRate;
    video.volume = clamp(activeClip.volume, 0, 1);
    video.muted = activeClip.muted;
    const sourceTime = activeClip.sourceStart + Math.max(0, playhead - activeClip.timelineStart) * activeClip.playbackRate;
    const changedClip = activeClipIdRef.current !== activeClip.id;
    activeClipIdRef.current = activeClip.id;
    if ((changedClip || !playing) && Math.abs(video.currentTime - sourceTime) > 0.12) video.currentTime = sourceTime;
    if (playing) void video.play().catch(() => setPlaying(false));
    else video.pause();
  }, [activeAsset?.kind, activeClip, assetUrls, playhead, playing]);

  useEffect(() => {
    if (imagePlaybackRafRef.current != null) cancelAnimationFrame(imagePlaybackRafRef.current);
    imagePlaybackRafRef.current = null;
    imagePlaybackStartedRef.current = null;
    if (!playing || activeAsset?.kind !== 'image' || !activeClip || !project) return;
    imagePlaybackStartedRef.current = { at: performance.now(), playhead };
    const tick = (now: number) => {
      const origin = imagePlaybackStartedRef.current;
      if (!origin) return;
      const nextTime = origin.playhead + (now - origin.at) / 1000;
      const clipEnd = activeClip.timelineStart + activeClip.duration;
      if (nextTime >= clipEnd - 0.001) {
        const index = project.clips.findIndex(clip => clip.id === activeClip.id);
        const next = project.clips[index + 1];
        if (next) setPlayhead(next.timelineStart + 0.001);
        else {
          setPlaying(false);
          setPlayhead(0);
        }
        return;
      }
      setPlayhead(nextTime);
      imagePlaybackRafRef.current = requestAnimationFrame(tick);
    };
    imagePlaybackRafRef.current = requestAnimationFrame(tick);
    return () => {
      if (imagePlaybackRafRef.current != null) cancelAnimationFrame(imagePlaybackRafRef.current);
      imagePlaybackRafRef.current = null;
      imagePlaybackStartedRef.current = null;
    };
  }, [activeAsset?.kind, activeClip, playing, project]);

  const splitClip = useCallback(() => {
    if (!project) return;
    const clip = selectedClip || activeClip;
    if (!clip) return;
    const local = playhead - clip.timelineStart;
    if (local < 0.12 || local > clip.duration - 0.12) {
      toast.error('Posicione o cursor dentro do clipe para dividir.');
      return;
    }
    const splitSource = clip.sourceStart + local * clip.playbackRate;
    commit(draft => {
      const index = draft.clips.findIndex(item => item.id === clip.id);
      if (index < 0) return;
      const left = { ...draft.clips[index], sourceEnd: splitSource };
      const right = {
        ...draft.clips[index],
        id: uid('clip'),
        name: `${draft.clips[index].name} · parte 2`,
        sourceStart: splitSource,
      };
      draft.clips.splice(index, 1, left, right);
    });
  }, [activeClip, commit, playhead, project, selectedClip]);

  // Excluir/duplicar agem sobre TODA a seleção (antes só o último item clicado).
  const removeSelection = useCallback(() => {
    if (!selectedItems.length) return;
    const clipIds = new Set(selectedClipIds);
    const overlayIds = new Set(selectedOverlayIds);
    commit(draft => {
      if (overlayIds.size) draft.overlays = draft.overlays.filter(overlay => !overlayIds.has(overlay.id));
      if (clipIds.size) {
        // A timeline não pode ficar sem nenhum clipe de vídeo.
        const remaining = draft.clips.filter(clip => !clipIds.has(clip.id));
        if (remaining.length) {
          draft.clips = remaining;
        } else {
          draft.clips = draft.clips.slice(0, 1);
          toast.error('O projeto precisa de pelo menos um clipe de vídeo — o primeiro foi mantido.');
        }
      }
    });
    setSelectedItems([]);
  }, [commit, selectedClipIds, selectedItems.length, selectedOverlayIds]);

  const duplicateClip = useCallback(() => {
    if (!project || !selectedClipIds.length) return;
    const ids = new Set(selectedClipIds);
    const created: SelectionItem[] = [];
    commit(draft => {
      // De trás para frente: inserir uma cópia não desloca os índices ainda por processar.
      for (let index = draft.clips.length - 1; index >= 0; index -= 1) {
        const clip = draft.clips[index];
        if (!ids.has(clip.id)) continue;
        const newId = uid('clip');
        created.push({ kind: 'clip', id: newId });
        draft.clips.splice(index + 1, 0, { ...structuredClone(clip), id: newId, name: `${clip.name} · cópia` });
      }
    });
    setSelectedItems(created.reverse());
  }, [commit, project, selectedClipIds]);

  const addAssetAsClip = useCallback((asset: VideoEditorAsset) => {
    const newId = uid(asset.kind === 'image' ? 'image' : 'clip');
    commit(draft => {
      if (asset.kind === 'image') {
        draft.overlays.push({
          id: newId,
          type: 'image',
          assetId: asset.id,
          name: asset.name,
          start: clamp(playhead, 0, Math.max(0, draft.duration - 0.1)),
          end: clamp(playhead + 4, 0.1, draft.duration),
          x: 0.5,
          y: 0.5,
          width: 0.55,
          opacity: 1,
          rotation: 0,
          animation: 'fade',
          exitAnimation: 'fade',
          soundEffectId: null,
          soundEffectVolume: 0.7,
          locked: false,
        });
        return;
      }
      draft.clips.push({
        id: newId,
        assetId: asset.id,
        name: asset.name,
        trackId: 'video-main',
        sourceStart: 0,
        sourceEnd: Math.max(0.1, asset.duration || 5),
        timelineStart: draft.duration,
        duration: Math.max(0.1, asset.duration || 5),
        playbackRate: 1,
        volume: 1,
        muted: false,
        audioEnhanced: asset.id.startsWith('source-'),
        animation: 'none',
        transform: { x: 0, y: 0, scale: 1, rotation: 0, fit: asset.id.startsWith('source-') ? 'contain' : 'cover' },
        transition: 'none',
        narrativeRole: 'body',
        aiReason: '',
      });
    });
    setSelection({ kind: asset.kind === 'image' ? 'overlay' : 'clip', id: newId });
  }, [commit, playhead]);

  const addAssetAtPosition = useCallback((asset: VideoEditorAsset, position?: DropPosition) => {
    if (asset.kind !== 'image') {
      addAssetAsClip(asset);
      return;
    }
    const newId = uid('image');
    commit(draft => {
      draft.overlays.push({
        id: newId,
        type: 'image',
        assetId: asset.id,
        name: asset.name,
        start: clamp(playhead, 0, Math.max(0, draft.duration - 0.1)),
        end: clamp(playhead + 4, 0.1, draft.duration),
        x: position?.x ?? 0.5,
        y: position?.y ?? 0.5,
        width: 0.55,
        opacity: 1,
        rotation: 0,
        animation: 'fade',
        exitAnimation: 'fade',
        soundEffectId: null,
        soundEffectVolume: 0.7,
        locked: false,
      });
    });
    setSelection({ kind: 'overlay', id: newId });
  }, [addAssetAsClip, commit, playhead]);

  const uploadAssets = useCallback(async (files: File[], position?: DropPosition) => {
    const accepted = files.filter(file => (
      file.type.startsWith('video/')
      || file.type.startsWith('image/')
      || EDITOR_MEDIA_FILE_PATTERN.test(file.name)
    ));
    if (!accepted.length) {
      toast.error('Envie arquivos de vídeo ou imagem.');
      return;
    }
    if (accepted.length > 10) {
      toast.error('Envie no máximo 10 mídias por vez.');
      return;
    }
    setUploadingAssets(true);
    try {
      const response = await api.uploadVideoEditorAssets(jobId, accepted);
      const newAssets = response.assets || [];
      if (!newAssets.length) throw new Error('O servidor não retornou as mídias enviadas.');
      let lastSelection: Selection = null;
      commit(draft => {
        const known = new Set(draft.assets.map(asset => asset.id));
        draft.assets.push(...newAssets.filter(asset => !known.has(asset.id)));
        newAssets.forEach((asset, index) => {
          if (asset.kind === 'image') {
            const id = uid('image');
            draft.overlays.push({
              id,
              type: 'image',
              assetId: asset.id,
              name: asset.name,
              start: clamp(playhead, 0, Math.max(0, draft.duration - 0.1)),
              end: clamp(playhead + 4, 0.1, draft.duration),
              x: clamp((position?.x ?? 0.5) + index * 0.025, 0, 1),
              y: clamp((position?.y ?? 0.5) + index * 0.025, 0, 1),
              width: 0.55,
              opacity: 1,
              rotation: 0,
              animation: 'fade',
              exitAnimation: 'fade',
              soundEffectId: null,
              soundEffectVolume: 0.7,
              locked: false,
            });
            lastSelection = { kind: 'overlay', id };
          } else {
            const id = uid('clip');
            const duration = Math.max(0.1, asset.duration || 5);
            draft.clips.push({
              id,
              assetId: asset.id,
              name: asset.name,
              trackId: 'video-main',
              sourceStart: 0,
              sourceEnd: duration,
              timelineStart: draft.duration,
              duration,
              playbackRate: 1,
              volume: 1,
              muted: false,
              audioEnhanced: false,
              animation: 'fade',
              transform: { x: 0, y: 0, scale: 1, rotation: 0, fit: 'cover' },
              transition: 'none',
              narrativeRole: 'body',
              aiReason: '',
            });
            lastSelection = { kind: 'clip', id };
          }
        });
      });
      if (lastSelection) setSelection(lastSelection);
      toast.success(`${newAssets.length} mídia${newAssets.length === 1 ? '' : 's'} adicionada${newAssets.length === 1 ? '' : 's'}.`);
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao enviar mídias.');
    } finally {
      setUploadingAssets(false);
      if (assetInputRef.current) assetInputRef.current.value = '';
    }
  }, [commit, jobId, playhead]);

  const handleExternalDragOver = useCallback((event: ReactDragEvent<HTMLElement>) => {
    if (!event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setExternalDragActive(true);
  }, []);

  const handleExternalDrop = useCallback((event: ReactDragEvent<HTMLElement>) => {
    if (!event.dataTransfer.files.length) return;
    event.preventDefault();
    event.stopPropagation();
    setExternalDragActive(false);
    void uploadAssets(Array.from(event.dataTransfer.files));
  }, [uploadAssets]);

  const handleStageDrop = useCallback((event: ReactDragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setExternalDragActive(false);
    const bounds = event.currentTarget.getBoundingClientRect();
    const position = {
      x: clamp((event.clientX - bounds.left) / bounds.width, 0, 1),
      y: clamp((event.clientY - bounds.top) / bounds.height, 0, 1),
    };
    if (event.dataTransfer.files.length) {
      void uploadAssets(Array.from(event.dataTransfer.files), position);
      return;
    }
    const assetId = event.dataTransfer.getData('application/x-astra-video-asset') || draggedAssetId;
    const asset = projectRef.current?.assets.find(item => item.id === assetId);
    if (asset) addAssetAtPosition(asset, position);
    setDraggedAssetId(null);
  }, [addAssetAtPosition, draggedAssetId, uploadAssets]);

  // Baixa o ARQUIVO ORIGINAL da mídia (não o proxy de prévia). A URL assinada já vem com
  // Content-Disposition: attachment — o arquivo está no MinIO, outro domínio, onde o
  // atributo `download` do <a> sozinho seria ignorado.
  const downloadAsset = useCallback(async (asset: VideoEditorAsset) => {
    setDownloadingAssetId(asset.id);
    try {
      const response = await api.getVideoEditorAssetUrl(jobId, asset.id, 'download');
      const link = document.createElement('a');
      link.href = response.asset.url;
      link.download = response.asset.name || asset.name;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      toast.error(error?.message || 'Não foi possível baixar a mídia.');
    } finally {
      setDownloadingAssetId(null);
    }
  }, [jobId]);

  const deleteUserAsset = useCallback(async (asset: VideoEditorAsset) => {
    const current = projectRef.current;
    if (!current || asset.source !== 'user') return;
    const clipUses = current.clips.filter(clip => clip.assetId === asset.id).length;
    const overlayUses = current.overlays.filter(overlay => overlay.type === 'image' && overlay.assetId === asset.id).length;
    if (clipUses || overlayUses) {
      const uses = [
        clipUses ? `${clipUses} clipe${clipUses === 1 ? '' : 's'}` : '',
        overlayUses ? `${overlayUses} camada${overlayUses === 1 ? '' : 's'} de imagem` : '',
      ].filter(Boolean).join(' e ');
      toast.error(`Esta mídia ainda é usada em ${uses}. Remova esses usos da timeline antes de excluir o arquivo.`);
      return;
    }
    if (!window.confirm(`Excluir permanentemente a mídia "${asset.name}" deste projeto?`)) return;
    setDeletingAssetId(asset.id);
    try {
      if (saveState !== 'saved') {
        const saved = await saveNow(true);
        if (!saved) throw new Error('Salve as remoções da timeline antes de excluir esta mídia.');
      }
      await api.deleteVideoEditorAsset(jobId, asset.id);
      const withoutAsset = (value: VideoEditorProject): VideoEditorProject => ({
        ...value,
        assets: value.assets.filter(item => item.id !== asset.id),
        clips: value.clips.filter(clip => clip.assetId !== asset.id),
        overlays: value.overlays.filter(overlay => overlay.type !== 'image' || overlay.assetId !== asset.id),
      });
      setHistory(currentHistory => currentHistory ? {
        past: currentHistory.past.map(withoutAsset),
        present: withoutAsset(currentHistory.present),
        future: currentHistory.future.map(withoutAsset),
      } : currentHistory);
      const loadedUrl = assetUrls[asset.id];
      if (loadedUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(loadedUrl);
        objectUrlsRef.current = objectUrlsRef.current.filter(url => url !== loadedUrl);
      }
      setAssetUrls(currentUrls => {
        const next = { ...currentUrls };
        delete next[asset.id];
        return next;
      });
      delete assetExpiryRef.current[asset.id];
      delete assetRetryRef.current[asset.id];
      assetLoadingRef.current.delete(asset.id);
      toast.success('Mídia excluída.');
    } catch (error: any) {
      toast.error(error?.message || 'Não foi possível excluir a mídia.');
    } finally {
      setDeletingAssetId(null);
    }
  }, [assetUrls, jobId, saveNow, saveState]);

  const addText = useCallback(() => {
    if (!project) return;
    const id = uid('overlay');
    commit(draft => {
      draft.overlays.push({
        id,
        type: 'text',
        designVersion: 2,
        name: 'Novo texto',
        text: 'Digite seu texto',
        start: clamp(playhead, 0, Math.max(0, draft.duration - 0.1)),
        end: clamp(playhead + 4, 0.1, draft.duration),
        x: 0.5,
        y: 0.32,
        width: 0.8,
        fontFamily: 'Montserrat',
        fontSize: 78,
        color: '#FFFFFF',
        textCase: 'preserve',
        letterSpacing: 0,
        outlineColor: '#000000',
        outlineOpacity: 0.9,
        outlineWidth: 4,
        shadowColor: '#000000',
        shadowOpacity: 0.45,
        shadowOffsetX: 0,
        shadowOffsetY: 6,
        backgroundColor: '#000000',
        backgroundOpacity: 0.35,
        backgroundPadding: 8,
        bold: true,
        italic: false,
        align: 'center',
        opacity: 1,
        rotation: 0,
        animation: 'none',
        exitAnimation: 'none',
        soundEffectId: null,
        soundEffectVolume: 0.7,
        stylePreset: 'custom',
        textRuns: [],
        locked: false,
      });
    });
    setSelection({ kind: 'overlay', id });
  }, [commit, playhead, project]);

  const updateClip = useCallback((id: string, patch: Partial<VideoEditorClip>) => {
    commit(draft => {
      draft.clips = draft.clips.map(clip => clip.id === id ? { ...clip, ...patch } : clip);
    });
  }, [commit]);

  // Aplica o MESMO patch a várias camadas de texto de uma vez, num único commit
  // (logo, um único passo de undo). É por aqui que o inspetor em lote mexe nas
  // ~40 legendas: N commits separados encheriam o histórico de N passos e cada
  // Ctrl+Z desfaria uma legenda só.
  const updateOverlays = useCallback((ids: string[], patch: Partial<VideoEditorTextOverlay>) => {
    const alvo = new Set(ids);
    if (!alvo.size) return;
    commit(draft => {
      draft.overlays = draft.overlays.map(overlay => {
        if (!alvo.has(overlay.id) || overlay.type !== 'text') return overlay;
        // Campos de aparência que TAMBÉM existem por trecho. Todo overlay que a IA
        // desenha vem com `textRuns`, e no .ass os tags do trecho são escritos
        // DEPOIS dos tags do bloco — logo o trecho ganha. Sem propagar aqui, o
        // campo do inspetor mexia só no bloco e não mudava nada, nem no MP4 nem na
        // tela (a prévia lê `run.outlineWidth`/`run.shadowOffsetY` no mesmo lugar).
        //
        // MEDIDO no .ass exportado de uma legenda `serifada` com o slider em 25:
        // `{...\bord25.0\...\yshad4.0}{...\bord3.0\yshad4.0}isso` — todo trecho
        // visível saía com 3. E na prévia: contorno computado no bloco 15,10px e no
        // trecho 1,81px, com a tinta do palco IDÊNTICA (4.818 px) em 3 e em 25.
        // O `\bord` e o `\yshad` do trecho estão em buildEditorAss (backend), e o
        // saneamento já preserva os dois campos do trecho — faltava só a propagação.
        const runStylePatch = {
          ...(patch.fontFamily !== undefined ? { fontFamily: patch.fontFamily } : {}),
          ...(patch.fontSize !== undefined ? { fontSize: patch.fontSize } : {}),
          ...(patch.color !== undefined ? { color: patch.color } : {}),
          ...(patch.outlineColor !== undefined ? { outlineColor: patch.outlineColor } : {}),
          ...(patch.outlineWidth !== undefined ? { outlineWidth: patch.outlineWidth } : {}),
          ...(patch.shadowOffsetY !== undefined ? { shadowOffsetY: patch.shadowOffsetY } : {}),
          ...(patch.bold !== undefined ? { bold: patch.bold } : {}),
          ...(patch.italic !== undefined ? { italic: patch.italic } : {}),
          ...(patch.textCase !== undefined ? { textCase: patch.textCase } : {}),
        };
        // ── Texto de GANCHO nao se remapeia: se REDESENHA ────────────────────
        //
        // Nos overlays de legenda os `textRuns` sao PALAVRAS, e remapear palavra a
        // palavra e o certo. No gancho eles sao LINHAS (o texto inteiro da linha),
        // porque a quebra em duas linhas e escolhida por largura MEDIDA e o corpo e
        // ajustado ao texto. Remapear ali virava 2 runs de linha em N runs de
        // palavra, cada um com posicao propria: a frase saia desalinhada e a quebra
        // balanceada morria. Entao trocar uma palavra do gancho manda `restyle` e o
        // servidor recalcula quebra, corpo e posicoes com o texto novo (ele passou a
        // levar o `text` editado para o desenho, junto com o estilo e a cor).
        const ganchoRedesenha = patch.text !== undefined && Boolean(overlay.headlineStyle);
        let textRuns = patch.text !== undefined && !ganchoRedesenha
          ? remapRunsToText(overlay.textRuns || [], patch.text, patch.end ?? overlay.end)
          : (overlay.textRuns || []).map(run => ({ ...run, ...runStylePatch }));
        if (patch.start !== undefined && patch.text === undefined) {
          const delta = patch.start - overlay.start;
          textRuns = textRuns.map(run => ({
            ...run,
            start: clamp(run.start + delta, patch.start!, patch.end ?? overlay.end),
            end: clamp(run.end + delta, patch.start! + 0.01, patch.end ?? overlay.end),
          }));
        }
        if (patch.end !== undefined && patch.text === undefined) {
          textRuns = textRuns.map(run => ({
            ...run,
            start: clamp(run.start, patch.start ?? overlay.start, patch.end!),
            end: clamp(run.end, run.start + 0.01, patch.end!),
          }));
        }
        // `restyle` e COMANDO, nao estado: o saneamento consome e nao devolve.
        return { ...overlay, ...patch, textRuns, ...(ganchoRedesenha ? { restyle: true } : {}) };
      });
    });
  }, [commit]);

  // Mesma assinatura de sempre: tudo que já editava uma camada continua valendo.
  const updateOverlay = useCallback(
    (id: string, patch: Partial<VideoEditorTextOverlay>) => updateOverlays([id], patch),
    [updateOverlays],
  );

  const updateImageOverlay = useCallback((id: string, patch: Partial<VideoEditorImageOverlay>) => {
    commit(draft => {
      draft.overlays = draft.overlays.map(overlay => (
        overlay.id === id && overlay.type === 'image' ? { ...overlay, ...patch } : overlay
      ));
    });
  }, [commit]);

  const getSoundEffectUrl = useCallback(async (soundEffectId: string) => {
    const cached = soundEffectUrlsRef.current[soundEffectId];
    if (cached) return cached;
    const blob = await api.downloadVideoEditorSoundEffect(soundEffectId);
    const url = URL.createObjectURL(blob);
    soundEffectUrlsRef.current[soundEffectId] = url;
    return url;
  }, []);

  const previewSoundEffect = useCallback(async (soundEffectId: string, volume: number) => {
    if (previewingSoundEffectId === soundEffectId && soundPreviewAudioRef.current && !soundPreviewAudioRef.current.paused) {
      soundPreviewAudioRef.current.pause();
      setPreviewingSoundEffectId(null);
      return;
    }
    try {
      soundPreviewAudioRef.current?.pause();
      const url = await getSoundEffectUrl(soundEffectId);
      const audio = new Audio(url);
      audio.volume = clamp(volume, 0, 1);
      audio.onended = () => setPreviewingSoundEffectId(null);
      audio.onerror = () => setPreviewingSoundEffectId(null);
      soundPreviewAudioRef.current = audio;
      setPreviewingSoundEffectId(soundEffectId);
      await audio.play();
    } catch (error: any) {
      setPreviewingSoundEffectId(null);
      toast.error(error?.message || 'Não foi possível ouvir o efeito.');
    }
  }, [getSoundEffectUrl, previewingSoundEffectId]);

  const playTimelineSoundEffect = useCallback(async (soundEffectId: string, volume: number) => {
    try {
      const url = await getSoundEffectUrl(soundEffectId);
      const audio = new Audio(url);
      audio.volume = clamp(volume, 0, 1);
      await audio.play();
    } catch {
      // A prévia visual continua mesmo se o navegador bloquear ou o SFX falhar.
    }
  }, [getSoundEffectUrl]);

  useEffect(() => {
    if (!project || !playing) {
      wasPlayingSoundRef.current = false;
      previousPlaybackTimeRef.current = playhead;
      return;
    }
    if (!wasPlayingSoundRef.current) {
      wasPlayingSoundRef.current = true;
      triggeredSoundEffectsRef.current.clear();
      previousPlaybackTimeRef.current = playhead - 0.025;
    }
    const previous = previousPlaybackTimeRef.current;
    if (playhead < previous) triggeredSoundEffectsRef.current.clear();
    project.overlays.forEach(overlay => {
      if (!overlay.soundEffectId || triggeredSoundEffectsRef.current.has(overlay.id)) return;
      if (overlay.start > previous && overlay.start <= playhead + 0.025) {
        triggeredSoundEffectsRef.current.add(overlay.id);
        void playTimelineSoundEffect(overlay.soundEffectId, overlay.soundEffectVolume ?? 0.7);
      }
    });
    previousPlaybackTimeRef.current = playhead;
  }, [playTimelineSoundEffect, playhead, playing, project]);

  const startOverlayDrag = useCallback((event: ReactPointerEvent, overlay: VideoEditorOverlay) => {
    if (!project || overlay.locked) return;
    event.preventDefault();
    event.stopPropagation();
    // Arrastando a POSIÇÃO no quadro com várias camadas selecionadas, o delta
    // vale para todas — é o que torna viável subir as ~40 legendas de um Reel de
    // uma vez. Mesmo critério do arraste no tempo (startOverlayMove): o grupo
    // inteiro para quando o PRIMEIRO item bate na borda do quadro, então as
    // distâncias relativas entre as camadas ficam intactas e ninguém sai da tela.
    const arrastandoIds = (selectedOverlayIds.includes(overlay.id) ? selectedOverlayIds : [overlay.id])
      .filter(id => !project.overlays.find(item => item.id === id)?.locked);
    if (!arrastandoIds.length) return;
    const before = cloneProject(project);
    const startX = event.clientX;
    const startY = event.clientY;
    // Snapshot da posição no início do gesto: recalcular a partir do estado atual
    // somaria o delta de novo a cada pointermove e a camada disparava para fora.
    const origens = new Map(project.overlays
      .filter(item => arrastandoIds.includes(item.id))
      .map(item => [item.id, { x: item.x, y: item.y }]));
    const xs = [...origens.values()].map(origem => origem.x);
    const ys = [...origens.values()].map(origem => origem.y);
    const limiteEsquerda = -Math.min(...xs);
    const limiteDireita = 1 - Math.max(...xs);
    const limiteTopo = -Math.min(...ys);
    const limiteBase = 1 - Math.max(...ys);
    const stage = (event.currentTarget as HTMLElement).parentElement;
    const bounds = stage?.getBoundingClientRect();
    if (!bounds) return;
    const move = (moveEvent: PointerEvent) => {
      const deltaX = clamp((moveEvent.clientX - startX) / bounds.width, limiteEsquerda, limiteDireita);
      const deltaY = clamp((moveEvent.clientY - startY) / bounds.height, limiteTopo, limiteBase);
      replaceDuringGesture(draft => {
        draft.overlays = draft.overlays.map(item => {
          const origem = origens.get(item.id);
          if (!origem) return item;
          // Clamp de novo por camada: projeto antigo com x/y fora de 0..1 não
          // arrasta o grupo inteiro para fora junto com ele.
          return { ...item, x: clamp(origem.x + deltaX, 0, 1), y: clamp(origem.y + deltaY, 0, 1) };
        });
      });
    };
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      pushGestureHistory(before);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
  }, [project, pushGestureHistory, replaceDuringGesture, selectedOverlayIds]);

  const startImageResize = useCallback((event: ReactPointerEvent, overlay: VideoEditorImageOverlay, handle: ResizeHandle) => {
    if (!project || overlay.locked) return;
    event.preventDefault();
    event.stopPropagation();
    const bounds = stageRef.current?.getBoundingClientRect();
    if (!bounds) return;
    const asset = project.assets.find(item => item.id === overlay.assetId);
    const mediaAspect = asset?.width && asset?.height ? asset.height / asset.width : 1;
    const normalizedHeightPerWidth = (bounds.width / bounds.height) * mediaAspect;
    const rotationRadians = overlay.rotation * Math.PI / 180;
    const cosRotation = Math.cos(rotationRadians);
    const sinRotation = Math.sin(rotationRadians);
    const cos = Math.abs(cosRotation);
    const sin = Math.abs(sinRotation);
    const horizontalExtentPerWidth = Math.max(0.01, cos + sin * mediaAspect);
    const verticalExtentPerWidth = Math.max(0.01, (bounds.width / bounds.height) * (sin + cos * mediaAspect));
    const maxByHorizontalBounds = Math.max(0.05, (2 * Math.min(overlay.x, 1 - overlay.x)) / horizontalExtentPerWidth);
    const maxByVerticalBounds = Math.max(0.05, (2 * Math.min(overlay.y, 1 - overlay.y)) / verticalExtentPerWidth);
    const maxWidth = clamp(Math.min(1, maxByHorizontalBounds, maxByVerticalBounds), 0.05, 1);
    const startX = event.clientX;
    const startY = event.clientY;
    const initialWidth = overlay.width;
    const before = cloneProject(project);
    const move = (moveEvent: PointerEvent) => {
      const screenDeltaX = moveEvent.clientX - startX;
      const screenDeltaY = moveEvent.clientY - startY;
      // As alças giram junto com a imagem. Projetar o ponteiro de volta aos
      // eixos locais mantém todas as oito alças operantes em qualquer rotação.
      const localDeltaX = screenDeltaX * cosRotation + screenDeltaY * sinRotation;
      const localDeltaY = -screenDeltaX * sinRotation + screenDeltaY * cosRotation;
      const horizontal = handle.includes('e')
        ? (localDeltaX / bounds.width) * 2
        : handle.includes('w')
          ? (-localDeltaX / bounds.width) * 2
          : 0;
      const vertical = handle.includes('s')
        ? ((localDeltaY / bounds.height) * 2) / Math.max(0.01, normalizedHeightPerWidth)
        : handle.includes('n')
          ? ((-localDeltaY / bounds.height) * 2) / Math.max(0.01, normalizedHeightPerWidth)
          : 0;
      const delta = Math.abs(horizontal) >= Math.abs(vertical) ? horizontal : vertical;
      const width = clamp(initialWidth + delta, 0.05, maxWidth);
      const xExtent = Math.min(0.5, width * horizontalExtentPerWidth / 2);
      const yExtent = Math.min(0.5, width * verticalExtentPerWidth / 2);
      replaceDuringGesture(draft => {
        draft.overlays = draft.overlays.map(item => (
          item.id === overlay.id && item.type === 'image'
            ? { ...item, width, x: clamp(item.x, xExtent, 1 - xExtent), y: clamp(item.y, yExtent, 1 - yExtent) }
            : item
        ));
      });
    };
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      pushGestureHistory(before);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  }, [project, pushGestureHistory, replaceDuringGesture]);

  const startVideoDrag = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (!project || !activeClip) return;
    event.preventDefault();
    event.stopPropagation();
    setSelection({ kind: 'clip', id: activeClip.id });
    const before = cloneProject(project);
    const startX = event.clientX;
    const startY = event.clientY;
    const initial = activeClip.transform;
    const bounds = event.currentTarget.parentElement?.getBoundingClientRect();
    if (!bounds) return;
    const move = (moveEvent: PointerEvent) => {
      const x = clamp(initial.x + ((moveEvent.clientX - startX) / bounds.width) * 2, -1, 1);
      const y = clamp(initial.y + ((moveEvent.clientY - startY) / bounds.height) * 2, -1, 1);
      replaceDuringGesture(draft => {
        draft.clips = draft.clips.map(clip => clip.id === activeClip.id
          ? { ...clip, transform: { ...clip.transform, x, y } }
          : clip);
      });
    };
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      pushGestureHistory(before);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
  }, [activeClip, project, pushGestureHistory, replaceDuringGesture]);

  const startTrim = useCallback((event: ReactPointerEvent, clip: VideoEditorClip, side: 'start' | 'end') => {
    if (!project) return;
    event.preventDefault();
    event.stopPropagation();
    const before = cloneProject(project);
    const startX = event.clientX;
    const originalStart = clip.sourceStart;
    const originalEnd = clip.sourceEnd;
    const assetDuration = project.assets.find(asset => asset.id === clip.assetId)?.duration || 3600;
    const move = (moveEvent: PointerEvent) => {
      const sourceDelta = ((moveEvent.clientX - startX) / zoom) * clip.playbackRate;
      replaceDuringGesture(draft => {
        draft.clips = draft.clips.map(item => {
          if (item.id !== clip.id) return item;
          return side === 'start'
            ? { ...item, sourceStart: clamp(originalStart + sourceDelta, 0, originalEnd - 0.08) }
            : { ...item, sourceEnd: clamp(originalEnd + sourceDelta, originalStart + 0.08, assetDuration) };
        });
      });
    };
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      pushGestureHistory(before);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
  }, [project, pushGestureHistory, replaceDuringGesture, zoom]);

  const startOverlayTrim = useCallback((event: ReactPointerEvent, overlay: VideoEditorOverlay, side: 'start' | 'end') => {
    if (!project || overlay.locked) return;
    event.preventDefault();
    event.stopPropagation();
    const before = cloneProject(project);
    const startX = event.clientX;
    const originalStart = overlay.start;
    const originalEnd = overlay.end;
    const move = (moveEvent: PointerEvent) => {
      const delta = (moveEvent.clientX - startX) / zoom;
      const start = side === 'start'
        ? clamp(originalStart + delta, 0, originalEnd - 0.05)
        : originalStart;
      const end = side === 'end'
        ? clamp(originalEnd + delta, originalStart + 0.05, project.duration)
        : originalEnd;
      replaceDuringGesture(draft => {
        draft.overlays = draft.overlays.map(item => {
          if (item.id !== overlay.id) return item;
          if (item.type === 'image') return { ...item, start, end };
          return {
            ...item,
            start,
            end,
            textRuns: (item.textRuns || []).map(run => ({
              ...run,
              start: clamp(run.start, start, Math.max(start, end - 0.01)),
              end: clamp(run.end, Math.min(end, start + 0.01), end),
            })).filter(run => run.end > run.start),
          };
        });
      });
    };
    const endGesture = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', endGesture);
      window.removeEventListener('pointercancel', endGesture);
      pushGestureHistory(before);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', endGesture);
    window.addEventListener('pointercancel', endGesture);
  }, [project, pushGestureHistory, replaceDuringGesture, zoom]);

  const seekTimelinePointer = useCallback((clientX: number) => {
    const bounds = timelineContentRef.current?.getBoundingClientRect();
    const currentProject = projectRef.current;
    if (!bounds || !currentProject) return;
    setPlayhead(clamp((clientX - bounds.left) / zoom, 0, currentProject.duration));
  }, [zoom]);

  const startPlayheadDrag = useCallback((event: ReactPointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setPlaying(false);
    seekTimelinePointer(event.clientX);
    const move = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault();
      seekTimelinePointer(moveEvent.clientX);
    };
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  }, [seekTimelinePointer]);

  // Reordena o GRUPO selecionado, não só o clipe arrastado.
  //
  // Clipe não tem posição livre na timeline: normalizeTimeline reempacota todos em
  // sequência a cada commit (é isso que o render do backend concatena). Então
  // "levar para o lado" um conjunto de clipes = mover o conjunto de lugar na
  // ordem, mantendo a ordem relativa entre eles.
  const reorderClip = useCallback((targetId: string) => {
    if (!draggedClipId) return;
    const movingIds = selectedClipIds.includes(draggedClipId) && selectedClipIds.length > 1
      ? selectedClipIds
      : [draggedClipId];
    if (movingIds.includes(targetId)) { setDraggedClipId(null); return; }
    commit(draft => {
      const moving = draft.clips.filter(clip => movingIds.includes(clip.id));
      if (!moving.length) return;
      const rest = draft.clips.filter(clip => !movingIds.includes(clip.id));
      const targetIndex = rest.findIndex(clip => clip.id === targetId);
      if (targetIndex < 0) return;
      // Arrastar para a direita solta DEPOIS do alvo; para a esquerda, antes.
      const draggedOriginalIndex = draft.clips.findIndex(clip => clip.id === draggedClipId);
      const targetOriginalIndex = draft.clips.findIndex(clip => clip.id === targetId);
      const insertAt = targetOriginalIndex > draggedOriginalIndex ? targetIndex + 1 : targetIndex;
      rest.splice(insertAt, 0, ...moving);
      draft.clips = rest;
    });
    setDraggedClipId(null);
  }, [commit, draggedClipId, selectedClipIds]);

  // Arrasta camadas ao longo do tempo (antes só era possível aparar as pontas).
  // Move todas as camadas selecionadas juntas, com o mesmo deslocamento.
  // ── Inserções de tela dividida ────────────────────────────────────────────
  //
  // Faixa própria na régua, e não a faixa de camadas, porque uma inserção NÃO é um
  // overlay: ela reenquadra o vídeo em vez de compor por cima dele, e não tem
  // posição, tamanho, rotação, opacidade nem animação para a pessoa mexer. O que
  // ela tem é MOMENTO e LAYOUT — e é só isso que a faixa oferece: arrastar no
  // tempo, trocar top/bottom, apagar.
  //
  // A seleção é local (não entra no `Selection` de clipe/camada) porque as ações em
  // massa da régua — dividir, duplicar, marquee — não fazem sentido numa janela
  // cuja duração tem de fechar num corte.
  //
  // NÃO há botão de "regerar imagem". Regerar custa créditos por inserção, e a
  // exportação reaproveita a imagem que já está no MinIO justamente para não
  // cobrar de novo por um vídeo que a pessoa só quis reexportar. Quando esse botão
  // existir, ele precisa do modal de confirmação de crédito antes — como o aviso
  // de troca de estilo destrutiva faz hoje no painel de estilo.
  const [selectedInsertId, setSelectedInsertId] = useState<string | null>(null);
  const imageInserts = project?.imageInserts || [];
  const selectedInsert = imageInserts.find(item => item.id === selectedInsertId) || null;

  // ── Prévia da tela dividida no palco ──────────────────────────────────────
  //
  // Os MESMOS números do render (videoSplitInsertService): faixa de 750 de 1920,
  // janela de vídeo de 1170, e o par zoom/foco de cada layout — 1,25/400 no 'top' e
  // 1,0/225 no 'bottom'. Repetidos aqui porque o palco é CSS e o render é ffmpeg;
  // não há como o navegador ler a constante do backend. Se um dos dois mudar, o
  // outro tem de mudar junto, e é por isso que o teste do backend crava os pixels.
  //
  // A conta do reenquadramento é a da referência, e não a do ffmpeg: em CSS o vídeo
  // é AMPLIADO e DESLOCADO dentro de uma caixa com overflow hidden; em ffmpeg o
  // mesmo resultado sai de um `crop`. `transform: scale(z) translateY(-focusY)` com
  // origem no topo faz um ponto y_src cair em (y_src - focusY) * z — que é a
  // definição do focusY.
  const SPLIT_PREVIEW = {
    bandFraction: 750 / 1920,
    videoFraction: 1170 / 1920,
    top: { zoom: 1.25, focusY: 400 },
    bottom: { zoom: 1.0, focusY: 225 },
  } as const;
  const insertNoPlayhead = imageInserts.find(item => playhead >= item.start && playhead < item.end) || null;
  const splitPreview = insertNoPlayhead
    ? {
      insert: insertNoPlayhead,
      ...SPLIT_PREVIEW[insertNoPlayhead.layout === 'bottom' ? 'bottom' : 'top'],
      bandTopPct: insertNoPlayhead.layout === 'bottom' ? SPLIT_PREVIEW.videoFraction * 100 : 0,
      videoTopPct: insertNoPlayhead.layout === 'bottom' ? 0 : SPLIT_PREVIEW.bandFraction * 100,
      // Degradê de costura: 110px na BASE da faixa, e SÓ no 'top'. No 'bottom' a
      // ausência é decisão — ali a legenda fica acima da costura, sobre o vídeo.
      seamGradient: insertNoPlayhead.layout === 'bottom' ? null : { heightPct: (110 / 1920) * 100 },
    }
    : null;

  const startInsertMove = useCallback((event: ReactPointerEvent, insert: VideoEditorImageInsert) => {
    if (!project || insert.locked) return;
    if ((event.target as HTMLElement)?.closest('button')) return;
    event.stopPropagation();
    const before = cloneProject(project);
    const startX = event.clientX;
    const duracao = insert.end - insert.start;
    // As vizinhas são o limite: duas janelas ativas no mesmo quadro desenhariam a
    // janela de vídeo de uma sobre a faixa da outra. O backend também corrige isso
    // no saneamento, mas deixar a tarja passar por cima da vizinha na régua e ela
    // voltar sozinha depois de salvar é pior do que não deixar arrastar.
    const ordenadas = [...(project.imageInserts || [])].sort((x, y) => x.start - y.start);
    const indice = ordenadas.findIndex(item => item.id === insert.id);
    const pisoVizinho = indice > 0 ? ordenadas[indice - 1].end : 0;
    const tetoVizinho = indice >= 0 && indice < ordenadas.length - 1 ? ordenadas[indice + 1].start : project.duration;
    let moved = false;

    const move = (moveEvent: PointerEvent) => {
      const bruto = (moveEvent.clientX - startX) / zoom;
      const start = clamp(insert.start + bruto, pisoVizinho, Math.max(pisoVizinho, tetoVizinho - duracao));
      if (Math.abs(start - insert.start) < 0.001 && !moved) return;
      moved = true;
      replaceDuringGesture(draft => {
        draft.imageInserts = (draft.imageInserts || []).map(item => (
          item.id === insert.id ? { ...item, start, end: start + duracao } : item
        ));
      });
    };
    const endGesture = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', endGesture);
      window.removeEventListener('pointercancel', endGesture);
      if (moved) pushGestureHistory(before);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', endGesture);
    window.addEventListener('pointercancel', endGesture);
  }, [project, zoom, replaceDuringGesture, pushGestureHistory]);

  const toggleInsertLayout = useCallback((id: string) => {
    commit(draft => {
      draft.imageInserts = (draft.imageInserts || []).map(item => (
        item.id === id ? { ...item, layout: item.layout === 'top' ? 'bottom' : 'top' } : item
      ));
    });
  }, [commit]);

  const removeInsert = useCallback((id: string) => {
    commit(draft => {
      draft.imageInserts = (draft.imageInserts || []).filter(item => item.id !== id);
    });
    setSelectedInsertId(current => (current === id ? null : current));
  }, [commit]);

  const startOverlayMove = useCallback((event: ReactPointerEvent, overlay: VideoEditorOverlay) => {
    if (!project) return;
    // As alças de aparar são botões dentro do bloco — não viram arraste do corpo.
    if ((event.target as HTMLElement)?.closest('button')) return;
    const movingIds = (selectedOverlayIds.includes(overlay.id) ? selectedOverlayIds : [overlay.id])
      .filter(id => !project.overlays.find(item => item.id === id)?.locked);
    if (!movingIds.length) return;
    // Sem preventDefault aqui: em parte dos navegadores ele suprime o `click`
    // seguinte, e é o click que trata a seleção. stopPropagation basta para o
    // gesto não virar retângulo de seleção na faixa de trás.
    event.stopPropagation();

    const before = cloneProject(project);
    const startX = event.clientX;
    // Snapshot COMPLETO (inclusive os textRuns) do estado no início do gesto.
    //
    // Cada pointermove recalcula a posição a partir daqui. Ler os runs do estado
    // atual em vez do snapshot somava o delta de novo a cada evento do mouse: as
    // palavras acumulavam o deslocamento, iam parar muito depois do fim do vídeo
    // e o texto não acendia em playhead nenhum — a camada sumia de vez.
    const originals = new Map(project.overlays
      .filter(item => movingIds.includes(item.id))
      .map(item => [item.id, {
        start: item.start,
        end: item.end,
        textRuns: item.type === 'text' ? (item.textRuns || []).map(run => ({ ...run })) : null,
      }]));
    // O grupo inteiro para de andar quando o primeiro item bate na borda.
    const minStart = Math.min(...[...originals.values()].map(o => o.start));
    const maxEnd = Math.max(...[...originals.values()].map(o => o.end));
    let moved = false;
    let appliedDelta = 0;

    const move = (moveEvent: PointerEvent) => {
      const rawDelta = (moveEvent.clientX - startX) / zoom;
      const delta = clamp(rawDelta, -minStart, Math.max(0, project.duration - maxEnd));
      if (Math.abs(delta) < 0.001 && !moved) return;
      moved = true;
      appliedDelta = delta;
      replaceDuringGesture(draft => {
        draft.overlays = draft.overlays.map(item => {
          const original = originals.get(item.id);
          if (!original) return item;
          const start = original.start + delta;
          const end = original.end + delta;
          if (item.type === 'image') return { ...item, start, end };
          return {
            ...item,
            start,
            end,
            // Sempre a partir do snapshot, nunca do valor já deslocado.
            textRuns: (original.textRuns || []).map(run => ({ ...run, start: run.start + delta, end: run.end + delta })),
          };
        });
      });
    };
    const endGesture = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', endGesture);
      window.removeEventListener('pointercancel', endGesture);
      if (!moved) return;
      pushGestureHistory(before);
      // O cursor acompanha o deslocamento.
      //
      // O palco só desenha as camadas que contêm o playhead (visibleOverlays), e
      // cada palavra de um texto só acende depois do seu run.start. Movendo a
      // camada sem mover o cursor, ela saía de baixo dele e SUMIA da edição — que
      // é o que acontecia ao arrastar várias juntas. Seguindo o mesmo delta, o
      // que estava na tela continua na tela, na mesma posição relativa.
      setPlayhead(value => clamp(value + appliedDelta, 0, project.duration));
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', endGesture);
    window.addEventListener('pointercancel', endGesture);
  }, [project, pushGestureHistory, replaceDuringGesture, selectedOverlayIds, zoom]);

  // Arrastar no vazio da faixa = retângulo de seleção. Fica restrito à faixa onde
  // começou (clipe e camada têm semânticas de arraste diferentes; misturar as duas
  // num mesmo gesto só confunde). Ctrl/Cmd soma ao que já estava selecionado.
  const startMarquee = useCallback((event: ReactPointerEvent, track: 'clip' | 'overlay') => {
    if (!project) return;
    if ((event.target as HTMLElement)?.closest('[data-timeline-item="true"]')) return;
    const bounds = timelineContentRef.current?.getBoundingClientRect();
    if (!bounds) return;
    event.preventDefault();
    const additive = event.ctrlKey || event.metaKey;
    const previous = additive ? selectedItems : [];
    const originSeconds = clamp((event.clientX - bounds.left) / zoom, 0, project.duration);
    let dragged = false;

    const apply = (toSeconds: number) => {
      const from = Math.min(originSeconds, toSeconds);
      const to = Math.max(originSeconds, toSeconds);
      setMarquee({ track, fromSeconds: from, toSeconds: to });
      const hits: SelectionItem[] = track === 'clip'
        ? project.clips
          .filter(clip => clip.timelineStart < to && clip.timelineStart + clip.duration > from)
          .map(clip => ({ kind: 'clip' as const, id: clip.id }))
        : project.overlays
          .filter(overlay => overlay.start < to && overlay.end > from)
          .map(overlay => ({ kind: 'overlay' as const, id: overlay.id }));
      const merged = [...previous.filter(item => !hits.some(hit => hit.kind === item.kind && hit.id === item.id)), ...hits];
      setSelectedItems(merged);
    };

    const move = (moveEvent: PointerEvent) => {
      const seconds = clamp((moveEvent.clientX - bounds.left) / zoom, 0, project.duration);
      if (!dragged && Math.abs(seconds - originSeconds) * zoom < 4) return;
      dragged = true;
      apply(seconds);
    };
    const endGesture = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', endGesture);
      window.removeEventListener('pointercancel', endGesture);
      setMarquee(null);
      // Clique seco no vazio (sem arrastar) limpa a seleção.
      if (!dragged && !additive) setSelectedItems([]);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', endGesture);
    window.addEventListener('pointercancel', endGesture);
  }, [project, selectedItems, zoom]);

  const startRenderPolling = useCallback(() => {
    if (renderTimerRef.current) clearInterval(renderTimerRef.current);
    renderTimerRef.current = setInterval(async () => {
      try {
        const response = await api.getVideoEditorJob(jobId);
        const current = response.job?.output?.editorRenders?.[target] as VideoEditorRender | undefined;
        if (!current) return;
        const isStale = current.projectRevision !== projectRef.current?.revision
          || editGenerationRef.current !== renderGenerationRef.current;
        const lastRenderActivity = Date.parse(current.updatedAt || current.createdAt || '');
        const isAbandoned = ['queued', 'running'].includes(current.status)
          && Number.isFinite(lastRenderActivity)
          && Date.now() - lastRenderActivity > RENDER_STALE_MS;
        setRender(isAbandoned || (isStale && (current.status === 'completed' || current.status === 'failed'))
          ? { ...current, status: 'idle', progress: 0, url: undefined }
          : current);
        if (current.status === 'completed' || current.status === 'failed' || isAbandoned) {
          if (renderTimerRef.current) clearInterval(renderTimerRef.current);
          renderTimerRef.current = null;
          if (isAbandoned) toast.error('A exportação foi interrompida. Você já pode iniciá-la novamente.');
          else if (isStale) toast('O projeto mudou durante a exportação. Exporte novamente para gerar a versão atual.');
          else if (current.status === 'completed') toast.success('Exportação concluída e salva na produção — é essa versão que aparece em Edições e vai para as redes.');
          else toast.error(current.error || 'A exportação falhou.');
        }
      } catch {
        // O próximo ciclo tenta novamente; a exportação continua no servidor.
      }
    }, 2500);
  }, [jobId, target]);

  useEffect(() => {
    if (render?.status === 'queued' || render?.status === 'running') startRenderPolling();
  }, [render?.id, render?.status, startRenderPolling]);

  const exportVideo = useCallback(async () => {
    const saved = saveState === 'saved' ? project : await saveNow(true);
    if (!saved) return toast.error('Salve o projeto antes de exportar.');
    const requestedGeneration = editGenerationRef.current;
    try {
      const response = await api.renderVideoEditorProject(jobId, target);
      renderGenerationRef.current = requestedGeneration;
      setRender(response.render);
      toast.success('Exportação iniciada em segundo plano.');
      startRenderPolling();
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao iniciar exportação.');
    }
  }, [jobId, project, saveNow, saveState, startRenderPolling, target]);

  const downloadRender = useCallback(async () => {
    try {
      const blob = await api.downloadVideoEditorArtifact(jobId, `editor:${target}`);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${project?.name || 'video-editado'}.mp4`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao baixar exportação.');
    }
  }, [jobId, project?.name, target]);

  const closeEditor = useCallback(async () => {
    if (saveState === 'dirty') {
      const saved = await saveNow(true);
      if (!saved && !window.confirm('Existem alterações ainda não salvas. Fechar mesmo assim?')) return;
    } else if (saveState === 'saving' && !window.confirm('O projeto ainda está salvando. Fechar mesmo assim?')) {
      return;
    } else if ((saveState === 'conflict' || saveState === 'error')
      && !window.confirm('Existem alterações que não foram salvas. Fechar mesmo assim?')) {
      return;
    }
    onClose();
  }, [onClose, saveNow, saveState]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const tag = (event.target as HTMLElement)?.tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo(); else undo();
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        redo();
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        void saveNow();
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
        // Ctrl+A: todos os clipes; com Shift, todas as camadas. Com uma camada
        // já selecionada, Ctrl+A sozinho também pega as camadas — quem está
        // mexendo em legenda espera "selecionar tudo" dentro do que está editando,
        // e é essa seleção que alimenta o inspetor em lote.
        event.preventDefault();
        const current = projectRef.current;
        if (!current) return;
        const focoEmCamada = event.shiftKey
          || selectedItemsRef.current.some(item => item.kind === 'overlay');
        setSelectedItems(focoEmCamada
          ? current.overlays.map(overlay => ({ kind: 'overlay' as const, id: overlay.id }))
          : current.clips.map(clip => ({ kind: 'clip' as const, id: clip.id })));
      } else if (event.code === 'Space') {
        event.preventDefault();
        setPlaying(value => !value);
      } else if (event.key.toLowerCase() === 's') {
        event.preventDefault();
        splitClip();
      } else if (event.key.toLowerCase() === 'm') {
        // M abre a marcação no ponto atual; M de novo fecha o intervalo.
        event.preventDefault();
        toggleNoteMark();
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        removeSelection();
      } else if (event.key === 'Escape') {
        // Escape desiste da marcação em curso antes de mexer na seleção.
        if (pendingNoteStart !== null) setPendingNoteStart(null);
        else setSelection(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pendingNoteStart, redo, removeSelection, saveNow, splitClip, toggleNoteMark, undo]);

  const handleVideoTime = () => {
    const video = videoRef.current;
    if (!video || !activeClip || !playingRef.current) return;
    const local = Math.max(0, (video.currentTime - activeClip.sourceStart) / activeClip.playbackRate);
    const global = activeClip.timelineStart + local;
    if (video.currentTime >= activeClip.sourceEnd - 0.03 || global >= activeClip.timelineStart + activeClip.duration) {
      const nextIndex = project?.clips.findIndex(clip => clip.id === activeClip.id) ?? -1;
      const next = project?.clips[nextIndex + 1];
      if (next) setPlayhead(next.timelineStart + 0.001);
      else {
        setPlaying(false);
        setPlayhead(0);
      }
    } else {
      setPlayhead(global);
    }
  };

  if (loading) {
    return (
      <EditorPortal>
        <div className="fixed inset-0 z-[2147483000] flex h-[100dvh] items-center justify-center bg-slate-950 text-white" data-testid="video-project-editor">
          <div className="text-center"><div className="mb-3 text-4xl animate-pulse">◫</div><p className="font-bold">Abrindo projeto editável...</p></div>
        </div>
      </EditorPortal>
    );
  }

  if (!project || loadError) {
    return (
      <EditorPortal>
        <div className="fixed inset-0 z-[2147483000] flex h-[100dvh] items-center justify-center bg-slate-950 p-6 text-white" data-testid="video-project-editor">
          <div className="max-w-md rounded-2xl border border-rose-500/30 bg-rose-500/10 p-6 text-center">
            <p className="text-lg font-black">Não foi possível abrir o editor</p>
            <p className="mt-2 text-sm text-rose-200">{loadError}</p>
            <div className="mt-5 flex justify-center gap-2">
              <button onClick={loadProject} className="rounded-xl bg-white px-4 py-2 text-sm font-bold text-slate-900">Tentar novamente</button>
              <button onClick={onClose} className="rounded-xl border border-white/20 px-4 py-2 text-sm">Fechar</button>
            </div>
          </div>
        </div>
      </EditorPortal>
    );
  }

  const timelineWidth = Math.max(900, project.duration * zoom + 80);
  const currentVideoUrl = activeAsset ? assetUrls[activeAsset.id] : '';
  // O export compõe todas as imagens antes do ASS; repetir a mesma ordem aqui
  // evita que o canvas mostre uma imagem acima de um texto que ficará acima no MP4.
  const visibleOverlays = project.overlays
    .filter(overlay => playhead >= overlay.start && playhead <= overlay.end)
    .sort((a, b) => Number(a.type !== 'image') - Number(b.type !== 'image'));
  const fontOptions = selectedTextOverlay && !FONT_OPTIONS.includes(selectedTextOverlay.fontFamily)
    ? [selectedTextOverlay.fontFamily, ...FONT_OPTIONS]
    : FONT_OPTIONS;
  const activeClipMotion = entranceMotion(activeClip?.animation, playhead - (activeClip?.timelineStart || 0));
  const previewZoom = previewZoomFor(project, activeClip, playhead);
  const saveLabels: Record<SaveState, string> = {
    saved: 'Salvo',
    dirty: 'Alterações pendentes',
    saving: 'Salvando...',
    conflict: 'Conflito de versão',
    error: 'Erro ao salvar',
  };

  return (
    <EditorPortal>
    <div
      className="fixed inset-0 z-[2147483000] isolate flex h-[100dvh] min-h-0 flex-col overflow-hidden bg-[#070b14] text-slate-100"
      data-testid="video-project-editor"
      onDragOver={handleExternalDragOver}
      onDragLeave={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setExternalDragActive(false);
      }}
      onDrop={handleExternalDrop}
    >
      {externalDragActive && (
        <div className="pointer-events-none fixed inset-3 z-[2147483640] flex items-center justify-center rounded-3xl border-2 border-dashed border-indigo-300 bg-indigo-500/20 backdrop-blur-sm">
          <div className="rounded-2xl bg-slate-950/90 px-8 py-6 text-center shadow-2xl">
            <p className="text-3xl">＋</p>
            <p className="mt-2 text-sm font-black text-white">Solte vídeos ou imagens no editor</p>
            <p className="mt-1 text-xs text-indigo-200">Vídeos entram na timeline; imagens viram camadas editáveis.</p>
          </div>
        </div>
      )}
      <header className="flex min-h-16 shrink-0 items-center gap-3 overflow-x-auto overflow-y-hidden border-b border-white/10 bg-slate-950 px-4 py-2 [&>button]:shrink-0 [&>span]:shrink-0">
        <button onClick={() => void closeEditor()} className="rounded-xl border border-white/10 px-3 py-2 text-sm font-bold hover:bg-white/10" aria-label="Fechar editor">←</button>
        <div className="min-w-0">
          <input
            value={project.name}
            onChange={event => commit(draft => { draft.name = event.target.value; })}
            className="w-56 max-w-[32vw] truncate border-0 bg-transparent text-sm font-black text-white outline-none"
            aria-label="Nome do projeto"
          />
          <p className="truncate text-[10px] text-slate-500">{title || 'Editor de vídeo'} · revisão {project.revision}</p>
        </div>
        <div className="mx-2 h-7 w-px bg-white/10" />
        <button onClick={undo} disabled={!history?.past.length} className="rounded-lg px-2.5 py-2 text-xs font-bold hover:bg-white/10 disabled:opacity-30" title="Desfazer (Ctrl+Z)">↶</button>
        <button onClick={redo} disabled={!history?.future.length} className="rounded-lg px-2.5 py-2 text-xs font-bold hover:bg-white/10 disabled:opacity-30" title="Refazer (Ctrl+Y)">↷</button>
        <span className={`ml-auto text-[11px] font-bold ${saveState === 'saved' ? 'text-emerald-400' : saveState === 'error' || saveState === 'conflict' ? 'text-rose-400' : 'text-amber-300'}`}>
          {saveLabels[saveState]}
        </span>
        {saveState === 'conflict' && conflictProject && (
          <>
            <button onClick={acceptRemoteConflict} className="rounded-xl border border-rose-400/40 px-3 py-2 text-xs font-black text-rose-200 hover:bg-rose-400/10">Carregar atual</button>
            <button onClick={keepLocalConflict} className="rounded-xl border border-amber-400/40 px-3 py-2 text-xs font-black text-amber-200 hover:bg-amber-400/10">Manter minhas edições</button>
          </>
        )}
        <button onClick={() => void saveNow()} className="rounded-xl border border-white/10 px-3.5 py-2 text-xs font-black hover:bg-white/10">Salvar</button>
        {render?.status === 'completed' ? (
          <>
            <span className="rounded-xl bg-emerald-500/15 px-3 py-2 text-[11px] font-black text-emerald-300" title="A exportação já está salva na produção: é ela que a aba Edições mostra, baixa e publica.">
              ✓ Salva em Edições
            </span>
            <button onClick={downloadRender} className="rounded-xl bg-emerald-500 px-4 py-2 text-xs font-black text-slate-950 hover:bg-emerald-400">Baixar MP4</button>
          </>
        ) : (
          <button onClick={exportVideo} disabled={render?.status === 'queued' || render?.status === 'running'} className="rounded-xl bg-indigo-500 px-4 py-2 text-xs font-black text-white hover:bg-indigo-400 disabled:opacity-60">
            {render?.status === 'queued' || render?.status === 'running' ? `Exportando ${render.progress || 0}%` : 'Exportar vídeo'}
          </button>
        )}
      </header>

      {project.sourceMode === 'flattened' && (
        <div className="shrink-0 border-b border-amber-400/20 bg-amber-400/10 px-4 py-2 text-center text-xs text-amber-200">
          {project.sourceWarning || 'Projeto antigo aberto como clipe único. Novos vídeos preservam todas as camadas da IA.'}
        </div>
      )}

      {/* A caixa abre centrada sobre a régua assim que o intervalo fecha: o ponto
          já está guardado, só falta dizer o que está errado nele. */}
      {noteDraft && (
        <div className="fixed inset-0 z-[2147483100] flex items-center justify-center bg-slate-950/70 p-6" onClick={() => setNoteDraft(null)}>
          <div className="w-full max-w-lg rounded-2xl border border-amber-300/30 bg-slate-900 p-5 shadow-2xl" onClick={event => event.stopPropagation()}>
            <p className="text-[10px] font-black uppercase tracking-wider text-amber-300/80">
              Marcação · {formatTime(noteDraft.start)} – {formatTime(noteDraft.end)}
            </p>
            <p className="mt-1 text-xs text-slate-400">O que está errado nesse trecho? Escreva do seu jeito.</p>
            <textarea
              autoFocus
              value={noteDraft.text}
              onChange={event => setNoteDraft(current => current && { ...current, text: event.target.value })}
              onKeyDown={event => {
                if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) { event.preventDefault(); commitNoteDraft(); }
                if (event.key === 'Escape') { event.preventDefault(); setNoteDraft(null); }
              }}
              rows={3}
              maxLength={1000}
              placeholder="ex: aqui ficou arrastado, corta essa parte"
              className="mt-3 w-full resize-none rounded-xl border border-white/10 bg-slate-950 p-3 text-sm text-slate-100 outline-none focus:border-amber-300/50"
            />
            <div className="mt-3 flex items-center justify-end gap-2">
              <span className="mr-auto text-[10px] text-slate-500">Ctrl+Enter salva · Esc descarta</span>
              <button onClick={() => setNoteDraft(null)} className="rounded-xl border border-white/10 px-3 py-2 text-xs font-black text-slate-300 hover:bg-white/10">Descartar</button>
              <button onClick={commitNoteDraft} className="rounded-xl bg-amber-400 px-4 py-2 text-xs font-black text-slate-950 hover:bg-amber-300">Salvar marcação</button>
            </div>
          </div>
        </div>
      )}

      <main className="grid min-h-0 flex-1 grid-cols-[220px_minmax(360px,1fr)_280px]">
        <aside className="min-h-0 overflow-y-auto border-r border-white/10 bg-slate-950/70 p-3">
          <div className="mb-3 flex items-center justify-between">
            <div><p className="text-xs font-black">Mídias</p><p className="text-[10px] text-slate-500">{project.assets.length} disponíveis</p></div>
            <div className="flex gap-1">
              <button onClick={() => assetInputRef.current?.click()} disabled={uploadingAssets} className="rounded-lg bg-indigo-500/15 px-2 py-1.5 text-[10px] font-black text-indigo-300 hover:bg-indigo-500/25 disabled:opacity-50">
                {uploadingAssets ? 'Enviando...' : '+ Mídia'}
              </button>
              <button onClick={addText} className="rounded-lg bg-fuchsia-500/15 px-2 py-1.5 text-[10px] font-black text-fuchsia-300 hover:bg-fuchsia-500/25">+ Texto</button>
              <input
                ref={assetInputRef}
                type="file"
                accept="video/*,image/*"
                multiple
                className="hidden"
                onChange={event => void uploadAssets(Array.from(event.target.files || []))}
              />
            </div>
          </div>
          <button type="button" onClick={() => assetInputRef.current?.click()} className="mb-3 w-full rounded-xl border border-dashed border-white/15 bg-white/[0.025] px-3 py-3 text-center text-[10px] font-bold text-slate-400 hover:border-indigo-400/60 hover:bg-indigo-500/10 hover:text-indigo-200">
            Arraste arquivos aqui ou clique para enviar
          </button>
          <div className="space-y-2">
            {project.assets.map(asset => (
              <div
                key={asset.id}
                draggable
                role="button"
                tabIndex={0}
                onDragStart={event => {
                  setDraggedAssetId(asset.id);
                  event.dataTransfer.effectAllowed = 'copy';
                  event.dataTransfer.setData('application/x-astra-video-asset', asset.id);
                }}
                onDragEnd={() => setDraggedAssetId(null)}
                onDoubleClick={() => addAssetAsClip(asset)}
                onKeyDown={event => {
                  if (event.key === 'Enter') addAssetAsClip(asset);
                }}
                onClick={() => {
                  const clip = project.clips.find(item => item.assetId === asset.id);
                  if (clip) {
                    setSelection({ kind: 'clip', id: clip.id });
                    setPlayhead(clip.timelineStart);
                    return;
                  }
                  const imageOverlay = project.overlays.find(item => item.type === 'image' && item.assetId === asset.id);
                  if (imageOverlay) {
                    setSelection({ kind: 'overlay', id: imageOverlay.id });
                    setPlayhead(imageOverlay.start);
                  }
                }}
                className={`group relative w-full cursor-pointer rounded-xl border p-2 text-left transition-colors ${activeAsset?.id === asset.id ? 'border-indigo-400 bg-indigo-500/15' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.07]'}`}
              >
                {asset.source === 'user' && (
                  <button
                    type="button"
                    disabled={deletingAssetId === asset.id}
                    onPointerDown={event => event.stopPropagation()}
                    onDragStart={event => { event.preventDefault(); event.stopPropagation(); }}
                    onClick={event => {
                      event.stopPropagation();
                      void deleteUserAsset(asset);
                    }}
                    className="absolute right-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-lg border border-rose-300/30 bg-slate-950/90 text-xs text-rose-300 opacity-80 shadow-lg hover:bg-rose-500 hover:text-white group-hover:opacity-100 disabled:opacity-40"
                    aria-label={`Excluir mídia ${asset.name}`}
                    title="Excluir mídia enviada"
                  >
                    {deletingAssetId === asset.id ? '…' : '🗑'}
                  </button>
                )}
                <div className="flex aspect-video items-center justify-center overflow-hidden rounded-lg bg-slate-900">
                  {asset.kind === 'image' && assetUrls[asset.id] ? (
                    <img src={assetUrls[asset.id]} alt="" className="h-full w-full object-cover" draggable={false} />
                  ) : (
                    <span className="text-xl text-slate-500">{asset.kind === 'image' ? '▧' : '▶'}</span>
                  )}
                </div>
                <p className="mt-2 truncate text-[11px] font-bold text-slate-200">{asset.name}</p>
                <div className="mt-1 flex items-center justify-between text-[9px] text-slate-500">
                  <span>{asset.kind === 'image' ? 'Imagem' : formatTime(asset.duration)}</span>
                  <span className="opacity-0 group-hover:opacity-100">arraste ou dê 2 cliques</span>
                </div>
                <button
                  type="button"
                  disabled={downloadingAssetId === asset.id}
                  onPointerDown={event => event.stopPropagation()}
                  onDragStart={event => { event.preventDefault(); event.stopPropagation(); }}
                  onDoubleClick={event => event.stopPropagation()}
                  onClick={event => {
                    event.stopPropagation();
                    void downloadAsset(asset);
                  }}
                  className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-white/10 px-1.5 py-0.5 text-[9px] font-bold text-slate-400 hover:border-indigo-400/60 hover:text-indigo-200 disabled:opacity-40"
                  aria-label={`Baixar mídia ${asset.name}`}
                  title="Baixar o arquivo original"
                >
                  <svg viewBox="0 0 16 16" width={9} height={9} aria-hidden><path d="M8 2v8m0 0L4.8 6.8M8 10l3.2-3.2M3 13.5h10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  {downloadingAssetId === asset.id ? 'Baixando…' : 'Baixar'}
                </button>
              </div>
            ))}
          </div>
          <div className="mt-5 border-t border-white/10 pt-4">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[10px] font-black uppercase tracking-widest text-emerald-300/70">Trilha de fundo</p>
              {/* Oculto: o controle e o transporte da timeline, nao um player
                  separado. So existe quando ha arquivo de audio de verdade. */}
              {/* So monta quando a trilha esta LIGADA: desmarcar a caixa tem que
                  remover o elemento, nao so pausar — senao ele continua ocupando
                  banda em segundo plano. Tambem e o jeito de testar se o
                  travamento vem da musica: desmarque e veja. */}
              {musicAudioSrc && music?.enabled && (
                <audio
                  key={music?.audioId}
                  ref={musicAudioRef}
                  src={musicAudioSrc}
                  loop
                  preload="metadata"
                  className="hidden"
                />
              )}
              <button
                type="button"
                onClick={() => setMusicPickerOpen(open => !open)}
                className="text-[10px] text-slate-400 hover:text-emerald-300"
              >
                {musicPickerOpen ? 'Fechar' : music ? 'Trocar' : 'Escolher'}
              </button>
            </div>

            {music ? (
              <div className="rounded-lg border border-emerald-400/25 bg-emerald-500/5 p-2">
                <div className="flex items-start gap-1.5">
                  <input
                    type="checkbox"
                    checked={music.enabled}
                    onChange={event => patchMusic({ enabled: event.target.checked })}
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded"
                    title="Publicar com esta musica"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[11px] text-emerald-50">{music.title || music.audioId}</p>
                    <p className="truncate text-[9px] text-slate-400">
                      {music.artist || 'Desconhecido'}
                      {music.pickedBy === 'llm' ? ' - escolha da IA' : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={removeMusic}
                    className="shrink-0 text-[10px] text-slate-600 hover:text-rose-300"
                    aria-label="Remover musica"
                  >
                    X
                  </button>
                </div>
                {music.reason && <p className="mt-1 text-[9px] italic leading-snug text-slate-500">{music.reason}</p>}
                {musicPlaybackError && (
                  <p className="mt-1 rounded border border-amber-400/30 bg-amber-400/10 px-1.5 py-1 text-[9px] leading-snug text-amber-200">
                    {musicPlaybackError}
                  </p>
                )}
                {(musicAudioSrc || music.previewUrl) && (musicAudioSrc ? (
                  <p className="mt-1 text-[9px] text-emerald-300/70">Toca junto com o play da timeline.</p>
                ) : (
                  <a
                    href={music.previewUrl || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-block text-[9px] text-slate-400 underline hover:text-emerald-300"
                  >
                    Ouvir no Instagram ↗
                  </a>
                ))}
                <label className="mt-2 block text-[9px] uppercase tracking-wide text-slate-500">
                  Volume {Math.round((music.volume ?? 0.1) * 100)}%
                </label>
                <input
                  type="range" min={1} max={100}
                  value={Math.round((music.volume ?? 0.1) * 100)}
                  onChange={event => patchMusic({ volume: Number(event.target.value) / 100 })}
                  className="w-full"
                />
                <p className="mt-1 text-[9px] leading-snug text-slate-500">
                  Anexada na publicacao do Instagram — o arquivo exportado nao leva a musica.
                </p>
              </div>
            ) : (
              <p className="text-[10px] leading-snug text-slate-500">
                {project?.musicUnavailable || 'Sem trilha. Clique em Escolher para ver o que esta em alta no Instagram.'}
              </p>
            )}

            {musicPickerOpen && (
              <div className="mt-2 space-y-2">
                <input
                  type="text"
                  value={musicQuery}
                  onChange={event => setMusicQuery(event.target.value)}
                  placeholder="Buscar - vazio = em alta"
                  className="w-full rounded-lg border border-white/10 bg-slate-900 px-2 py-1 text-[10px] text-slate-200 outline-none focus:border-emerald-400"
                />
                {musicLoading && <p className="text-[9px] text-slate-500">Carregando...</p>}
                {musicCatalogError && <p className="text-[9px] leading-snug text-amber-300/80">{musicCatalogError}</p>}
                {!musicLoading && !musicCatalogError && musicCatalog.length === 0 && (
                  <p className="text-[9px] text-slate-500">Nenhuma faixa encontrada.</p>
                )}
                <div className="max-h-52 space-y-1 overflow-y-auto">
                  {musicCatalog.map(track => (
                    <div
                      key={track.audioId}
                      className={`flex items-center gap-1.5 rounded-lg border px-2 py-1 ${track.audioId === music?.audioId ? 'border-emerald-400/50 bg-emerald-500/10' : 'border-white/10 hover:bg-white/5'}`}
                    >
                      {track.coverUrl && <img src={track.coverUrl} alt="" className="h-6 w-6 shrink-0 rounded object-cover" />}
                      <button type="button" onClick={() => chooseMusicTrack(track)} className="min-w-0 flex-1 text-left">
                        <p className="truncate text-[10px] text-slate-200">{track.title || track.audioId}</p>
                        <p className="truncate text-[9px] text-slate-500">
                          {track.artist || 'Desconhecido'}
                          {track.duration ? ` - ${formatTime(track.duration)}` : ''}
                        </p>
                      </button>
                      {track.previewUrl && track.previewIsExternal !== false && (
                        // Sem arquivo: a unica audicao possivel e a pagina no Instagram.
                        <a
                          href={track.previewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 text-[9px] text-slate-400 hover:text-emerald-300"
                          title="Ouvir no Instagram"
                        >
                          ↗
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {notes.length > 0 && (
            <div className="mt-5 border-t border-white/10 pt-4">
              <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-amber-300/70">
                Marcações ({notes.filter(note => note.status === 'open').length} abertas)
              </p>
              <div className="space-y-1.5">
                {notes.map(note => (
                  <div key={note.id} className={`group rounded-lg border px-2 py-1.5 ${note.status === 'done' ? 'border-emerald-400/25 bg-emerald-500/5' : 'border-amber-300/30 bg-amber-400/10'}`}>
                    <div className="flex items-start gap-1.5">
                      <button
                        type="button"
                        onClick={() => toggleNoteStatus(note.id)}
                        className={`mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border text-[8px] ${note.status === 'done' ? 'border-emerald-400 bg-emerald-500 text-slate-950' : 'border-amber-300/60'}`}
                        title={note.status === 'done' ? 'Reabrir marcação' : 'Marcar como resolvida'}
                      >
                        {note.status === 'done' ? '✓' : ''}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setPlaying(false); setPlayhead(note.start); }}
                        className="min-w-0 flex-1 text-left"
                        title="Ir para esse ponto"
                      >
                        <p className={`text-[10px] leading-snug ${note.status === 'done' ? 'text-slate-500 line-through' : 'text-amber-50'}`}>{note.text}</p>
                        <p className="mt-0.5 font-mono text-[8px] text-slate-500">{formatTime(note.start)} – {formatTime(note.end)}</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => removeNote(note.id)}
                        className="shrink-0 text-[10px] text-slate-600 opacity-0 hover:text-rose-300 group-hover:opacity-100"
                        aria-label="Remover marcação"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-5 border-t border-white/10 pt-4">
            <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-500">Atalhos</p>
            <div className="space-y-1 text-[10px] text-slate-500">
              <p><kbd className="text-slate-300">Espaço</kbd> reproduzir</p>
              <p><kbd className="text-slate-300">S</kbd> dividir clipe</p>
              <p><kbd className="text-slate-300">M</kbd> marcar problema</p>
              <p><kbd className="text-slate-300">Delete</kbd> remover</p>
              <p><kbd className="text-slate-300">Ctrl Z/Y</kbd> desfazer/refazer</p>
            </div>
          </div>
        </aside>

        <section className="flex min-h-0 flex-col bg-[#0b1020]">
          <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden p-5">
            <div
              ref={stageRef}
              className="relative max-h-full max-w-full overflow-hidden shadow-2xl shadow-black/60"
              style={{
                aspectRatio: `${project.width}/${project.height}`,
                height: project.height >= project.width ? 'min(58vh, 100%)' : 'auto',
                width: project.width > project.height ? 'min(70vw, 100%)' : 'auto',
                maxWidth: '100%',
                backgroundColor: project.backgroundColor,
              }}
              onClick={() => setSelection(null)}
              onDragOver={event => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; }}
              onDrop={handleStageDrop}
              data-testid="video-preview-stage"
            >
              {/* Janela de vídeo da tela dividida. Sem inserção ativa ela é uma
                  moldura transparente do tamanho do palco e nada muda. */}
              <div
                className="absolute overflow-hidden"
                style={splitPreview
                  ? { left: 0, top: `${splitPreview.videoTopPct}%`, width: '100%', height: `${SPLIT_PREVIEW.videoFraction * 100}%` }
                  : { inset: 0 }}
              >
              <div
                className="absolute left-0 top-0 w-full"
                style={splitPreview
                  ? {
                    height: `${(1 / SPLIT_PREVIEW.videoFraction) * 100}%`,
                    transformOrigin: 'top center',
                    transform: `scale(${splitPreview.zoom}) translateY(${(-splitPreview.focusY / 1920) * 100}%)`,
                  }
                  : { height: '100%' }}
              >
              {/* Zoom da exportação (salto de plano + push-in), o mesmo do MP4.
                  Ver `previewZoomFor`. Escalar pelo ponto `centerX` equivale ao
                  `x=(iw-iw/zoom)*cx` do zoompan; o eixo vertical fica centrado. */}
              <div
                className="h-full w-full"
                style={previewZoom ? {
                  transform: `scale(${previewZoom.zoom.toFixed(4)})`,
                  transformOrigin: `${(previewZoom.centerX * 100).toFixed(2)}% 50%`,
                } : undefined}
              >
              {currentVideoUrl && activeAsset?.kind === 'image' ? (
                <img
                  src={currentVideoUrl}
                  alt={activeAsset.name}
                  draggable={false}
                  onPointerDown={startVideoDrag}
                  className="h-full w-full"
                  style={{
                    objectFit: activeClip?.transform.fit || 'cover',
                    objectPosition: activeClip
                      ? `${(activeClip.transform.x + 1) * 50}% ${(activeClip.transform.y + 1) * 50}%`
                      : '50% 50%',
                    transformOrigin: activeClip
                      ? `${(activeClip.transform.x + 1) * 50}% ${(activeClip.transform.y + 1) * 50}%`
                      : '50% 50%',
                    transform: `${activeClipMotion.transform} scale(${activeClip?.transform.scale || 1}) rotate(${activeClip?.transform.rotation || 0}deg)`.trim(),
                    opacity: activeClipMotion.opacity,
                  }}
                />
              ) : currentVideoUrl ? (
                <video
                  ref={videoRef}
                  src={currentVideoUrl}
                  playsInline
                  onPointerDown={startVideoDrag}
                  onTimeUpdate={handleVideoTime}
                  onLoadedMetadata={() => {
                    if (!videoRef.current || !activeClip) return;
                    if (activeAsset) assetRetryRef.current[activeAsset.id] = 0;
                    videoRef.current.currentTime = activeClip.sourceStart + Math.max(0, playhead - activeClip.timelineStart) * activeClip.playbackRate;
                    if (playingRef.current) void videoRef.current.play();
                  }}
                  onError={() => {
                    if (!activeAsset) return;
                    const attempts = assetRetryRef.current[activeAsset.id] || 0;
                    if (attempts >= 2) return;
                    assetRetryRef.current[activeAsset.id] = attempts + 1;
                    assetExpiryRef.current[activeAsset.id] = 0;
                    setAssetUrls(current => {
                      const next = { ...current };
                      delete next[activeAsset.id];
                      return next;
                    });
                  }}
                  className="h-full w-full"
                  style={{
                    objectFit: activeClip?.transform.fit || 'cover',
                    objectPosition: activeClip
                      ? `${(activeClip.transform.x + 1) * 50}% ${(activeClip.transform.y + 1) * 50}%`
                      : '50% 50%',
                    transformOrigin: activeClip
                      ? `${(activeClip.transform.x + 1) * 50}% ${(activeClip.transform.y + 1) * 50}%`
                      : '50% 50%',
                    transform: activeClip
                      ? `${activeClipMotion.transform} scale(${activeClip.transform.scale}) rotate(${activeClip.transform.rotation}deg)`.trim()
                      : undefined,
                    opacity: activeClipMotion.opacity,
                  }}
                />
              ) : (
                <div className="flex h-full min-h-64 w-full items-center justify-center text-xs text-slate-500">Carregando mídia...</div>
              )}
              </div>
              </div>
              </div>
              {/* Faixa de arte da tela dividida. Fica ANTES dos overlays de texto
                  porque no vídeo entregue ela também é queimada antes — a legenda e
                  o gancho ficam por cima dela, nunca por baixo. */}
              {splitPreview && (
                <div
                  className="absolute left-0 w-full overflow-hidden"
                  style={{ top: `${splitPreview.bandTopPct}%`, height: `${SPLIT_PREVIEW.bandFraction * 100}%`, backgroundColor: '#0a0a0c' }}
                  onClick={event => { event.stopPropagation(); setSelectedInsertId(splitPreview.insert.id); }}
                  role="presentation"
                >
                  {assetUrls[splitPreview.insert.assetId] && (
                    <img
                      src={assetUrls[splitPreview.insert.assetId]}
                      alt={splitPreview.insert.subject}
                      draggable={false}
                      className="h-full w-full object-cover"
                    />
                  )}
                  {splitPreview.seamGradient && (
                    <div
                      className="absolute left-0 bottom-0 w-full"
                      style={{
                        height: `${(splitPreview.seamGradient.heightPct / (SPLIT_PREVIEW.bandFraction * 100)) * 100}%`,
                        background: 'linear-gradient(180deg,rgba(10,10,12,0),rgba(10,10,12,0.75))',
                      }}
                    />
                  )}
                </div>
              )}
              {visibleOverlays.map(overlay => {
                const motion = overlayMotion(overlay, playhead);
                const commonStyle: CSSProperties = {
                  left: `${overlay.x * 100}%`,
                  top: `${overlay.y * 100}%`,
                  width: `${overlay.width * 100}%`,
                  transform: `translate(-50%, -50%) ${motion.transform} rotate(${overlay.rotation}deg)`,
                  opacity: overlay.opacity * motion.opacity,
                };
                if (overlay.type === 'image') {
                  const imageAsset = project.assets.find(asset => asset.id === overlay.assetId);
                  const imageUrl = assetUrls[overlay.assetId];
                  return (
                    <button
                      key={overlay.id}
                      type="button"
                      onPointerDown={event => startOverlayDrag(event, overlay)}
                      onClick={event => { event.stopPropagation(); setSelection({ kind: 'overlay', id: overlay.id }); }}
                      className={`absolute box-border cursor-move select-none bg-transparent p-0 ${selection?.kind === 'overlay' && selection.id === overlay.id ? 'ring-2 ring-indigo-400 ring-offset-2 ring-offset-transparent' : ''}`}
                      style={commonStyle}
                      aria-label={`Imagem ${overlay.name}`}
                    >
                      {imageUrl ? (
                        <img src={imageUrl} alt={imageAsset?.name || overlay.name} className="pointer-events-none block h-auto w-full" draggable={false} />
                      ) : (
                        <span className="block aspect-square w-full animate-pulse rounded-lg bg-white/10" />
                      )}
                      {selection?.kind === 'overlay' && selection.id === overlay.id && !overlay.locked && (
                        <>
                          {(Object.keys(IMAGE_RESIZE_HANDLE_CLASSES) as ResizeHandle[]).map(handle => (
                            <span
                              key={handle}
                              onPointerDown={event => startImageResize(event, overlay, handle)}
                              className={`absolute z-20 h-3 w-3 rounded-full border border-indigo-200 bg-white shadow-[0_0_0_2px_rgba(79,70,229,0.75)] ${IMAGE_RESIZE_HANDLE_CLASSES[handle]}`}
                              aria-hidden="true"
                              data-resize-handle={handle}
                            />
                          ))}
                        </>
                      )}
                    </button>
                  );
                }
                return (
                  <button
                    key={overlay.id}
                    type="button"
                    onPointerDown={event => startOverlayDrag(event, overlay)}
                    onClick={event => { event.stopPropagation(); setSelection({ kind: 'overlay', id: overlay.id }); }}
                    className={`absolute box-border cursor-move select-none whitespace-pre-wrap ${selection?.kind === 'overlay' && selection.id === overlay.id ? 'ring-2 ring-indigo-400 ring-offset-2 ring-offset-transparent' : ''}`}
                    style={{
                      ...commonStyle,
                      color: overlay.color,
                      backgroundColor: 'transparent',
                      fontFamily: overlay.fontFamily,
                      fontSize: `${Math.max(1, overlay.fontSize * stageScale)}px`,
                      padding: 0,
                      // Peso explícito quando o estilo o carrega (500/600/700/800);
                      // sem ele, o comportamento de sempre.
                      fontWeight: overlay.fontWeight || (overlay.bold ? 900 : 400),
                      fontStyle: overlay.italic ? 'italic' : 'normal',
                      textAlign: overlay.align,
                      letterSpacing: `${overlay.letterSpacing * stageScale}px`,
                      // Achatamento dos glifos do estilo `simples` (escalaX/escalaY
                      // 0,9 da tabela): no .ass ele é \fscx/\fscy e aqui é o
                      // `transform`. Sem ele a prévia mostrava a linha 10% maior do
                      // que o vídeo, nos dois eixos.
                      //
                      // A escala ENTRA NA COMPOSIÇÃO do transform, nunca no lugar dele.
                      //
                      // O QUE ACONTECIA ANTES: este objeto era espalhado DEPOIS de
                      // `commonStyle` com a chave `transform`, então ele SUBSTITUÍA o
                      // `translate(-50%, -50%)` que ancora a camada pelo CENTRO (e de
                      // quebra o `rotate` e a animação de entrada). A camada passava a
                      // ser desenhada com o canto superior esquerdo em (x, y): meia
                      // largura à direita e meia altura abaixo do lugar certo. O
                      // export NÃO tem esse desvio — o .ass é sempre \an5 + \pos(x,y),
                      // pelo centro —, então quem arrastava a legenda até ela ficar bem
                      // posicionada NA TELA gravava um `x` deslocado de meia largura, e
                      // o MP4 desenhava a linha fora do quadro (caixa de 720px: 180px
                      // para fora, à esquerda).
                      //
                      // SÓ atingia overlay ESTILIZADO com escala != 1 — o `simples`
                      // (0,9 nos dois eixos) e os grupos de karaokê que o `fit`
                      // encolheu (~1 em 13 grupos) —, e era por isso que o defeito
                      // aparecia em ALGUMAS legendas e não em todas.
                      //
                      // Ordem: `scale` no FIM da lista aplica primeiro (achata os
                      // glifos em torno do centro da caixa) e o `translate` continua
                      // sendo o último a agir, com as porcentagens medidas na caixa
                      // NÃO transformada — o centro cai exatamente em (x, y), como no
                      // `\pos` do .ass.
                      ...(estiloDoOverlay(overlay) && (overlay.scaleX !== 1 || overlay.scaleY !== 1) ? {
                        transform: `${commonStyle.transform} scale(${overlay.scaleX ?? 1},${overlay.scaleY ?? 1})`,
                        transformOrigin: 'center',
                      } : {}),
                      // O contorno do CSS é CENTRADO no contorno do glifo e o do
                      // ASS cresce só para FORA: o mesmo número desenha metade da
                      // espessura aqui. O overlay estilizado guarda o número do ASS
                      // (é ele que tem de sair certo no MP4), então a prévia dobra.
                      WebkitTextStroke: `${Math.max(0, overlay.outlineWidth * (contornoDobrado(overlay) ? 2 : 1) * stageScale)}px ${colorWithOpacity(overlay.outlineColor, overlay.outlineOpacity)}`,
                      paintOrder: 'stroke fill',
                      textShadow: `${overlay.shadowOffsetX * stageScale}px ${overlay.shadowOffsetY * stageScale}px 0 ${colorWithOpacity(overlay.shadowColor, overlay.shadowOpacity)}`,
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        // No `realce` a caixa ja foi pintada linha a linha acima:
                        // pintar de novo aqui daria um retangulo unico, que e o
                        // desenho do `card` e nao o dele.
                        backgroundColor: overlay.backgroundScope === 'line'
                          ? 'transparent'
                          : colorWithOpacity(overlay.backgroundColor, overlay.backgroundOpacity),
                        padding: overlay.backgroundScope === 'line'
                          ? 0
                          : `${Math.max(0, overlay.backgroundPadding * stageScale)}px`,
                      }}
                    >
                      {overlay.textRuns?.length
                        // Empilha por linha, como o vídeo faz. Sem agrupar por
                        // lineIndex os trechos ficavam todos em fluxo horizontal
                        // e o editor mostrava uma frase corrida, nada parecida
                        // com o bloco que sai no MP4.
                        ? groupRunsByLine(overlay.textRuns).map((line, lineIndex) => (
                            <div
                              key={`${overlay.id}-line-${lineIndex}`}
                              style={{
                                display: 'block',
                                // Passo de linha do ESTILO quando ele existe. O
                                // 1,04 fixo não é o 1,02–1,18 das tabelas, e num
                                // bloco de duas linhas essa diferença é visível.
                                lineHeight: overlay.lineStep
                                  ? `${overlay.lineStep * stageScale}px`
                                  : 1.04,
                                whiteSpace: 'nowrap',
                                // `realce` pinta um bloco POR LINHA (a folga entre
                                // eles é o desenho); `card` pinta um retângulo atrás
                                // das duas, e aí a caixa fica no bloco, abaixo.
                                ...(overlay.backgroundScope === 'line' ? {
                                  display: 'inline-block',
                                  backgroundColor: colorWithOpacity(overlay.backgroundColor, overlay.backgroundOpacity),
                                  padding: `0 ${Math.max(0, overlay.backgroundPadding * stageScale)}px`,
                                  borderRadius: `${3 * stageScale}px`,
                                } : {}),
                              }}
                            >
                              {line.map((run, index) => (
                                <span
                                  key={`${overlay.id}-run-${lineIndex}-${index}`}
                                  style={{
                                    display: 'inline-block',
                                    marginLeft: index > 0 ? `${12 * stageScale}px` : undefined,
                                    color: run.color,
                                    fontFamily: run.fontFamily,
                                    fontSize: `${Math.max(1, run.fontSize * stageScale)}px`,
                                    fontWeight: run.fontWeight || (run.bold ? 900 : 400),
                                    fontStyle: run.italic ? 'italic' : 'normal',
                                    // Na legenda do MODO ANTIGO a palavra que
                                    // ainda não foi falada fica FANTASMA, como no
                                    // vídeo; nos quatro estilos novos e no bloco
                                    // empilhado ela é invisível (ver
                                    // legendaComFantasma).
                                    //
                                    // `captionReveal: 'group'` acende o grupo
                                    // INTEIRO de uma vez, que é o que o motor
                                    // desenha nos três estáticos: um evento por
                                    // linha, tudo a 100% desde o primeiro quadro.
                                    // Sem espelhar isso a prévia montava a linha
                                    // palavra a palavra e voltava a divergir do MP4
                                    // — o mesmo defeito do fantasma com o sinal
                                    // trocado (texto FALTANDO em vez de sobrando).
                                    opacity: revelacaoDaLegenda(overlay) === 'group' || playhead + 0.001 >= run.start
                                      ? 1
                                      : (legendaComFantasma(overlay) ? 0.34 : 0),
                                    // Contorno e sombra por trecho, como o vídeo:
                                    // um traço único para o bloco inteiro engorda
                                    // a linha pequena e some na linha grande — e,
                                    // na COR, pinta de preto o halo das linhas
                                    // escuras que no MP4 saem com halo branco.
                                    WebkitTextStroke: `${Math.max(0, (Number.isFinite(run.outlineWidth as number) ? (run.outlineWidth as number) : overlay.outlineWidth) * (contornoDobrado(overlay) ? 2 : 1) * stageScale)}px ${colorWithOpacity(run.outlineColor || overlay.outlineColor, overlay.outlineOpacity)}`,
                                    // O X vem do BLOCO de propósito: o trecho só
                                    // carrega o deslocamento vertical, e o `\xshad`
                                    // do .ass sai do overlay (buildEditorAss) — o
                                    // run tag só sobrescreve o `\yshad`. Com o `0px`
                                    // fixo que estava aqui, mexer "Sombra X" mudava
                                    // o MP4 e não mudava a tela: medido, sombra X 40
                                    // deixava a tinta do palco idêntica (4.818 px) e
                                    // a sombra computada no trecho em 0px.
                                    ...(Number.isFinite(run.shadowOffsetY as number) ? {
                                      textShadow: `${overlay.shadowOffsetX * stageScale}px ${(run.shadowOffsetY as number) * stageScale}px 0 ${colorWithOpacity(overlay.shadowColor, overlay.shadowOpacity)}`,
                                    } : {}),
                                  }}
                                >
                                  {applyTextCase(run.text, run.textCase)}
                                </span>
                              ))}
                            </div>
                          ))
                        : applyTextCase(overlay.text, overlay.textCase)}
                    </span>
                  </button>
                );
              })}

              {/* Zona segura das plataformas. Instagram e TikTok desenham a
                  própria interface por cima do vídeo: legenda do post, nome do
                  perfil e a coluna de botões à direita. Texto colocado ali
                  simplesmente não é lido — e no editor manual nada impedia
                  arrastar um overlay para dentro dessa faixa. */}
              {showSafeArea && project.height > project.width && (
                <div className="pointer-events-none absolute inset-0 z-20" aria-hidden>
                  <div
                    className="absolute inset-x-0 bottom-0 border-t border-dashed border-rose-400/40 bg-rose-500/[0.07]"
                    style={{ height: `${(430 / 1920) * 100}%` }}
                  >
                    <span className="absolute left-1.5 top-1 text-[8px] font-black uppercase tracking-wider text-rose-300/70">
                      legenda e botões do app
                    </span>
                  </div>
                  <div
                    className="absolute inset-y-0 right-0 border-l border-dashed border-rose-400/25"
                    style={{ width: '14%' }}
                  />
                  <div
                    className="absolute inset-x-0 top-0 border-b border-dashed border-rose-400/25"
                    style={{ height: '8%' }}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="flex h-14 shrink-0 items-center justify-center gap-4 border-t border-white/10 bg-slate-950/60 px-4">
            <button
              onClick={toggleNoteMark}
              className={`rounded-lg px-2 py-1 text-[10px] font-black ${pendingNoteStart === null ? 'text-amber-300/80 hover:bg-amber-400/15' : 'bg-amber-400/25 text-amber-200'}`}
              title={pendingNoteStart === null ? 'Marcar um problema a partir daqui (M)' : `Fechar a marcação iniciada em ${formatTime(pendingNoteStart)} (M)`}
            >
              {pendingNoteStart === null ? '⚑ Marcar' : '⚑ Fechar'}
            </button>
            {project.height > project.width && (
              <button
                onClick={() => setShowSafeArea(value => !value)}
                className={`rounded-lg px-2 py-1 text-[10px] font-black ${showSafeArea ? 'bg-rose-400/20 text-rose-200' : 'text-slate-500 hover:bg-white/10'}`}
                title="Mostra onde o Instagram e o TikTok desenham a própria interface por cima do vídeo"
              >
                ⬚ Zona segura
              </button>
            )}
            <button onClick={() => setPlayhead(0)} className="rounded-lg p-2 text-slate-400 hover:bg-white/10 hover:text-white">|◀</button>
            <button onClick={() => setPlaying(value => !value)} className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-sm font-black text-slate-950 hover:scale-105">
              {playing ? 'Ⅱ' : '▶'}
            </button>
            <span className="w-28 font-mono text-xs text-slate-300">{formatTime(playhead)} <span className="text-slate-600">/ {formatTime(project.duration)}</span></span>
            <input
              type="range"
              min={0}
              max={Math.max(0.01, project.duration)}
              step={0.01}
              value={playhead}
              onChange={event => { setPlaying(false); setPlayhead(Number(event.target.value)); }}
              className="w-40 accent-red-400"
              aria-label="Cursor da timeline"
            />
            <select
              value={project.aspectRatio}
              onChange={event => {
                const ratio = event.target.value as VideoEditorProject['aspectRatio'];
                commit(draft => Object.assign(draft, { aspectRatio: ratio, ...ASPECT_SIZES[ratio] }));
              }}
              className="rounded-lg border border-white/10 bg-slate-900 px-2 py-1.5 text-[10px] font-bold text-slate-300"
              aria-label="Proporção do vídeo"
            >
              <option value="9:16">9:16 Reels</option>
              <option value="16:9">16:9 YouTube</option>
              <option value="1:1">1:1 Feed</option>
              <option value="4:5">4:5 Retrato</option>
            </select>
          </div>
        </section>

        <aside className="min-h-0 overflow-y-auto border-l border-white/10 bg-slate-950/70 p-4" data-testid="video-inspector">
          {selectedClip ? (
            <div className="space-y-4">
              <div><p className="text-xs font-black">Clipe de vídeo</p><p className="mt-0.5 truncate text-[10px] text-slate-500">{selectedClip.name}</p></div>
              <div className="grid grid-cols-2 gap-2">
                <NumberInput label="Início fonte" value={selectedClip.sourceStart} min={0} max={selectedClip.sourceEnd - 0.08} onChange={value => updateClip(selectedClip.id, { sourceStart: value })} />
                <NumberInput label="Fim fonte" value={selectedClip.sourceEnd} min={selectedClip.sourceStart + 0.08} max={selectedClipAsset?.duration || 3600} onChange={value => updateClip(selectedClip.id, { sourceEnd: value })} />
              </div>
              <div>
                <FieldLabel>Velocidade · {selectedClip.playbackRate.toFixed(2)}x</FieldLabel>
                <input type="range" min={0.25} max={4} step={0.05} value={selectedClip.playbackRate} onChange={event => updateClip(selectedClip.id, { playbackRate: Number(event.target.value) })} className="w-full accent-indigo-500" />
              </div>
              <div>
                <div className="flex items-end justify-between">
                  <FieldLabel>Áudio · {selectedClip.muted ? 'removido' : `${Math.round(selectedClip.volume * 100)}%`}</FieldLabel>
                  <button onClick={() => updateClip(selectedClip.id, { muted: !selectedClip.muted })} className={`mb-1 rounded-md px-2 py-1 text-[9px] font-black ${selectedClip.muted ? 'bg-rose-500/20 text-rose-300' : 'bg-emerald-500/20 text-emerald-300'}`}>
                    {selectedClip.muted ? 'Ativar áudio' : 'Remover áudio'}
                  </button>
                </div>
                <input type="range" min={0} max={1} step={0.05} disabled={selectedClip.muted} value={Math.min(1, selectedClip.volume)} onChange={event => updateClip(selectedClip.id, { volume: Number(event.target.value) })} className="w-full accent-emerald-500 disabled:opacity-30" />
              </div>
              <div>
                <FieldLabel>Ajuste no canvas</FieldLabel>
                <select value={selectedClip.transform.fit} onChange={event => updateClip(selectedClip.id, { transform: { ...selectedClip.transform, fit: event.target.value as 'cover' | 'contain' } })} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-2 text-xs">
                  <option value="cover">Preencher / recortar</option>
                  <option value="contain">Conter inteiro</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <NumberInput label="Posição X" value={selectedClip.transform.x} min={-1} max={1} onChange={value => updateClip(selectedClip.id, { transform: { ...selectedClip.transform, x: value } })} />
                <NumberInput label="Posição Y" value={selectedClip.transform.y} min={-1} max={1} onChange={value => updateClip(selectedClip.id, { transform: { ...selectedClip.transform, y: value } })} />
                <NumberInput label="Escala" value={selectedClip.transform.scale} min={0.25} max={4} step={0.05} onChange={value => updateClip(selectedClip.id, { transform: { ...selectedClip.transform, scale: value } })} />
                <NumberInput label="Rotação" value={selectedClip.transform.rotation} min={-180} max={180} step={1} onChange={value => updateClip(selectedClip.id, { transform: { ...selectedClip.transform, rotation: value } })} />
              </div>
              <div>
                {/* O 'flash' daqui NÃO é o flash de emenda dos elementos de edição:
                    é um fade branco de ~5 quadros nas DUAS pontas deste clipe, com
                    `afade` de áudio junto. O rótulo dizia só "Flash" e quem ligava
                    esperando o acento de corte abafava a voz em cada corte. */}
                <FieldLabel>Transição do clipe</FieldLabel>
                <select value={selectedClip.transition} onChange={event => updateClip(selectedClip.id, { transition: event.target.value as VideoEditorClip['transition'] })} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-2 text-xs">
                  <option value="none">Sem transição</option><option value="fade">Fade suave (clipe)</option><option value="flash">Fade branco (clipe)</option>
                </select>
                {selectedClip.transition !== 'none' && (
                  <p className="mt-1 text-[9px] leading-snug text-slate-500">
                    {selectedClip.transition === 'flash'
                      ? 'Clarão branco nas duas pontas deste clipe. É só imagem — o áudio não abaixa.'
                      : 'Esmaece nas duas pontas deste clipe, com o áudio descendo junto.'}
                    {' '}Para o acento de corte, use “Flash na transição” no painel do projeto.
                  </p>
                )}
              </div>
              <div>
                <FieldLabel>Animação de entrada</FieldLabel>
                <select value={selectedClip.animation || 'none'} onChange={event => updateClip(selectedClip.id, { animation: event.target.value as VideoEditorOverlayAnimation })} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-2 text-xs">
                  {ANIMATION_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </div>
              {/* Centro do zoom por corte. A medição automática (`measureSubjectCenter`)
                  erra em plano com duas pessoas: ela mede concentração de borda, então
                  fecha entre as duas cabeças. Aqui a pessoa corrige à mão — e este é o
                  único lugar onde dá, porque o passe que mede já terminou e não roda de
                  novo no editor. O deslocamento medido (para onde a pessoa andou) é
                  preservado: o controle move o trajeto inteiro, não achata o tracking. */}
              {(editElementsAtivos.zoomCuts || editElementsAtivos.zoomAuto) && editElementsAtivos.tracking && (
                <div>
                  <FieldLabel>Centro do zoom neste clipe</FieldLabel>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={15}
                      max={85}
                      value={Math.round((selectedClip.zoomCenter?.centerX ?? 0.5) * 100)}
                      onChange={event => {
                        const novo = Number(event.target.value) / 100;
                        const atual = selectedClip.zoomCenter;
                        const inicio = atual?.centerX ?? 0.5;
                        const passeio = atual && atual.centerXEnd != null ? atual.centerXEnd - inicio : null;
                        updateClip(selectedClip.id, {
                          zoomCenter: {
                            centerX: +novo.toFixed(4),
                            centerXEnd: passeio == null
                              ? null
                              : +Math.min(1, Math.max(0, novo + passeio)).toFixed(4),
                            concentracao: atual?.concentracao ?? null,
                          },
                        });
                      }}
                      className="flex-1 accent-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => updateClip(selectedClip.id, {
                        zoomCenter: { centerX: 0.5, centerXEnd: null, concentracao: null },
                      })}
                      className="rounded-lg bg-white/5 px-2 py-1.5 text-[10px] font-black text-slate-300"
                    >
                      Centralizar
                    </button>
                  </div>
                  <p className="mt-1 text-[9px] leading-snug text-slate-500">
                    {selectedClip.zoomCenter
                      ? `Fecha em ${Math.round((selectedClip.zoomCenter.centerX) * 100)}% da largura`
                      : 'Sem medição neste clipe: fecha no meio, parado.'}
                    {selectedClip.zoomCenter && selectedClip.zoomCenter.centerXEnd != null
                      && Math.abs(selectedClip.zoomCenter.centerXEnd - selectedClip.zoomCenter.centerX) >= 0.005
                      ? ` e acompanha até ${Math.round(selectedClip.zoomCenter.centerXEnd * 100)}%.`
                      : '.'}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 pt-2">
                <button onClick={splitClip} className="rounded-lg bg-indigo-500/15 px-2 py-2 text-[10px] font-black text-indigo-300">Dividir</button>
                <button onClick={duplicateClip} className="rounded-lg bg-white/5 px-2 py-2 text-[10px] font-black text-slate-300">Duplicar</button>
                <button onClick={removeSelection} disabled={project.clips.length <= 1} className="rounded-lg bg-rose-500/10 px-2 py-2 text-[10px] font-black text-rose-300 disabled:opacity-30">Excluir</button>
              </div>
              {selectedClip.aiReason && <div className="rounded-xl border border-indigo-400/15 bg-indigo-400/5 p-3"><p className="text-[9px] font-black uppercase text-indigo-300">Decisão da IA</p><p className="mt-1 text-[10px] leading-relaxed text-slate-400">{selectedClip.aiReason}</p></div>}
            </div>
          ) : overlayLoteAtivo && loteReferencia ? (
            // Inspetor em LOTE. Aqui só entram campos que fazem sentido para
            // várias camadas ao mesmo tempo: o TEXTO e os TEMPOS são de cada uma
            // (aplicar o mesmo texto a 40 legendas apagaria a fala inteira), então
            // ficam de fora e o painel avisa o porquê.
            <div className="space-y-4">
              <div>
                <p className="text-xs font-black">{selectedTextOverlays.length} legendas selecionadas</p>
                <p className="text-[10px] text-slate-500">O ajuste vale para todas de uma vez — e um Ctrl+Z desfaz tudo junto.</p>
              </div>
              {captionOverlayIds.length > 1 && (
                <button
                  onClick={selecionarTodasLegendas}
                  className="w-full rounded-lg bg-indigo-500/15 py-2 text-[10px] font-black text-indigo-300 hover:bg-indigo-500/25"
                >
                  Selecionar todas as legendas ({captionOverlayIds.length})
                </button>
              )}
              <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                <FieldLabel>Posição no quadro</FieldLabel>
                <p className="mb-2 text-[9px] leading-relaxed text-slate-500">
                  O valor é aplicado igual em todas. Para preservar as distâncias entre elas, arraste uma no vídeo — o grupo inteiro acompanha.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <NumberInput label="Altura (Y)" value={loteReferencia.y} min={0} max={1} step={0.005} onChange={value => updateOverlays(selectedTextOverlayIds, { y: value })} />
                  <NumberInput label="Horizontal (X)" value={loteReferencia.x} min={0} max={1} step={0.005} onChange={value => updateOverlays(selectedTextOverlayIds, { x: value })} />
                </div>
              </div>
              <div>
                <FieldLabel>Fonte</FieldLabel>
                <select value={loteReferencia.fontFamily} onChange={event => updateOverlays(selectedTextOverlayIds, { fontFamily: event.target.value })} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-2 text-xs" style={{ fontFamily: loteReferencia.fontFamily }}>
                  {fontOptions.map(font => <option key={font} style={{ fontFamily: font }}>{font}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <NumberInput label="Corpo" value={loteReferencia.fontSize} min={12} max={220} step={1} onChange={value => updateOverlays(selectedTextOverlayIds, { fontSize: value })} />
                <NumberInput label="Largura" value={loteReferencia.width} min={0.1} max={1} step={0.05} onChange={value => updateOverlays(selectedTextOverlayIds, { width: value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><FieldLabel>Cor do texto</FieldLabel><input type="color" value={loteReferencia.color} onChange={event => updateOverlays(selectedTextOverlayIds, { color: event.target.value })} className="h-9 w-full rounded-lg border border-slate-700 bg-slate-900 p-1" /></div>
                <div><FieldLabel>Cor do contorno</FieldLabel><input type="color" value={loteReferencia.outlineColor} onChange={event => updateOverlays(selectedTextOverlayIds, { outlineColor: event.target.value })} className="h-9 w-full rounded-lg border border-slate-700 bg-slate-900 p-1" /></div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                <div className="mb-2 flex items-center justify-between"><FieldLabel>Contorno</FieldLabel><span className="text-[9px] text-slate-500">{loteReferencia.outlineWidth.toFixed(1)}px · {Math.round(loteReferencia.outlineOpacity * 100)}%</span></div>
                <input type="range" min={0} max={30} step={0.5} value={loteReferencia.outlineWidth} onChange={event => updateOverlays(selectedTextOverlayIds, { outlineWidth: Number(event.target.value) })} className="w-full accent-indigo-500" aria-label="Largura do contorno em lote" />
                <input type="range" min={0} max={1} step={0.05} value={loteReferencia.outlineOpacity} onChange={event => updateOverlays(selectedTextOverlayIds, { outlineOpacity: Number(event.target.value) })} className="mt-2 w-full accent-indigo-300" aria-label="Opacidade do contorno em lote" />
              </div>
              <div className="flex gap-2">
                {/* B DESABILITADO quando o peso é do estilo (ver
                    pesoDoEstiloTravaNegrito) — a MESMA guarda do inspetor de uma
                    camada, que faltava aqui. Este painel é o único caminho de
                    "Selecionar todas as legendas", isto é, exatamente as legendas da
                    IA, que TODAS carregam `fontWeight`: era o lugar onde o botão
                    mudo era mais fácil de clicar. MEDIDO no .ass de produção
                    (legenda `simples`, Poppins 600): com B ligado e com B desligado
                    o arquivo sai BYTE A BYTE idêntico — `\fnPoppins SemiBold` e
                    `\b0` nos dois casos, porque o peso explícito vence o booleano ao
                    escolher a face e `pesoNoNome` derruba o `\b`. Na prévia é o
                    mesmo (`run.fontWeight || (run.bold ? 900 : 400)`). */}
                <button
                  onClick={() => updateOverlays(selectedTextOverlayIds, { bold: !loteReferencia.bold })}
                  disabled={loteTravaNegrito}
                  title={loteTravaNegrito
                    ? 'O peso vem do estilo. Troque em “Estilo de legenda/gancho”, no painel do projeto.'
                    : 'Negrito'}
                  className={`flex-1 rounded-lg py-2 text-xs font-black disabled:cursor-not-allowed disabled:opacity-30 ${loteReferencia.bold ? 'bg-indigo-500 text-white' : 'bg-white/5'}`}
                >B</button>
                <button onClick={() => updateOverlays(selectedTextOverlayIds, { italic: !loteReferencia.italic })} className={`flex-1 rounded-lg py-2 text-xs italic ${loteReferencia.italic ? 'bg-indigo-500 text-white' : 'bg-white/5'}`}>I</button>
                {(['left', 'center', 'right'] as const).map(align => <button key={align} onClick={() => updateOverlays(selectedTextOverlayIds, { align })} className={`flex-1 rounded-lg py-2 text-[10px] ${loteReferencia.align === align ? 'bg-indigo-500 text-white' : 'bg-white/5'}`}>{align === 'left' ? '≡←' : align === 'center' ? '≡' : '→≡'}</button>)}
              </div>
              {loteTravaNegrito && (
                <p className="-mt-2 text-[9px] leading-snug text-slate-500">
                  O negrito {selectedTextOverlays.length > 1 ? 'destes textos vem' : 'deste texto vem'} do peso do estilo (a tabela que o vídeo
                  desenha), não do botão B. Para mudar, troque o estilo em{' '}
                  <strong className="text-slate-400">Estilo de legenda</strong> ou{' '}
                  <strong className="text-slate-400">Estilo de gancho</strong>, no painel do projeto.
                </p>
              )}
              <div className="grid grid-cols-2 gap-2">
                <NumberInput label="Opacidade" value={loteReferencia.opacity} min={0} max={1} step={0.05} onChange={value => updateOverlays(selectedTextOverlayIds, { opacity: value })} />
                <NumberInput label="Rotação" value={loteReferencia.rotation} min={-180} max={180} step={1} onChange={value => updateOverlays(selectedTextOverlayIds, { rotation: value })} />
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                <p className="text-[9px] leading-relaxed text-slate-500">
                  Conteúdo, início, fim e efeito sonoro são de cada legenda — clique numa só para editá-los.
                </p>
              </div>
              <button onClick={removeSelection} className="w-full rounded-lg bg-rose-500/10 py-2 text-[10px] font-black text-rose-300">Remover {selectedTextOverlays.length} camadas</button>
            </div>
          ) : selectedOverlay?.type === 'text' ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between"><div><p className="text-xs font-black">Texto / overlay</p><p className="text-[10px] text-slate-500">Arraste diretamente no vídeo</p></div><button onClick={() => updateOverlay(selectedOverlay.id, { locked: !selectedOverlay.locked })} className="text-xs">{selectedOverlay.locked ? '🔒' : '🔓'}</button></div>
              {captionOverlayIds.length > 1 && (
                <button
                  onClick={selecionarTodasLegendas}
                  className="w-full rounded-lg bg-indigo-500/15 py-2 text-[10px] font-black text-indigo-300 hover:bg-indigo-500/25"
                >
                  Selecionar todas as legendas ({captionOverlayIds.length})
                </button>
              )}
              <div><FieldLabel>Conteúdo</FieldLabel><textarea value={selectedOverlay.text} rows={3} onChange={event => updateOverlay(selectedOverlay.id, { text: event.target.value })} className="w-full resize-none rounded-lg border border-slate-700 bg-slate-900 p-2.5 text-xs outline-none focus:border-indigo-500" /></div>
              <div>
                <FieldLabel>Fonte</FieldLabel>
                <select value={selectedOverlay.fontFamily} onChange={event => updateOverlay(selectedOverlay.id, { fontFamily: event.target.value })} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-2 text-xs" style={{ fontFamily: selectedOverlay.fontFamily }}>
                  {fontOptions.map(font => <option key={font} style={{ fontFamily: font }}>{font}</option>)}
                </select>
                <p className="mt-1 text-[9px] text-slate-600">Muda a frase inteira. Para variar palavra a palavra, use a lista abaixo.</p>
              </div>

              {/* Edição por palavra. O texto da IA vem com tipografia diferente
                  em cada palavra — é isso que dá o desenho ao bloco. Sem este
                  painel, qualquer ajuste de fonte no overlay achatava tudo numa
                  fonte só e não havia como desfazer palavra a palavra. */}
              {(selectedOverlay.textRuns?.length || 0) > 1 && (
                <div>
                  <FieldLabel>Palavras</FieldLabel>
                  <div className="space-y-1.5">
                    {selectedOverlay.textRuns!.map((run, index) => (
                      <div key={`${selectedOverlay.id}-run-edit-${index}`} className="rounded-lg border border-white/10 bg-white/[0.02] p-1.5">
                        <p className="mb-1 truncate text-[10px] font-black text-slate-300" style={{ fontFamily: run.fontFamily }}>
                          {run.text}
                        </p>
                        <div className="flex items-center gap-1">
                          <select
                            value={run.fontFamily}
                            onChange={event => updateTextRun(selectedOverlay.id, index, { fontFamily: event.target.value })}
                            className="min-w-0 flex-1 rounded border border-slate-700 bg-slate-900 px-1.5 py-1 text-[10px]"
                            style={{ fontFamily: run.fontFamily }}
                            aria-label={`Fonte da palavra ${run.text}`}
                          >
                            {fontOptions.map(font => <option key={font} style={{ fontFamily: font }}>{font}</option>)}
                          </select>
                          <input
                            type="color"
                            value={run.color?.slice(0, 7) || '#FFFFFF'}
                            onChange={event => updateTextRun(selectedOverlay.id, index, { color: event.target.value })}
                            className="h-6 w-8 shrink-0 rounded border border-slate-700 bg-slate-900 p-0.5"
                            aria-label={`Cor da palavra ${run.text}`}
                          />
                          <button
                            type="button"
                            onClick={() => updateTextRun(selectedOverlay.id, index, { italic: !run.italic })}
                            className={`h-6 w-6 shrink-0 rounded border text-[10px] italic ${run.italic ? 'border-indigo-400 bg-indigo-500/30 text-white' : 'border-slate-700 text-slate-400'}`}
                            title="Itálico"
                          >
                            I
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <NumberInput label="Tamanho" value={selectedOverlay.fontSize} min={12} max={220} step={1} onChange={value => updateOverlay(selectedOverlay.id, { fontSize: value })} />
                <NumberInput label="Largura" value={selectedOverlay.width} min={0.1} max={1} step={0.05} onChange={value => updateOverlay(selectedOverlay.id, { width: value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <FieldLabel>Caixa do texto</FieldLabel>
                  <select value={selectedOverlay.textCase} onChange={event => updateOverlay(selectedOverlay.id, { textCase: event.target.value as VideoEditorTextOverlay['textCase'] })} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-2 text-xs">
                    <option value="preserve">Como digitado</option>
                    <option value="upper">MAIÚSCULAS</option>
                    <option value="lower">minúsculas</option>
                    <option value="title">Título</option>
                  </select>
                </div>
                <NumberInput label="Entreletras" value={selectedOverlay.letterSpacing} min={-10} max={40} step={0.5} onChange={value => updateOverlay(selectedOverlay.id, { letterSpacing: value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><FieldLabel>Cor do texto</FieldLabel><input type="color" value={selectedOverlay.color} onChange={event => updateOverlay(selectedOverlay.id, { color: event.target.value })} className="h-9 w-full rounded-lg border border-slate-700 bg-slate-900 p-1" /></div>
                <div><FieldLabel>Cor do contorno</FieldLabel><input type="color" value={selectedOverlay.outlineColor} onChange={event => updateOverlay(selectedOverlay.id, { outlineColor: event.target.value })} className="h-9 w-full rounded-lg border border-slate-700 bg-slate-900 p-1" /></div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                <div className="mb-2 flex items-center justify-between"><FieldLabel>Contorno</FieldLabel><span className="text-[9px] text-slate-500">{selectedOverlay.outlineWidth.toFixed(1)}px · {Math.round(selectedOverlay.outlineOpacity * 100)}%</span></div>
                <input type="range" min={0} max={30} step={0.5} value={selectedOverlay.outlineWidth} onChange={event => updateOverlay(selectedOverlay.id, { outlineWidth: Number(event.target.value) })} className="w-full accent-indigo-500" aria-label="Largura do contorno" />
                <input type="range" min={0} max={1} step={0.05} value={selectedOverlay.outlineOpacity} onChange={event => updateOverlay(selectedOverlay.id, { outlineOpacity: Number(event.target.value) })} className="mt-2 w-full accent-indigo-300" aria-label="Opacidade do contorno" />
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                <div className="grid grid-cols-2 gap-2">
                  <div><FieldLabel>Cor da sombra</FieldLabel><input type="color" value={selectedOverlay.shadowColor} onChange={event => updateOverlay(selectedOverlay.id, { shadowColor: event.target.value })} className="h-9 w-full rounded-lg border border-slate-700 bg-slate-900 p-1" /></div>
                  <NumberInput label="Sombra X" value={selectedOverlay.shadowOffsetX} min={-50} max={50} step={1} onChange={value => updateOverlay(selectedOverlay.id, { shadowOffsetX: value })} />
                  <NumberInput label="Sombra Y" value={selectedOverlay.shadowOffsetY} min={-50} max={50} step={1} onChange={value => updateOverlay(selectedOverlay.id, { shadowOffsetY: value })} />
                </div>
                <div className="mt-2"><FieldLabel>Sombra · {Math.round(selectedOverlay.shadowOpacity * 100)}%</FieldLabel><input type="range" min={0} max={1} step={0.05} value={selectedOverlay.shadowOpacity} onChange={event => updateOverlay(selectedOverlay.id, { shadowOpacity: Number(event.target.value) })} className="w-full accent-violet-500" /></div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                <div><FieldLabel>Cor do fundo</FieldLabel><input type="color" value={selectedOverlay.backgroundColor} onChange={event => updateOverlay(selectedOverlay.id, { backgroundColor: event.target.value })} className="h-9 w-full rounded-lg border border-slate-700 bg-slate-900 p-1" /></div>
                <div className="mt-2"><FieldLabel>Fundo · {Math.round(selectedOverlay.backgroundOpacity * 100)}%</FieldLabel><input type="range" min={0} max={1} step={0.05} value={selectedOverlay.backgroundOpacity} onChange={event => updateOverlay(selectedOverlay.id, { backgroundOpacity: Number(event.target.value) })} className="w-full accent-fuchsia-500" /></div>
                <div className="mt-2"><NumberInput label="Respiro" value={selectedOverlay.backgroundPadding} min={0} max={60} step={1} onChange={value => updateOverlay(selectedOverlay.id, { backgroundPadding: value })} /></div>
              </div>
              <div className="flex gap-2">
                {/* B DESABILITADO quando o peso é do estilo (ver
                    pesoDoEstiloTravaNegrito): o botão mexia o estado e não mudava
                    nem a tela nem o MP4. O rótulo aponta onde a decisão vive, no
                    mesmo espírito do aviso da transição do clipe. */}
                <button
                  onClick={() => updateOverlay(selectedOverlay.id, { bold: !selectedOverlay.bold })}
                  disabled={pesoDoEstiloTravaNegrito(selectedOverlay)}
                  title={pesoDoEstiloTravaNegrito(selectedOverlay)
                    ? 'O peso vem do estilo. Troque em “Estilo de legenda/gancho”, no painel do projeto.'
                    : 'Negrito'}
                  className={`flex-1 rounded-lg py-2 text-xs font-black disabled:cursor-not-allowed disabled:opacity-30 ${selectedOverlay.bold ? 'bg-indigo-500 text-white' : 'bg-white/5'}`}
                >B</button>
                <button onClick={() => updateOverlay(selectedOverlay.id, { italic: !selectedOverlay.italic })} className={`flex-1 rounded-lg py-2 text-xs italic ${selectedOverlay.italic ? 'bg-indigo-500 text-white' : 'bg-white/5'}`}>I</button>
                {(['left', 'center', 'right'] as const).map(align => <button key={align} onClick={() => updateOverlay(selectedOverlay.id, { align })} className={`flex-1 rounded-lg py-2 text-[10px] ${selectedOverlay.align === align ? 'bg-indigo-500 text-white' : 'bg-white/5'}`}>{align === 'left' ? '≡←' : align === 'center' ? '≡' : '→≡'}</button>)}
              </div>
              {pesoDoEstiloTravaNegrito(selectedOverlay) && (
                <p className="-mt-2 text-[9px] leading-snug text-slate-500">
                  O negrito deste texto vem do peso do estilo (a tabela que o vídeo
                  desenha), não do botão B. Para mudar, troque o estilo em{' '}
                  <strong className="text-slate-400">Estilo de legenda</strong> ou{' '}
                  <strong className="text-slate-400">Estilo de gancho</strong>, no painel do projeto.
                </p>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <FieldLabel>Entrada</FieldLabel>
                  <select value={selectedOverlay.animation} onChange={event => updateOverlay(selectedOverlay.id, { animation: event.target.value as VideoEditorOverlayAnimation })} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-2 text-xs">
                    {ANIMATION_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
                <div>
                  <FieldLabel>Saída</FieldLabel>
                  <select value={selectedOverlay.exitAnimation || 'none'} onChange={event => updateOverlay(selectedOverlay.id, { exitAnimation: event.target.value as VideoEditorOverlayAnimation })} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-2 text-xs">
                    {ANIMATION_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
              </div>
              <SoundEffectControls
                soundEffects={soundEffects}
                value={selectedOverlay.soundEffectId}
                volume={selectedOverlay.soundEffectVolume ?? 0.7}
                previewingId={previewingSoundEffectId}
                onChange={(soundEffectId, defaultVolume) => updateOverlay(selectedOverlay.id, {
                  soundEffectId,
                  soundEffectVolume: soundEffectId ? (defaultVolume ?? selectedOverlay.soundEffectVolume ?? 0.7) : selectedOverlay.soundEffectVolume,
                })}
                onVolumeChange={soundEffectVolume => updateOverlay(selectedOverlay.id, { soundEffectVolume })}
                onPreview={previewSoundEffect}
              />
              <div className="grid grid-cols-2 gap-2">
                <NumberInput label="Início" value={selectedOverlay.start} min={0} max={selectedOverlay.end - 0.05} onChange={value => updateOverlay(selectedOverlay.id, { start: value })} />
                <NumberInput label="Fim" value={selectedOverlay.end} min={selectedOverlay.start + 0.05} max={project.duration} onChange={value => updateOverlay(selectedOverlay.id, { end: value })} />
                <NumberInput label="Rotação" value={selectedOverlay.rotation} min={-180} max={180} step={1} onChange={value => updateOverlay(selectedOverlay.id, { rotation: value })} />
                <NumberInput label="Opacidade" value={selectedOverlay.opacity} min={0} max={1} step={0.05} onChange={value => updateOverlay(selectedOverlay.id, { opacity: value })} />
              </div>
              <button onClick={removeSelection} className="w-full rounded-lg bg-rose-500/10 py-2 text-[10px] font-black text-rose-300">Remover overlay</button>
            </div>
          ) : selectedInsert ? (
            /* ── Inserção de tela dividida ────────────────────────────────────
               Três controles, e só três: MOMENTO, LAYOUT e apagar. Não há posição,
               tamanho, rotação, opacidade nem animação porque a geometria da tela
               dividida é medida (faixa de 750px, janela de 1170, par zoom/foco por
               layout) — deixar a pessoa mexer nela decapitaria o sujeito. E não há
               fade: a janela entra e sai em corte seco.
               Também não há "regerar imagem": isso custa créditos e exigiria o
               modal de confirmação de crédito antes, que ainda não existe aqui. */
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-black">Tela dividida</p>
                  <p className="text-[10px] text-slate-500">Arraste a tarja na régua para mover no tempo.</p>
                </div>
                <button onClick={() => setSelectedInsertId(null)} className="text-[10px] font-black text-slate-500">fechar</button>
              </div>
              <div className="overflow-hidden rounded-xl border border-white/10 bg-black/30 p-2">
                {assetUrls[selectedInsert.assetId] ? (
                  <img src={assetUrls[selectedInsert.assetId]} alt={selectedInsert.subject} className="mx-auto max-h-32 max-w-full object-contain" />
                ) : <div className="h-24 animate-pulse rounded-lg bg-white/5" />}
              </div>
              <div>
                <FieldLabel>Assunto escolhido pela IA</FieldLabel>
                <p className="rounded-lg border border-white/10 bg-white/[0.03] p-2 text-[10px] leading-snug text-slate-300">{selectedInsert.subject}</p>
                {selectedInsert.aiReason && (
                  <p className="mt-1.5 text-[10px] leading-snug text-slate-500">{selectedInsert.aiReason}</p>
                )}
              </div>
              <div>
                <FieldLabel>Onde fica a imagem</FieldLabel>
                <div className="grid grid-cols-2 gap-2">
                  {(['top', 'bottom'] as const).map(opcao => (
                    <button
                      key={opcao}
                      onClick={() => { if (selectedInsert.layout !== opcao) toggleInsertLayout(selectedInsert.id); }}
                      aria-pressed={selectedInsert.layout === opcao}
                      className={`rounded-lg border px-2 py-2 text-[10px] font-black ${selectedInsert.layout === opcao ? 'border-sky-300 bg-sky-500/25 text-sky-100' : 'border-slate-700 bg-slate-900 text-slate-400'}`}
                    >
                      {opcao === 'top' ? '▀ Imagem em cima' : '▄ Imagem embaixo'}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-[10px] leading-snug text-slate-500">
                  Em cima, você aparece embaixo da imagem com o plano mais fechado. Embaixo, você
                  fica no alto do quadro e a imagem entra por baixo.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <NumberInput
                  label="Início"
                  value={selectedInsert.start}
                  min={0}
                  max={Math.max(0, selectedInsert.end - 1.2)}
                  onChange={value => commit(draft => {
                    const duracao = selectedInsert.end - selectedInsert.start;
                    draft.imageInserts = (draft.imageInserts || []).map(item => (
                      item.id === selectedInsert.id ? { ...item, start: value, end: value + duracao } : item
                    ));
                  })}
                />
                <NumberInput
                  label="Fim"
                  value={selectedInsert.end}
                  min={selectedInsert.start + 1.2}
                  max={project.duration}
                  onChange={value => commit(draft => {
                    draft.imageInserts = (draft.imageInserts || []).map(item => (
                      item.id === selectedInsert.id ? { ...item, end: value } : item
                    ));
                  })}
                />
              </div>
              <p className="rounded-lg border border-amber-400/20 bg-amber-500/10 p-1.5 text-[9px] leading-snug text-amber-200">
                <strong>Apagar a inserção não devolve crédito.</strong> Ela já foi gerada e cobrada
                quando a IA montou o vídeo. Exportar de novo reaproveita a mesma imagem — não gera
                nem cobra nada.
              </p>
              <button onClick={() => removeInsert(selectedInsert.id)} className="w-full rounded-lg bg-rose-500/10 py-2 text-[10px] font-black text-rose-300">Remover inserção</button>
            </div>
          ) : selectedImageOverlay ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div><p className="text-xs font-black">Imagem / camada</p><p className="text-[10px] text-slate-500">Arraste a imagem diretamente no canvas.</p></div>
                <button onClick={() => updateImageOverlay(selectedImageOverlay.id, { locked: !selectedImageOverlay.locked })} className="text-xs">{selectedImageOverlay.locked ? '🔒' : '🔓'}</button>
              </div>
              <div className="overflow-hidden rounded-xl border border-white/10 bg-black/30 p-2">
                {assetUrls[selectedImageOverlay.assetId] ? (
                  <img src={assetUrls[selectedImageOverlay.assetId]} alt={selectedImageOverlay.name} className="mx-auto max-h-32 max-w-full object-contain" />
                ) : <div className="h-24 animate-pulse rounded-lg bg-white/5" />}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <NumberInput label="Início" value={selectedImageOverlay.start} min={0} max={selectedImageOverlay.end - 0.05} onChange={value => updateImageOverlay(selectedImageOverlay.id, { start: value })} />
                <NumberInput label="Fim" value={selectedImageOverlay.end} min={selectedImageOverlay.start + 0.05} max={project.duration} onChange={value => updateImageOverlay(selectedImageOverlay.id, { end: value })} />
                <NumberInput label="Posição X" value={selectedImageOverlay.x} min={0} max={1} step={0.01} onChange={value => updateImageOverlay(selectedImageOverlay.id, { x: value })} />
                <NumberInput label="Posição Y" value={selectedImageOverlay.y} min={0} max={1} step={0.01} onChange={value => updateImageOverlay(selectedImageOverlay.id, { y: value })} />
                <NumberInput label="Tamanho" value={selectedImageOverlay.width} min={0.05} max={1} step={0.01} onChange={value => updateImageOverlay(selectedImageOverlay.id, { width: value })} />
                <NumberInput label="Rotação" value={selectedImageOverlay.rotation} min={-180} max={180} step={1} onChange={value => updateImageOverlay(selectedImageOverlay.id, { rotation: value })} />
              </div>
              <div>
                <FieldLabel>Opacidade · {Math.round(selectedImageOverlay.opacity * 100)}%</FieldLabel>
                <input type="range" min={0} max={1} step={0.05} value={selectedImageOverlay.opacity} onChange={event => updateImageOverlay(selectedImageOverlay.id, { opacity: Number(event.target.value) })} className="w-full accent-indigo-500" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <FieldLabel>Entrada</FieldLabel>
                  <select value={selectedImageOverlay.animation} onChange={event => updateImageOverlay(selectedImageOverlay.id, { animation: event.target.value as VideoEditorOverlayAnimation })} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-2 text-xs">
                    {ANIMATION_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
                <div>
                  <FieldLabel>Saída</FieldLabel>
                  <select value={selectedImageOverlay.exitAnimation || 'none'} onChange={event => updateImageOverlay(selectedImageOverlay.id, { exitAnimation: event.target.value as VideoEditorOverlayAnimation })} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-2 text-xs">
                    {ANIMATION_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
              </div>
              <SoundEffectControls
                soundEffects={soundEffects}
                value={selectedImageOverlay.soundEffectId}
                volume={selectedImageOverlay.soundEffectVolume ?? 0.7}
                previewingId={previewingSoundEffectId}
                onChange={(soundEffectId, defaultVolume) => updateImageOverlay(selectedImageOverlay.id, {
                  soundEffectId,
                  soundEffectVolume: soundEffectId ? (defaultVolume ?? selectedImageOverlay.soundEffectVolume ?? 0.7) : selectedImageOverlay.soundEffectVolume,
                })}
                onVolumeChange={soundEffectVolume => updateImageOverlay(selectedImageOverlay.id, { soundEffectVolume })}
                onPreview={previewSoundEffect}
              />
              <button onClick={removeSelection} className="w-full rounded-lg bg-rose-500/10 py-2 text-[10px] font-black text-rose-300">Remover imagem</button>
            </div>
          ) : (
            <div className="space-y-4">
              <div><p className="text-xs font-black">Projeto</p><p className="text-[10px] text-slate-500">Selecione um clipe, texto ou imagem para editar.</p></div>
              <div><FieldLabel>Fundo do canvas</FieldLabel><input type="color" value={project.backgroundColor} onChange={event => commit(draft => { draft.backgroundColor = event.target.value; })} className="h-10 w-full rounded-lg border border-slate-700 bg-slate-900 p-1" /></div>
              <StylePanel
                captionStyle={estilosEmUso.captionStyle}
                headlineStyle={estilosEmUso.headlineStyle}
                accent={estilosEmUso.accent}
                legendas={estilosEmUso.legendas}
                ganchos={estilosEmUso.ganchos}
                onCaptionStyle={estilo => trocarEstilo('captionStyle', estilo)}
                onHeadlineStyle={estilo => trocarEstilo('headlineStyle', estilo)}
                onAccent={trocarAccent}
              />
              <EditElementsPanel
                achatado={project.sourceMode === 'flattened'}
                value={editElementsAtivos}
                onChange={patch => commit(draft => {
                  draft.editElements = { ...EDIT_ELEMENTS_DEFAULT, ...(draft.editElements || {}), ...patch };
                })}
              />
              <button onClick={addText} className="w-full rounded-xl bg-fuchsia-500 px-3 py-2.5 text-xs font-black text-white">Adicionar texto</button>
            </div>
          )}
        </aside>
      </main>

      <section className="h-[260px] shrink-0 border-t border-white/10 bg-slate-950">
        <div className="flex h-12 items-center gap-2 border-b border-white/10 px-3">
          <button onClick={splitClip} className="rounded-lg border border-white/10 px-2.5 py-1.5 text-[10px] font-black hover:bg-white/10">✂ Dividir</button>
          <button onClick={duplicateClip} disabled={!selectedClipIds.length} className="rounded-lg border border-white/10 px-2.5 py-1.5 text-[10px] font-black hover:bg-white/10 disabled:opacity-30">Duplicar{selectedClipIds.length > 1 ? ` (${selectedClipIds.length})` : ''}</button>
          <button onClick={removeSelection} disabled={!selectedItems.length} className="rounded-lg border border-rose-400/20 px-2.5 py-1.5 text-[10px] font-black text-rose-300 disabled:opacity-30">Excluir{selectedItems.length > 1 ? ` (${selectedItems.length})` : ''}</button>
          {selectedItems.length > 1 && (
            <span className="rounded-lg bg-indigo-500/20 px-2 py-1 text-[10px] font-black text-indigo-200">{selectedItems.length} selecionados</span>
          )}
          <span className="hidden text-[10px] text-slate-500 xl:inline">Ctrl+clique soma · Shift+clique pega a faixa · arraste no vazio para selecionar vários</span>
          <div className="ml-auto flex items-center gap-2 text-[10px] text-slate-500"><span>Zoom</span><input type="range" min={18} max={100} value={zoom} onChange={event => setZoom(Number(event.target.value))} className="w-24 accent-indigo-500" /></div>
        </div>
        <div className="grid grid-cols-[110px_1fr]" style={{ height: imageInserts.length > 0 ? 242 : 208 }}>
          <div className="border-r border-white/10 bg-slate-950">
            <div className="h-7 border-b border-white/10" />
            <div className="flex h-[78px] items-center border-b border-white/10 px-3 text-[10px] font-black text-slate-400">🎬 VÍDEO</div>
            <div className="flex h-[62px] items-center border-b border-white/10 px-3 text-[10px] font-black text-slate-400">T CAMADAS</div>
            {imageInserts.length > 0 && (
              <div className="flex h-[34px] items-center border-b border-white/10 px-3 text-[10px] font-black text-sky-300/80" title="Inserções de imagem em tela dividida">▀ TELA DIV.</div>
            )}
            <div className="flex h-10 items-center px-3 text-[10px] font-black text-slate-500">🔊 ÁUDIO</div>
          </div>
          <div className="overflow-x-auto overflow-y-hidden" data-testid="video-timeline">
            <div ref={timelineContentRef} className="relative h-full" style={{ width: timelineWidth }}>
              <div className="relative h-7 cursor-ew-resize touch-none border-b border-white/10 bg-white/[0.02]" onPointerDown={startPlayheadDrag} title="Clique ou arraste para navegar">
                {Array.from({ length: Math.ceil(project.duration) + 1 }, (_, second) => (
                  <span key={second} className="absolute top-0 h-7 border-l border-white/10 pl-1 pt-1 font-mono text-[8px] text-slate-600" style={{ left: second * zoom }}>{second % 5 === 0 ? `${second}s` : ''}</span>
                ))}
              </div>
              <div
                className="relative h-[78px] border-b border-white/10"
                onPointerDown={event => startMarquee(event, 'clip')}
                onDragOver={event => {
                  if (!draggedAssetId) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'copy';
                }}
                onDrop={event => {
                  if (!draggedAssetId) return;
                  event.preventDefault();
                  event.stopPropagation();
                  const asset = project.assets.find(item => item.id === draggedAssetId);
                  if (asset) addAssetAsClip(asset);
                  setDraggedAssetId(null);
                }}
              >
                {project.clips.map((clip, index) => (
                  <div
                    key={clip.id}
                    data-timeline-item="true"
                    draggable
                    onDragStart={() => {
                      // Arrastar um clipe que não está na seleção passa a seleção
                      // para ele; arrastar um selecionado leva o grupo inteiro.
                      if (!isSelected('clip', clip.id)) setSelection({ kind: 'clip', id: clip.id });
                      setDraggedClipId(clip.id);
                    }}
                    onDragOver={event => event.preventDefault()}
                    onDrop={() => reorderClip(clip.id)}
                    onClick={event => {
                      const single = handleTimelineItemClick(event, 'clip', clip.id, project.clips.map(item => item.id));
                      if (single) setPlayhead(clip.timelineStart + 0.001);
                    }}
                    className={`absolute top-2 flex h-[62px] cursor-grab items-center overflow-hidden rounded-lg border px-3 text-left shadow-lg ${isSelected('clip', clip.id) ? 'border-indigo-300 bg-indigo-500/40 ring-1 ring-indigo-300' : index % 2 ? 'border-cyan-400/30 bg-cyan-500/25' : 'border-indigo-400/30 bg-indigo-500/25'}`}
                    style={{ left: clip.timelineStart * zoom, width: Math.max(28, clip.duration * zoom) }}
                    title={`${clip.name} · arraste para reordenar (Ctrl+clique para selecionar vários)`}
                  >
                    {(() => {
                      const asset = project.assets.find(item => item.id === clip.assetId);
                      const strip = asset && assetGraphics[`${asset.id}:filmstrip`];
                      if (!strip || !asset?.duration) return null;
                      return (
                        <div
                          aria-hidden
                          className="pointer-events-none absolute inset-0 opacity-40"
                          style={mediaBackdropStyle(strip, clip, asset.duration, zoom)}
                        />
                      );
                    })()}
                    <button type="button" onPointerDown={event => startTrim(event, clip, 'start')} className="absolute inset-y-0 left-0 z-10 w-2 cursor-ew-resize bg-white/15 hover:bg-white/35" aria-label="Aparar início" />
                    <div className="relative min-w-0"><p className="truncate text-[10px] font-black drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">{clip.name}</p><p className="text-[8px] text-slate-200/80 drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">{clip.playbackRate}x · {clip.muted ? 'sem áudio' : `${Math.round(clip.volume * 100)}%`}</p></div>
                    <button type="button" onPointerDown={event => startTrim(event, clip, 'end')} className="absolute inset-y-0 right-0 z-10 w-2 cursor-ew-resize bg-white/15 hover:bg-white/35" aria-label="Aparar fim" />
                  </div>
                ))}
              </div>
              <div className="relative h-[62px] border-b border-white/10" onPointerDown={event => startMarquee(event, 'overlay')}>
                {project.overlays.map(overlay => (
                  <div
                    key={overlay.id}
                    data-timeline-item="true"
                    role="button"
                    tabIndex={0}
                    onPointerDown={event => {
                      // Ctrl/Shift+clique é intenção de SELEÇÃO: não inicia arraste.
                      if (event.ctrlKey || event.metaKey || event.shiftKey) { event.stopPropagation(); return; }
                      startOverlayMove(event, overlay);
                    }}
                    onClick={event => {
                      const single = handleTimelineItemClick(event, 'overlay', overlay.id, project.overlays.map(item => item.id));
                      if (single) setPlayhead(overlay.start);
                    }}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        setSelection({ kind: 'overlay', id: overlay.id });
                        setPlayhead(overlay.start);
                      }
                    }}
                    className={`absolute top-2 flex h-11 items-center overflow-hidden rounded-lg border px-3 text-left ${overlay.locked ? 'cursor-not-allowed' : 'cursor-grab'} ${isSelected('overlay', overlay.id) ? 'border-fuchsia-200 bg-fuchsia-500/50 ring-1 ring-fuchsia-200' : overlay.type === 'image' ? 'border-amber-400/30 bg-amber-500/25' : 'border-fuchsia-400/30 bg-fuchsia-500/25'} ${overlay.start >= project.duration ? 'opacity-40 border-dashed' : ''}`}
                    style={{ left: overlay.start * zoom, width: Math.max(28, (overlay.end - overlay.start) * zoom) }}
                    title={`${overlay.name} · arraste para mover no tempo${overlay.locked ? ' (bloqueada)' : ''}${overlay.start >= project.duration ? ' · além do fim do vídeo (não exporta)' : ''}`}
                  >
                    <button
                      type="button"
                      disabled={overlay.locked}
                      onPointerDown={event => startOverlayTrim(event, overlay, 'start')}
                      className="absolute inset-y-0 left-0 w-2 cursor-ew-resize bg-white/15 hover:bg-white/35 disabled:cursor-not-allowed disabled:opacity-30"
                      aria-label={`Aparar início de ${overlay.name}`}
                    />
                    <p className="truncate text-[9px] font-black">{overlay.type === 'text' ? overlay.text : `▧ ${overlay.name}`}</p>
                    <button
                      type="button"
                      disabled={overlay.locked}
                      onPointerDown={event => startOverlayTrim(event, overlay, 'end')}
                      className="absolute inset-y-0 right-0 w-2 cursor-ew-resize bg-white/15 hover:bg-white/35 disabled:cursor-not-allowed disabled:opacity-30"
                      aria-label={`Aparar fim de ${overlay.name}`}
                    />
                  </div>
                ))}
              </div>
              {/* Faixa das inserções de tela dividida. Só existe quando há
                  inserção: uma faixa vazia rouba 34px de altura da régua num
                  projeto que nunca vai ter nenhuma. */}
              {imageInserts.length > 0 && (
                <div className="relative h-[34px] border-b border-white/10">
                  {imageInserts.map(insert => (
                    <div
                      key={insert.id}
                      data-timeline-item="true"
                      role="button"
                      tabIndex={0}
                      onPointerDown={event => startInsertMove(event, insert)}
                      onClick={() => { setSelectedInsertId(insert.id); setPlayhead(insert.start); }}
                      onKeyDown={event => { if (event.key === 'Enter') { setSelectedInsertId(insert.id); setPlayhead(insert.start); } }}
                      className={`absolute top-1 flex h-7 items-center gap-1 overflow-hidden rounded-lg border px-2 text-left ${insert.locked ? 'cursor-not-allowed' : 'cursor-grab'} ${selectedInsertId === insert.id ? 'border-sky-200 bg-sky-500/50 ring-1 ring-sky-200' : 'border-sky-400/30 bg-sky-500/25'}`}
                      style={{ left: insert.start * zoom, width: Math.max(28, (insert.end - insert.start) * zoom) }}
                      title={`Tela dividida (${insert.layout === 'top' ? 'imagem em cima' : 'imagem embaixo'}): ${insert.subject} · arraste para mover no tempo`}
                    >
                      <span className="shrink-0 text-[9px] font-black">{insert.layout === 'top' ? '▀' : '▄'}</span>
                      <p className="truncate text-[9px] font-black">{insert.subject}</p>
                    </div>
                  ))}
                </div>
              )}
              {/* Faixa de marcações: cada tarja é um "aqui está errado" escrito
                  por quem assistiu, ancorado no trecho exato. */}
              <div className="relative h-7 border-b border-white/10">
                {pendingNoteStart !== null && (
                  <div
                    className="absolute top-0 h-full border-l-2 border-amber-300 bg-amber-400/20"
                    style={{
                      left: Math.min(pendingNoteStart, playhead) * zoom,
                      width: Math.max(2, Math.abs(playhead - pendingNoteStart) * zoom),
                    }}
                    title="Marcação em curso — aperte M de novo para fechar, Esc para desistir"
                  />
                )}
                {notes.map(note => (
                  <button
                    key={note.id}
                    type="button"
                    onClick={() => setPlayhead(note.start)}
                    className={`absolute top-1 flex h-5 items-center overflow-hidden rounded border px-1 text-left ${note.status === 'done' ? 'border-emerald-400/40 bg-emerald-500/20 line-through opacity-60' : 'border-amber-300/60 bg-amber-500/30'}`}
                    style={{ left: note.start * zoom, width: Math.max(14, (note.end - note.start) * zoom) }}
                    title={`${note.text} (${formatTime(note.start)}–${formatTime(note.end)})`}
                  >
                    <span className="truncate text-[8px] font-black text-amber-50">{note.text}</span>
                  </button>
                ))}
              </div>
              {/* Faixa de áudio: com a onda desenhada, o usuário VÊ onde é fala e
                  onde é silêncio — que é justamente onde o corte deve cair. Sem
                  ela, ajustar a borda é tentativa e erro com play e volta. */}
              <div className="relative h-10">
                {project.clips.map(clip => {
                  const asset = project.assets.find(item => item.id === clip.assetId);
                  const wave = asset && assetGraphics[`${asset.id}:waveform`];
                  const width = Math.max(20, clip.duration * zoom);
                  if (!wave || !asset?.duration) {
                    return (
                      <div key={clip.id} className={`absolute top-3 h-2 rounded-full ${clip.muted ? 'bg-rose-500/30' : 'bg-emerald-500/35'}`} style={{ left: clip.timelineStart * zoom, width }} />
                    );
                  }
                  return (
                    <div
                      key={clip.id}
                      className={`absolute top-1 h-8 overflow-hidden rounded ${clip.muted ? 'opacity-30 grayscale' : ''}`}
                      style={{ left: clip.timelineStart * zoom, width }}
                      title={clip.muted ? `${clip.name} · sem áudio` : clip.name}
                    >
                      <div className="absolute inset-0 bg-emerald-950/40" />
                      <div className="absolute inset-0" style={mediaBackdropStyle(wave, clip, asset.duration, zoom)} />
                    </div>
                  );
                })}
              </div>
              {marquee && (
                <div
                  className="pointer-events-none absolute z-20 rounded border border-indigo-300/70 bg-indigo-400/20"
                  style={{
                    left: marquee.fromSeconds * zoom,
                    width: Math.max(2, (marquee.toSeconds - marquee.fromSeconds) * zoom),
                    top: marquee.track === 'clip' ? 28 : 106,
                    height: marquee.track === 'clip' ? 78 : 62,
                  }}
                />
              )}
              <div
                className="absolute bottom-0 top-0 z-30 w-5 -translate-x-1/2 cursor-ew-resize touch-none focus:outline-none"
                style={{ left: playhead * zoom }}
                onPointerDown={startPlayheadDrag}
                onKeyDown={event => {
                  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                  event.preventDefault();
                  setPlaying(false);
                  setPlayhead(value => clamp(value + (event.key === 'ArrowLeft' ? -0.1 : 0.1), 0, project.duration));
                }}
                role="slider"
                tabIndex={0}
                data-testid="timeline-playhead"
                aria-label="Cursor de reprodução arrastável"
                aria-valuemin={0}
                aria-valuemax={project.duration}
                aria-valuenow={playhead}
              >
                <div className="pointer-events-none absolute bottom-0 left-1/2 top-0 w-px -translate-x-1/2 bg-red-400 shadow-[0_0_8px_#f87171]" />
                <div className="pointer-events-none absolute left-1/2 top-0 h-3 w-3 -translate-x-1/2 rotate-45 bg-red-400 shadow-[0_0_6px_#f87171]" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </EditorPortal>
  );
}
